/**
 * Der deutsche Wortlaut der Oberfläche.
 *
 * **Diese Datei liest und ändert ein Mensch.** Sie trägt Wortlaut und kein
 * Warum: die Begründungen stehen weiterhin als Kommentar neben dem Code, den
 * sie erklären. Genau diese Trennung war der Anlass (#67) — die Oberfläche
 * hatte den Ton der Dokumentation geerbt, und wer einen Schalter sucht, liest
 * keinen Aufsatz.
 *
 * Drei Regeln, damit sie eine Datei zum Lesen bleibt:
 *
 * - **Ein Satz sagt, was etwas tut.** Kein Gedankenstrich, keine Begründung,
 *   keine Nominalisierung. Anrede ist „du".
 * - **Zusammengesetztes ist eine Funktion**, kein Zusammenbau im JSX. Eine
 *   andere Sprache stellt die Wörter anders.
 * - **Die Schlüssel sind Namen, nicht Text.** Ein Schlüssel, der den deutschen
 *   Satz wiederholt, ist beim ersten Umformulieren falsch.
 *
 * Seit 0.16.0 steht die ganze Oberfläche hier. In 0.28.0 wurde einmal
 * durchgelesen und geglättet: Gedankenstrich-Nachsätze, mitgelieferte
 * Begründungen und Nominalstil sind raus. Aus dem Betrieb: „klingen zum Teil
 * immer noch sehr sperrig."
 */
import { handbuch } from './handbuch'

/** The speeds that have a word; every other one is said as a factor. */
const SPEED_WORDS: Record<number, string> = {
  1: 'normal',
  0.5: 'halbes Tempo',
  0.25: 'Viertel-Tempo',
  0.1: 'Zehntel-Tempo',
  0.05: 'Zwanzigstel-Tempo',
  2: 'doppeltes Tempo',
}

export const de = {
  /** Das Handbuch (#92) -- eigene Datei, gleiche Regeln. */
  handbuch,
  /** Die Liste links und die Kopfzeile darüber. */
  einstellungen: {
    titel: 'Einstellungen',
    zurueck: 'Zurück',
    zurueckZuDenKategorien: 'Zurück zur Übersicht',
    konto: 'Konto',
    benachrichtigungen: 'Benachrichtigungen',
    darstellung: 'Darstellung',
    dokumentation: 'Dokumentation',
    datenschutz: 'Datenschutz',
    openSource: 'Open Source',
    diagnose: 'Diagnose',
    administration: 'Administration',
    betrieb: 'Betrieb',
    /** An der Zeile mit offenen Anfragen — die Zahl steht auch im Namen. */
    offen: (titel: string, zahl: number) => `${titel}, ${zahl} offen`,
  },

  administration: {
    titel: 'Administration',
    personen: 'Personen',
    speicherplatz: 'Speicherplatz',
    medien: 'Größe von Medien',
    /**
     * The map's appearance for new trips (1.100.0, from use: "in
     * Administration steht immer noch „Farben“ obwohl auch andere Daten
     * eingestellt werden"): tile source, strokes, pins and the film.
     */
    appearance: 'Darstellung Farben / Karte / Route',
    poisAufDerKarte: 'POIs auf der Karte',
    ortUndWetter: 'Ort und Wetter',
    gifs: 'GIFs',
    protokoll: 'Protokoll',
    /** Das Protokoll der Verwaltungshandlungen (1.7.0). */
    protokollLead: 'Wer wann eine Rolle geändert, ein Konto stillgelegt, eine Reise gelöscht oder den Speicher aufgeräumt hat. Die letzten hundert Einträge, neueste zuerst.',
    protokollLeer: 'Noch kein Eintrag.',
    protokollZeile: (wer: string, wann: string) => `${wer} · ${wann}`,
    protokollUnbekannt: 'gelöschtes Konto',
    protokollOhneNamen: 'ohne Namen',
    /** Ein Satz je Handlung. `wen` ist die Person oder die Reise, wie sie damals hieß. */
    handlung: {
      role_set: (wen: string, rolle: string) => `${wen}: Rolle jetzt „${rolle}"`,
      supervisor_set: (wen: string) => `${wen} ist jetzt Supervisor`,
      supervisor_cleared: (wen: string) => `${wen} ist nicht mehr Supervisor`,
      name_set: (wen: string, name: string) =>
        name === '' ? `${wen}: Name für die Verwaltung entfernt` : `${wen}: Name für die Verwaltung jetzt „${name}"`,
      account_disabled: (wen: string) => `${wen} stillgelegt`,
      account_enabled: (wen: string) => `${wen} wieder freigegeben`,
      account_deleted: (wen: string) => `Konto von ${wen} gelöscht`,
      trip_reset: (reise: string, nachrichten: number, medien: number) =>
        `Reise „${reise}" zurückgesetzt: ${nachrichten} Nachrichten, ${medien} Fotos und Videos`,
      trip_deleted: (reise: string, nachrichten: number, medien: number) =>
        `Reise „${reise}" gelöscht: ${nachrichten} Nachrichten, ${medien} Fotos und Videos`,
      member_removed: (reise: string, wen: string) => `${wen} aus der Reise „${reise}" entfernt`,
      cutoff_set: (reise: string, wen: string) => `${wen} in der Reise „${reise}" aus dem Verteiler genommen`,
      cutoff_cleared: (reise: string, wen: string) => `${wen} in der Reise „${reise}" wieder in den Verteiler genommen`,
      settings_changed: (schluessel: string) => `Einstellungen geändert: ${schluessel}`,
      storage_swept: (dateien: number, bytes: string) => `Speicher aufgeräumt: ${dateien} Dateien, ${bytes}`,
      region_added: (name: string) => `Geschützte Region „${name}" angelegt`,
      region_removed: (name: string) => `Geschützte Region „${name}" entfernt`,
      suggestions_deleted: (reise: string, punkte: number) =>
        `Vorschläge in der Reise „${reise}" gelöscht: ${punkte} POIs`,
      missing_swept: (eintraege: number) => `Einträge ohne Datei entfernt: ${eintraege}`,
      orphan_deleted: (datei: string, bytes: string) => `Datei ohne Eintrag gelöscht: ${datei} (${bytes})`,
      empty_folders_removed: (ordner: number) => `Leere Ordner entfernt: ${ordner}`,
      database_swept: (zeilen: number) => `Datenbank aufgeräumt: ${zeilen === 1 ? '1 Zeile' : `${zeilen} Zeilen`}`,
      backup_created: (tabellen: number, bytes: string) => `Sicherung erstellt: ${tabellen} Tabellen, ${bytes}`,
      maintenance_on: (readersOnly: boolean) =>
        readersOnly ? 'Wartung eingeschaltet, gesperrt für Mitlesende' : 'Wartung eingeschaltet, gesperrt für alle',
      maintenance_off: 'Wartung ausgeschaltet',
      sonst: (was: string) => was,
    },
    rollen: { reader: 'liest mit', author: 'darf Reisen anlegen', admin: 'darf Reisen anlegen und verwalten' } as Record<string, string>,
  },

  konto: {
    darfAlles: 'Du kannst Reisen anlegen und Personen freischalten.',
    darfReisen: 'Du kannst eigene Reisen anlegen.',
    darfMitlesen: 'Du liest mit und kannst antworten.',
    abmelden: 'Abmelden',
    /** Die Kopie der eigenen Daten (1.15.0), Art. 15 und 20 DSGVO. */
    datenTitel: 'Deine Daten',
    datenLead: 'Alles, was diese Anwendung über dich gespeichert hat, als eine Datei: dein Konto, deine Anmeldungen, deine Reisen, deine Nachrichten, Reaktionen, Stimmen und POIs. Nichts von anderen.',
    datenDateien: 'Fotos und Videos sind nicht in der Datei, nur ihre Angaben und Adressen. Die Dateien selbst holst du in der App.',
    datenHolen: 'Meine Daten als Datei',
  },

  kontokarte: {
    email: 'E-Mail-Adresse',
    emailLead: (adresse: string) =>
      `${adresse}. Damit meldest du dich an und setzt dein Passwort zurück. Ändern lässt sie sich hier nicht.`,
    name: 'Dein Name',
    nameLead: 'Dieser Name steht über deinen Nachrichten.',
    nameFeld: 'Name',
    nameSpeichern: 'Namen speichern',
    passwort: 'Passwort ändern',
    passwortLead: 'Andere Geräte werden dabei abgemeldet. Du bekommst eine E-Mail zur Bestätigung.',
    passwortAlt: 'Bisheriges Passwort',
    passwortNeu: 'Neues Passwort',
    passwortNochmal: 'Neues Passwort wiederholen',
    passwortUngleich: 'Die beiden neuen Passwörter sind nicht gleich.',
    passwortGeaendert: 'Geändert. Die anderen Geräte sind abgemeldet.',

    /**
     * Passkeys (#21). „Face ID, Touch ID oder der Code deines Geräts": das
     * sind die Wörter, die auf dem Gerät selbst stehen -- nicht „biometrisch"
     * und nicht „WebAuthn".
     */
    passkeys: 'Passkeys',
    passkeysLead: 'Anmelden mit Face ID, Touch ID oder dem Code deines Geräts, ohne Passwort. Der Passkey liegt in deinem Schlüsselbund, nicht auf dem Server.',
    passkeyAnlegen: 'Passkey hinzufügen',
    passkeyKeine: 'Noch kein Passkey.',
    passkeyNichtMoeglich: 'Dieser Browser bietet keine Passkeys an. Auf einem anderen Gerät kannst du einen anlegen.',
    passkeyNichtEingerichtet: 'Passkeys sind hier nicht eingeschaltet. Die Verwaltung muss dafür die Adresse der Seite eintragen.',
    /**
     * „Angelegt auf iPad" und nicht bloß „iPad": der Name ist der Browser, in
     * dem der Passkey entstand, nicht der Ort, an dem er gilt. Aus dem Betrieb:
     * am iPad angelegt, am iPhone angemeldet, es ist derselbe Schlüsselbund.
     */
    passkeyAngelegtAuf: (name: string) => `Angelegt auf ${name}`,
    passkeyAngelegtAm: (wann: string) => `am ${wann}`,
    passkeyBenutzt: (wann: string) => `zuletzt benutzt am ${wann}`,
    passkeyNieBenutzt: 'noch nie benutzt',
    passkeyEntfernen: 'Entfernen',
    passkeyEntfernenFrage: (name: string) =>
      `Den auf „${name}" angelegten Passkey entfernen? Anmelden geht damit danach nicht mehr.`,
    passkeyAbgebrochen: 'Abgebrochen. Es wurde nichts angelegt.',
    /** Der Name in der Liste, wenn sich das Gerät nicht erkennen lässt. */
    passkeyGeraet: 'Gerät',
    passkeyPasswortBleibt: 'Dein Passwort gilt weiterhin, auch mit Passkey.',
  },

  push: {
    titel: 'Benachrichtigungen',
    erstInstallieren: 'Auf dem iPhone geht das erst, wenn die App über Teilen → Zum Home-Bildschirm installiert ist. Öffne sie danach von dort.',
    kannNicht: 'Dieser Browser kann keine Benachrichtigungen empfangen.',
    an: 'Dieses Gerät bekommt eine Nachricht, wenn es etwas Neues gibt. Einzelne Reisen kannst du auf ihrer Reiseseite stummschalten.',
    aus: 'Bekomme eine Nachricht, wenn jemand etwas Neues schreibt, auch bei geschlossener App.',
    veraltet: 'Die Anmeldung dieses Geräts ist nicht mehr gültig. Schalte sie hier wieder ein.',
    verloren: 'Dieses Gerät war angemeldet, ist es aber nicht mehr. Schalte es wieder ein.',
    gesperrt: 'Der Browser blockiert Benachrichtigungen für diese Seite. Erlaube sie in den Browser-Einstellungen.',
    frageOffen: 'Der Browser wartet auf deine Antwort. Am Rechner steht die Frage oft nur als kleines Symbol links in der Adresszeile.',
    dauert: 'Das dauert ungewöhnlich lange. Lade die Seite neu und versuch es noch einmal.',
    einschalten: 'Auf diesem Gerät einschalten',
    abschalten: 'Auf diesem Gerät abschalten',
    probe: 'Testnachricht senden',
    probeEines: 'Verschickt. Die Nachricht sollte gleich ankommen.',
    probeMehrere: (zahl: number) => `An ${zahl} Geräte verschickt.`,
    symbolzahl: 'Zahl am App-Symbol',
    symbolzahlAn: 'Das App-Symbol zeigt die Zahl deiner ungelesenen Beiträge.',
    symbolzahlAus: 'Das Symbol bleibt ohne Zahl. Benachrichtigungen kommen weiter an.',
    symbolzahlNichtGemerkt: 'Das ließ sich gerade nicht speichern. Versuch es noch einmal.',
    /** Nur auf iPhone und iPad: dort führt der Weg weiter in die Systemeinstellungen. */
    systemeinstellungen: 'In iOS kannst du weitere Einstellungen zu den Mitteilungen in den Systemeinstellungen bei Travel Broadcast ändern.',
    geraeteTitel: 'Angemeldete Geräte',
    geraeteFrist: (wochen: number) =>
      `Öffnest du die App auf einem Gerät ${wochen} Wochen lang nicht, wird es abgemeldet. Beim nächsten Öffnen meldet es sich von selbst wieder an.`,
    geraetArt: {
      iphone: 'iPhone',
      ipad: 'iPad',
      mac: 'Mac oder iPad',
      android: 'Android',
      windows: 'Windows',
      chromeos: 'ChromeOS',
      linux: 'Linux',
      other: 'Anderes Gerät',
    } as Record<string, string>,
    diesesGeraet: 'dieses Gerät',
    geraetZuletzt: (datum: string) => `App zuletzt geöffnet am ${datum}`,
    geraetBis: (datum: string) => `Wird am ${datum} abgemeldet, wenn du die App dort bis dahin nicht öffnest`,
    geraetEntfernen: 'Abmelden',
    geraetEntfernenName: (art: string) => `${art} abmelden`,
    schritte: {
      schluessel: 'Schlüssel vom Server holen …',
      erlaubnis: 'Der Browser fragt nach der Erlaubnis …',
      worker: 'Hintergrunddienst anmelden …',
      abonnement: 'Abonnement beim Push-Dienst lösen …',
      server: 'Gerät beim Server eintragen …',
    },
  },

  /**
   * Die App auf den Startbildschirm legen (#101). Der Knopf heißt anders,
   * je nachdem, was danach kommt: ein Systemdialog oder eine Anleitung.
   */
  app: {
    titel: 'Als App hinzufügen',
    lead: 'Dann startet Travel Broadcast ohne Browserleisten und kann dich benachrichtigen.',
    zeile: 'Travel Broadcast als App hinzufügen',
    knopfInstallieren: 'App installieren',
    knopfAnleitung: 'Als App hinzufügen',
    anleitungTitel: 'Als App hinzufügen',
    schritte: [
      // **Keine Ortsangabe mehr.** Hier stand „unten“, und aus dem Betrieb
      // kam mit Bildschirmfotos zurück: je nach Safari-Einstellung sitzt die
      // Adressleiste oben oder unten, und je nach Bildschirmgröße steht
      // Teilen daneben -- oder gar nicht, sondern nur im Menü in der
      // Adressleiste. Am iPhone des Melders war es das Menü. Der Satz nennt
      // deshalb das Symbol und beide Orte, statt einen zu behaupten.
      'Such in Safari das Teilen-Symbol: ein Pfeil, der nach oben aus einem Kasten zeigt.',
      'Je nach Gerät steht es neben der Adressleiste. Findest du es nicht, tippe in der Adressleiste auf das Menü mit den Strichen, dort steht „Teilen“.',
      'Wähle „Zum Home-Bildschirm“. Du musst in der Liste ein Stück nach unten wischen.',
      'Schalte „Als Web-App öffnen“ ein, wenn es angeboten wird, und tippe auf „Hinzufügen“.',
    ],
    nurSafari: 'Auf iPhone und iPad geht es nur in Safari, nicht in Chrome, Firefox oder Edge.',
  },

  schrift: {
    titel: 'Schriftgröße',
    /*
     * **Die drei Stufen -- hier und nicht in `lib/darstellung.ts`** (1.64.0).
     * Dort stand neben jedem Wert sein Name (`{ wert: 'klein', name: 'Klein' }`),
     * also Oberflächentext in einer Datei ohne Komponente. Der Wert bleibt
     * dort, der Name steht hier: die Karte schlägt ihn über den Wert nach.
     */
    stufen: { klein: 'Klein', mittel: 'Mittel', gross: 'Groß' },
    lead: 'Gilt in der ganzen App und auf allen deinen Geräten. Im Chat passt sich die Größe zusätzlich dem Bildschirm an.',
    wahl: 'Schriftgröße',
    nichtGemerkt: 'Die Größe gilt auf diesem Gerät. Für deine anderen Geräte ließ sie sich nicht speichern.',
  },

  /** Geschützte Regionen (#99): wo der Import keinen Vorschlag anlegt. */
  regionen: {
    titel: 'Geschützte Regionen',
    lead: 'In diesen Gegenden legt der Routenimport keine Vorschläge an, zum Beispiel rund um dein Zuhause. Die Route selbst bleibt, wie sie aufgezeichnet ist. Die Liste sieht nur die Verwaltung.',
    leer: 'Noch keine Region.',
    zeile: (name: string, radius: number) => `${name} · ${radius} m Umkreis`,
    entfernen: 'Entfernen',
    entfernenFrage: (name: string) => `Die Region „${name}" entfernen? Der nächste Import legt dort wieder Vorschläge an.`,
    neu: 'Neue Region',
    karteHinweis: 'Tippe auf die Karte, um den Mittelpunkt zu setzen. Oder nimm deinen Standort, oder trag die Koordinaten ein.',
    hier: 'Mein Standort',
    lat: 'Breite',
    lon: 'Länge',
    name: 'Name',
    radius: 'Umkreis in Metern',
    radiusHinweis: (von: number, bis: number) => `Zwischen ${von} und ${bis}. 3000 sind ein Ort mit seinem Umland.`,
    anlegen: 'Region anlegen',
  },

  erkennung: {
    titel: 'Aufenthalte',
    lead: 'Beim Import einer Route sucht die App nach längeren Stopps und schlägt sie als Highlights vor.',
    minuten: 'Mindestdauer (Minuten)',
    minutenHinweis: '0 schaltet die Erkennung ab.',
    umkreis: 'Umkreis (Meter)',
    umkreisHinweis: '800 m sind ein guter Wert. Bei mehr verschmelzen zwei Stopps in einer Stadt zu einem, und eine langsame Durchfahrt zählt als Aufenthalt.',
  },

  /** Ein POI je Ort (#96, 1.51.0): der Umkreis, in dem ein Halt oder ein neuer POI zum Spot wird. */
  einPoiJeOrt: {
    titel: 'Ein POI je Ort',
    lead: 'Setzt du einen neuen POI so nah an einem vorhandenen, fragt die App, ob er ein Spot an diesem POI werden soll. Halte aus einer Route bleiben eigene POIs.',
    umkreis: 'Umkreis (Meter)',
    umkreisHinweis: '2000 m sind ein Ort: Altstadt, Hafen und Museum liegen so nah beieinander. 0 schaltet es ab.',
  },

  gif: {
    titel: 'GIFs',
    lead: 'Mitlesende können mit einem GIF antworten statt mit Worten.',
    name: 'GIF-Auswahl',
    an: 'Der GIF-Knopf steht in der Eingabezeile. Die Suche läuft über den Server, zu Giphy geht nur der Suchbegriff. Ein gewähltes GIF speichert der Server wie ein hochgeladenes Bild und zählt es zum Speicherplatz der Reise.',
    aus: 'Ohne Schlüssel gibt es keinen GIF-Knopf, alles andere läuft. Einen kostenlosen Schlüssel bekommst du auf developers.giphy.com („Create an App", Typ API). Er kommt in die .env auf dem Server:',
  },

  kontoloeschung: {
    oeffnen: 'Konto löschen …',
    wirdGezaehlt: 'Wird gezählt …',
    verschwindet: 'Das verschwindet:',
    geschrieben: (zahl: number) =>
      zahl === 1 ? '1 geschriebene Nachricht' : `${zahl} geschriebene Nachrichten`,
    erhalten: (zahl: number) =>
      zahl === 1
        ? '1 Nachricht an diese Person, auch vom Autor / von der Autorin geschriebene'
        : `${zahl} Nachrichten an diese Person, auch vom Autor / von der Autorin geschriebene`,
    dateien: (zahl: number) => (zahl === 1 ? '1 Datei' : `${zahl} Dateien`),
    reaktionen: (zahl: number) => (zahl === 1 ? '1 Reaktion' : `${zahl} Reaktionen`),
    geraete: (zahl: number) =>
      zahl === 1 ? '1 angemeldetes Gerät' : `${zahl} angemeldete Geräte`,
    betroffeneReisen: 'Betroffene Reisen:',
    reiseZeile: (titel: string, zahl: number) =>
      `${titel}: ${zahl === 1 ? '1 Nachricht' : `${zahl} Nachrichten`}`,
    gesperrt: (reisen: string) =>
      `Diesem Konto gehören noch Reisen: ${reisen}. Lösche erst die Reisen, dann das Konto. Bis dahin kannst du es nur stilllegen.`,
    endgueltig: 'Das lässt sich nicht rückgängig machen. Alles, was diese Person geschrieben hat, verschwindet aus allen Reiseberichten.',
    abtippenName: (adresse: string) => `E-Mail-Adresse ${adresse} abtippen`,
    abbrechen: 'Abbrechen',
    loeschen: 'Endgültig löschen',
    wirdGeloescht: 'Wird gelöscht …',
    geloescht: (name: string, nachrichten: number, dateien: number) =>
      `„${name}" ist gelöscht: ${nachrichten === 1 ? '1 Nachricht' : `${nachrichten} Nachrichten`} und ${dateien === 1 ? '1 Datei' : `${dateien} Dateien`} sind weg.`,
  },

  personen: {
    lead: 'Wer über einen Einladungslink kommt, liest mit und antwortet. Eigene Reiseberichte schreiben darf nur, wen du dafür freischaltest. Den Namen für die Verwaltung siehst nur du.',
    wirdGeladen: 'Wird geladen …',
    du: ' · du',
    stillgelegt: ' · stillgelegt',
    reisen: (zahl: number) => (zahl === 1 ? '1 Reise' : `${zahl} Reisen`),
    nenntSichSelbst: (name: string) => `nennt sich selbst ${name}`,
    namensfeld: (name: string) => `Name für die Verwaltung, sonst ${name}`,
    rolleVon: (name: string) => `Rolle von ${name}`,
    eigeneRolle: 'Deine eigene Rolle kann nur eine zweite Verwaltung ändern.',
    liestMit: 'Liest mit',
    darfReisen: 'Darf Reisen anlegen',
    darfVerwalten: 'Darf Reisen anlegen und verwalten',
    /** Die Sortierzeile und die kompakte Zeile (#97). */
    sortieren: 'Sortieren nach',
    sortName: 'Name',
    sortEmail: 'E-Mail',
    sortBeigetreten: 'Beigetreten',
    sortInApp: 'In der App',
    sortGeschrieben: 'Geschrieben',
    sortRolle: 'Rolle',
    aufsteigend: 'aufsteigend',
    absteigend: 'absteigend',
    beigetretenAm: (datum: string) => `seit ${datum}`,
    zuletztInAppAm: (datum: string) => `zuletzt in der App am ${datum}`,
    zuletztGeschriebenAm: (datum: string) => `zuletzt geschrieben am ${datum}`,
    nieInApp: 'noch nie in der App',
    nieGeschrieben: 'noch nichts geschrieben',
    nieKurz: 'noch nie',
    rolleKurz: { reader: 'liest mit', author: 'Autor/in', admin: 'Verwaltung' } as Record<'reader' | 'author' | 'admin', string>,
    zeileOeffnen: (name: string) => `${name} bearbeiten`,
    stilllegen: 'Konto stilllegen',
    stilllegenKurz: 'Stilllegen',
    stilllegenFrage: (name: string) =>
      `${name} stilllegen? Die Person wird sofort abgemeldet und kann sich nicht mehr anmelden. Reisen, Nachrichten und Mitgliedschaften bleiben. Du kannst das jederzeit aufheben.`,
    freigeben: 'Wieder freigeben',
    supervisor: 'Supervisor',
    supervisorHinweis: 'Ein Supervisor sieht und ändert die technischen Einstellungen unter Betrieb.',
    supervisorSelbst: 'Dein eigenes Merkmal kann nur ein zweiter Supervisor ändern.',
    supervisorMarke: ' · Supervisor',
  },

  /** Die Kategorie „Betrieb" für den Supervisor (#62). */
  betrieb: {
    titel: 'Betrieb',
    konfiguration: 'Konfiguration',
    selbstpruefung: 'Selbstprüfung',
    protokoll: 'Protokoll',
    /** Die Kategorie (#82); `datenbank` darunter ist die Zeile der Selbstprüfung. */
    datenbankTitel: 'Datenbank',
    /** Die Kategorie der Sicherung (#77). */
    sicherungTitel: 'Sicherung',
    /** The maintenance page (#131). */
    maintenance: {
      title: 'Wartungsseite',
      lead: 'Während der Wartung sehen alle anderen nur eine Wartungsseite. Was sie ohne Verbindung schreiben, wartet auf ihrem Gerät.',
      on: 'Die Wartungsseite ist eingeschaltet.',
      off: 'Die Wartungsseite ist ausgeschaltet.',
      since: (when: string) => `Seit ${when}`,
      until: (when: string) => `Wieder da: ${when}`,
      scope: 'Gesperrt für',
      scopeAll: 'Alle außer dir',
      scopeReaders: 'Nur Mitlesende',
      scopeReadersHint: 'Autorinnen und Autoren können die neue Version vorher testen.',
      untilField: 'Voraussichtlich wieder da (optional)',
      sentence: 'Satz auf der Wartungsseite (optional)',
      count: (has: number, may: number) => `${has} von ${may} Zeichen`,
      switchOn: 'Wartungsseite einschalten',
      save: 'Änderung speichern',
      switchOff: 'Wartungsseite ausschalten',
      askOn: 'Wartungsseite einschalten? Alle anderen sehen dann nur noch die Wartungsseite.',
      askOnShort: 'Einschalten',
      askOff: 'Wartungsseite ausschalten? Alle sehen danach wieder die App. Hast du die Datenprüfung laufen lassen?',
      askOffShort: 'Ausschalten',
      exit: 'Notausgang: Lösche per FTP die Datei maintenance.json im Medienordner. Dann ist die Wartungsseite aus.',
    },
    /** The consistency checks (#131). */
    diagnosis: {
      title: 'Datenprüfung',
      lead: 'Prüft die Datenbank nach einer Migration. Die Prüfungen lesen nur und ändern nichts.',
      all: 'Alle prüfen',
      again: 'Erneut prüfen',
      running: 'Wird geprüft …',
      ok: 'In Ordnung',
      findings: (count: number) => (count === 1 ? '1 Fund' : `${count} Funde`),
      more: (further: number) => `und ${further} weitere`,
      findingInTrip: (finding: string, trip: string) => `${finding} · ${trip}`,
      checks: {
        schema: 'Schema: Tabellen und Spalten wie im Code',
        message_refs: 'Alles, was an einem Beitrag hängt, hat seinen Beitrag',
        chat_order: 'Jeder Beitrag hat seinen Platz im Chat',
        media_files: 'Jedes Foto und Video hat seine Datei',
      } as Record<string, string>,
    },
    sicherungLead: 'Die Datenbank und die Datei .env in einem verschlüsselten ZIP. Fotos und Videos sicherst du per FTP.',
    sicherungPassphrase: 'Passphrase',
    sicherungHinweis: (mindestens: number) =>
      `Mindestens ${mindestens} Zeichen. Ohne sie lässt sich die Sicherung nicht öffnen. Der Server speichert sie nicht.`,
    sicherungZaehler: (hat: number, braucht: number) => `${hat} von ${braucht} Zeichen`,
    sicherungErstellen: 'Sicherung erstellen',
    sicherungLaeuft: 'Sicherung wird erstellt …',
    sicherungFertig: (tabellen: number, reisen: number, pois: number, groesse: string) =>
      `Fertig: ${tabellen} Tabellen, ${reisen === 1 ? '1 Reise' : `${reisen} Reisen`}, ${pois === 1 ? '1 POI' : `${pois} POIs`}, ${groesse}. Die Zeilen je Tabelle stehen in LIESMICH.txt.`,
    sicherungHolen: (datei: string) => `${datei} herunterladen`,
    sicherungFrist: 'Der Link gilt zwei Stunden. Danach löscht der Server die Datei.',
    sicherungOeffnen: 'Öffnen kannst du sie mit 7-Zip unter Windows oder mit Keka am Mac. Der Explorer und das Archivprogramm von macOS können sie nicht öffnen.',
    sicherungNichtMoeglich: 'Dieser Server kann keine verschlüsselten ZIP-Dateien schreiben. Sichere die Datenbank dann in phpMyAdmin.',
    konfigurationLead: 'Die Werte der Datei .env auf dem Server. Der Server liest sie bei jeder Anfrage neu, eine Änderung gilt sofort.',
    nichtSchreibbar: 'Die Datei .env ist für den Server nicht beschreibbar. Ändere sie per FTP.',
    fehlt: 'Auf dem Server liegt keine Datei .env.',
    zurueckNehmen: 'Letzte Änderung zurücknehmen',
    zurueckgenommen: 'Zurückgenommen.',
    unbekannt: 'Zeilen in der Datei, die die Anwendung nicht kennt:',
    nurFtp: 'Nur per FTP.',
    ausUmgebung: 'Kommt aus einer Umgebungsvariable des Servers. Die Datei ändert daran nichts.',
    quelleDatei: 'aus der Datei',
    quelleVorgabe: 'Vorgabe',
    gesetzt: 'gesetzt',
    leer: 'leer',
    neuerWert: 'Neuen Wert eingeben',
    speichern: 'Speichern',
    gespeichert: 'Gespeichert.',
    auskommentieren: 'Aus',
    vorgabe: (wert: string) => `Vorgabe: ${wert}`,
    /**
     * Der Satz unter einem Schlüssel, der eine Einstellung deckelt (1.1.2).
     * Aus dem Betrieb: „ein fester Wert in der Beschreibung, obwohl ein
     * anderer eingestellt ist" — die Zahl war die der Einstellung unter
     * Administration, und der Satz sagte nicht, wovon er spricht.
     */
    deckeFuer: (name: string) => `Obergrenze für „${name}" unter`,
    deckeOrt: 'Administration › Fotos und Videos',
    deckeStand: (gewaehlt: number | null | undefined, wert: number | null | undefined) =>
      `. Dort eingestellt: ${gewaehlt ?? '–'}, erlaubt bis ${wert ?? '–'}.`,
    geprueft: 'Ein falscher Wert hier sperrt aus oder erreicht jeden Mitlesenden. Bitte genau prüfen.',
    geprueftFrage: (key: string) => `${key} wirklich ändern? Der Server prüft die Form des Werts, aber nicht, ob er zu deiner Installation passt.`,
    aendern: 'Ändern',
    selbstpruefungLead: 'Was das Hosting entscheidet und was die Anwendung darüber weiß.',
    /** Das Aufräumen der Datenbank (#82). */
    datenbankLead: 'Zeilen, die die Anwendung nicht mehr braucht: abgelaufene Sitzungen und Anfragen, alte Zähler, Protokolle älter als ein Jahr. Nachrichten, Fotos und Reisen fasst das nicht an.',
    datenbankTabelle: {
      sessions: 'Abgelaufene Sitzungen (älter als eine Woche)',
      password_resets: 'Verbrauchte oder abgelaufene Reset-Links',
      passkey_challenges: 'Abgelaufene Passkey-Anfragen',
      throttles: 'Alte Zähler der Bremse',
      env_changes: 'Konfigurationsprotokoll, älter als ein Jahr',
      admin_actions: 'Verwaltungsprotokoll, älter als ein Jahr',
    } as Record<string, string>,
    datenbankZeilen: (zahl: number) => (zahl === 1 ? '1 Zeile' : `${zahl} Zeilen`),
    datenbankNichts: 'Nichts aufzuräumen.',
    datenbankAufraeumen: 'Aufräumen',
    datenbankAufraeumenKurz: 'Löschen',
    datenbankFrage: (zahl: number) =>
      `${zahl === 1 ? '1 Zeile' : `${zahl} Zeilen`} löschen? Sie sind abgelaufen oder älter als ein Jahr. Die Anwendung braucht sie nicht mehr, und das Aufräumen steht danach im Protokoll der Verwaltung.`,
    datenbankWeggeraeumt: (zahl: number) => (zahl === 0 ? 'Es war nichts wegzuräumen.' : `Weggeräumt: ${zahl === 1 ? '1 Zeile' : `${zahl} Zeilen`}.`),
    version: 'Version',
    build: 'Commit',
    datenbank: 'Datenbank',
    schema: 'Schema vollständig',
    schemaFehlt: (spalten: string[]) => `Es fehlt: ${spalten.join(', ')}`,
    php: 'PHP',
    envDatei: '.env vorhanden',
    envSchreibbar: '.env beschreibbar',
    medienOrdner: 'Medienordner beschreibbar',
    postMax: 'post_max_size',
    uploadMax: 'upload_max_filesize',
    speicherGrenze: 'memory_limit',
    laufzeit: 'max_execution_time',
    zip: 'ZIP (für EPUB und Medien)',
    sicherungAes: 'Verschlüsselung der Sicherung',
    gd: 'GD (Bilder)',
    openssl: 'OpenSSL (Passkeys, Apple)',
    curl: 'cURL (Push, Apple, Giphy)',
    buchSchrift: 'Schrift für das Buch',
    kartenquelleFest: 'Feste Kachelquelle',
    push: 'Push eingerichtet',
    appleMaps: 'Apple Maps',
    appleWeather: 'Apple Weather',
    appleMusic: 'Apple Music',
    giphy: 'Giphy',
    mail: 'E-Mail',
    mailWie: (transport: string, absender: boolean) => `${transport}${absender ? '' : ', ohne Absender'}`,
    betreiber: 'Betreiberangaben vollständig',
    appUrl: 'APP_URL gesetzt',
    protokollLead: 'Wer wann welchen Wert geändert hat. Bei Geheimnissen steht nur, dass sie geändert wurden.',
    protokollLeer: 'Noch keine Änderung.',
    protokollZeile: (wer: string, wann: string) => `${wer} · ${wann}`,
    protokollAlt: (alt: string | null, neu: string | null) => `${alt ?? '–'} → ${neu ?? '–'}`,
    protokollGeheim: 'Wert nicht aufgezeichnet',
    protokollUndo: ' · zurückgenommen',
    unbekanntePerson: 'unbekannt',
  },

  medien: {
    titel: 'Medien prüfen',
    lead: 'Neu hochgeladene Videos spielen auf allen Geräten ab. Ältere Videos können in einem Format vorliegen, das nicht jedes Gerät abspielt.',
    pruefen: 'Bestand prüfen',
    wirdGeprueft: 'Wird geprüft …',
    pruefungFehlt: 'Die Prüfung hat nicht geklappt.',
    /** Vor dem Pfad; er steht als `<code>` daneben, daher das Leerzeichen. */
    wurzelVor: 'Alle Pfade unterhalb von ',
    loeschenFehlt:
      'Die Datei ließ sich nicht löschen. Vielleicht gehört sie inzwischen doch zu einem Beitrag — dann ist es richtig so.',
    raeumenFehlt: 'Die Einträge ließen sich nicht entfernen.',
    /** Die zwei Knöpfe an einer herrenlosen Datei. */
    ansehen: 'Ansehen',
    loeschen: 'Löschen',
    allesGut: (dateien: string) => `${dateien} geprüft, alle spielen überall ab.`,
    befunde: (dateien: string, zahl: number) =>
      `${dateien} geprüft, ${zahl} davon spielen nicht überall ab.`,
    offen: (zahl: number) =>
      zahl === 1 ? ` 1 weitere steht noch aus, sie kommt beim nächsten Lauf.` : ` ${zahl} weitere stehen noch aus, sie kommen beim nächsten Lauf.`,
    abgleichTitel: 'Dateien und Einträge',
    abgleichLead: 'Findet Dateien ohne Eintrag in der Datenbank und Einträge ohne Datei. Beides verfälscht den Speicherplatz.',
    abgleichen: 'Dateien abgleichen',
    wirdAbgeglichen: 'Wird abgeglichen …',
    abgleichFehlt: 'Der Abgleich hat nicht geklappt.',
    ohneEintrag: (dateien: string) => `${dateien} ohne Eintrag`,
    ohneDatei: (eintraege: string) => `${eintraege} ohne Datei`,
    angesehen: (dateien: string) => `${dateien} angesehen`,
    stehenAus: (zahl: number) => `, ${zahl} stehen noch aus`,
    weitersehen: 'Die nächsten ansehen',
    vonVorne: 'Wieder von vorne',
    abgleichFussnote: 'Dateien ohne Eintrag werden im ganzen Speicher gesucht, Einträge ohne Datei nur in deinen Reisen. Dateien vom letzten Tag bleiben außen vor, sie können noch im Upload sein.',
    dateiOhneEintrag: 'Datei ohne Eintrag',
    eintragOhneDatei: 'Eintrag ohne Datei',
    reisebildFehlt: 'Bild der Reise fehlt, die Reise bleibt',
    standbildFehlt: 'Standbild fehlt, das Video spielt weiter',
    vorschaubildFehlt: 'Vorschaubild fehlt, die Kachel bleibt leer',
    beitrag: (id: number) => ` · Beitrag ${id}`,
    punkt: (id: number) => ` · POI ${id}`,
    dateiLoeschen: 'Datei löschen',
    dateiLoeschenFrage: 'Diese Datei löschen? Sie hängt an keinem Beitrag und keiner Reise. Das lässt sich nicht rückgängig machen.',
    eintraegeEntfernen: (eintraege: string) => `${eintraege} entfernen`,
    eintraegeEntfernenFrage: (eintraege: string) =>
      `${eintraege} ohne Datei entfernen? Die Beiträge bleiben stehen und verlieren nur den Anhang. Das lässt sich nicht rückgängig machen.`,
    wirdEntfernt: 'Wird entfernt …',
    datei: 'Datei',
    dateien: 'Dateien',
    eintrag: 'Eintrag',
    eintraege: 'Einträge',
    /** Leere Ordner im Speicher (1.147.0), aus der Zeit vor dem Aufräumen beim Löschen. */
    leereOrdner: (ordner: string) => `${ordner} leer`,
    ordner: 'Ordner',
    ordnerViele: 'Ordner',
    ordnerEntfernen: (ordner: string) => `${ordner} entfernen`,
    ordnerEntfernenFrage: (ordner: string) =>
      `${ordner} entfernen? Sie enthalten keine Dateien. Das lässt sich nicht rückgängig machen.`,
    ordnerFehlt: 'Die leeren Ordner ließen sich nicht entfernen.',
  },

  speicher: {
    titel: 'Speicherplatz',
    lead: 'Fotos, Videos und Sprachnachrichten aller Reisen zusammen. Ist der Platz voll, nimmt die App nichts Neues mehr an.',
    wirdGezaehlt: 'Wird gezählt …',
    nichtErmittelt: 'Das ließ sich nicht ermitteln.',
    belegt: (prozent: number) => `${prozent} Prozent belegt`,
    fotos: 'Fotos',
    videos: 'Videos',
    sprache: 'Sprache',
    zurueckgezogen: 'zurückgezogen',
    abgeschlossen: 'abgeschlossen',
    alleVorschaubilder: 'Alle Fotos haben ein Vorschaubild.',
    vorschaubilder: (mit: number, gesamt: number) =>
      `Vorschaubilder: ${mit} von ${gesamt} Fotos. Ältere Fotos haben keins und zeigen in der Kachel das Original.`,
    reisenAnderer: (groesse: string) => `Reisen anderer: ${groesse}`,
    beiwerk: (zahl: number, groesse: string) =>
      `Darin: ${zahl === 1 ? '1 Datei' : `${zahl} Dateien`} Vorschaubilder, Standbilder und Aufzeichnungen (${groesse}).`,
    keineBuecher: 'Es liegt kein gebautes Buch auf dem Server.',
    buecher: (zahl: number, groesse: string) =>
      zahl === 1
        ? `1 gebautes Buch (${groesse}) liegt auf dem Server. Sein Link gilt zwei Stunden, danach räumt der nächste Buchbau, Abruf oder dieser Knopf es weg.`
        : `${zahl} gebaute Bücher (${groesse}) liegen auf dem Server. Ein Link gilt zwei Stunden, danach räumt der nächste Buchbau, Abruf oder dieser Knopf sie weg.`,
    buecherWegraeumenFrage: 'Gebaute Bücher löschen, deren Link abgelaufen ist? Ein Buch lässt sich jederzeit neu bauen.',
    nichtsLiegt: 'In deinen Reisen liegt nichts herum.',
    liegt: (zahl: number, groesse: string) =>
      zahl === 1
        ? `1 liegengebliebene Datei (${groesse}) hängt an keiner Nachricht und an keinem POI.`
        : `${zahl} liegengebliebene Dateien (${groesse}) hängen an keiner Nachricht und an keinem POI.`,
    weitere: (zahl: number) => `… und ${zahl} weitere. Der Knopf räumt alle weg.`,
    nichtsWegzuraeumen: 'Es war nichts wegzuräumen.',
    weggeraeumt: (zahl: number, groesse: string) =>
      zahl === 1 ? `1 Datei weggeräumt, ${groesse} frei.` : `${zahl} Dateien weggeräumt, ${groesse} frei.`,
    wegraeumen: 'Liegengebliebenes wegräumen',
    wegraeumenKurz: 'Wegräumen',
    wegraeumenFrage: (zahl: number) =>
      `${zahl} Dateien löschen, die an keiner Nachricht und keinem POI hängen? Es sind Reste abgebrochener Uploads. Das lässt sich nicht rückgängig machen.`,
    heute: 'heute',
    seitEinemTag: 'seit einem Tag',
    seitTagen: (tage: number) => `seit ${tage} Tagen`,
    vonMitlesendem: 'von einem Mitlesenden',
    artFoto: 'Foto',
    artGif: 'GIF',
    artVideo: 'Video',
    artSprache: 'Sprachnachricht',
  },

  grenzen: {
    /*
     * **Die Einheiten an den Zahlenfeldern.** Sie standen als
     * `einheit="Pixel"` im JSX -- sechsmal, und kein Prüflauf sah es: eine
     * Beschriftung ohne Leerzeichen fiel durch jede Regel. „%" und „MB" sind
     * Zeichen und keine Wörter und bleiben, wo sie stehen.
     */
    pixel: 'Pixel',
    sekunden: 'Sekunden',
    bilder: 'Bilder',
    titel: 'Fotos und Videos',
    lead: 'Gilt für alle Reisen. Die Bildgröße wirkt ab dem nächsten Foto, die Grenzen sofort.',
    fotoKante: 'Lange Kante der Fotos',
    fotoKanteHinweis: 'Kleiner wird körnig, größer kostet viel Platz für wenig Gewinn.',
    fotoQualitaet: 'Bildqualität',
    fotoQualitaetHinweis: 'Unter 60 sieht man es, über 90 kostet viel Platz für wenig.',
    leserKante: 'Lange Kante bei Fotos von Mitlesenden',
    leserKanteHinweis: 'Gilt für alle Mitlesenden in allen Reisen. Fotos in Antworten dürfen kleiner sein als die im Reisebericht.',
    leserQualitaet: 'Bildqualität bei Fotos von Mitlesenden',
    vorschauKante: 'Lange Kante der Vorschaubilder',
    vorschauKanteHinweis: 'Für die Kacheln im Chat und auf der Karte. 800 reicht für ein iPad; gilt ab dem nächsten Foto, bestehende Vorschaubilder bleiben.',
    fotoMax: 'Größtes Foto',
    videoKante: 'Lange Kante der Videos',
    videoKanteHinweis: '1920 entspricht 1080p. Die Grenze gilt für die lange Kante, quer wie hochkant. Verkleinert wird auf dem Gerät des Absenders. Das Video der Streckenstatistik hat genau diese Breite, im Format 16:9.',
    videoFps: 'Bilder je Sekunde',
    videoFpsHinweis: 'Ein iPhone nimmt mit 60 auf, Zeitlupe mit 240. Eine Zeitlupe bleibt eine Zeitlupe, sie wird nur etwas ruckeliger. Das Video der Streckenstatistik läuft mit genau dieser Bildrate.',
    videoMax: 'Größtes Video',
    videoMaxHinweis: 'Die Grenze gilt nach dem Verkleinern und fängt Videos ab, die ein Gerät nicht umrechnen konnte.',
    rechnen: 'Rechnen beim Verkleinern',
    rechnenQualitaet: 'Qualität (höhere Datenrate)',
    rechnenTempo: 'Tempo (kleinere Datei)',
    rechnenHinweis: '„Tempo" ergibt kleinere Dateien mit gröberem Bild, „Qualität" größere mit feinerem.',
    datenrate: 'Datenrate beim Verkleinern',
    datenrateStandard: 'Standard',
    datenrateHoch: 'Hoch',
    datenrateSehrHoch: 'Sehr hoch',
    datenrateHinweis: 'Höher heißt feineres Bild und größere Datei. Bei 1080p mit 60 Bildern je Sekunde wird eine Minute Video etwa 75 MB (Standard), 140 MB (Hoch) oder 233 MB (Sehr hoch) groß. Prüfe dazu die größte Videodatei. Das Video der Streckenstatistik bleibt bei Standard.',
    platzJeReise: 'Platz je Reise',
    deckel: 'Die Obergrenzen stehen in der .env auf dem Server. Höhere Werte werden auf diese Grenzen gesetzt.',
    feld: (label: string, einheit: string) => `${label} (${einheit})`,
    hoechstens: (decke: number) => `Höchstens ${decke} laut .env.`,
    bereich: (von: number, bis: number) => `Zwischen ${von} und ${bis}.`,
    aufnahmeDauer: 'Längste Sprachaufnahme',
    aufnahmeDauerHinweis: 'Danach endet die Aufnahme von selbst und wartet auf Senden. 120 sind zwei Minuten.',
    auswahlMax: 'Weitere Bilder in „Aus dieser Reise"',
    auswahlMaxHinweis: 'Was zu einem POI passt, steht immer vollständig da. Darunter stehen höchstens so viele weitere.',
  },

  dienste: {
    titel: 'Ort und Wetter',
    lead: 'Ortsname und Wetter zu jedem POI auf der Karte, zur Stunde, in der du dort warst.',
    ortsnamen: 'Ortsnamen',
    wetter: 'Wetter',
    /** Um den Dateinamen herum, der als `<code>` dazwischensteht. */
    envVor: 'Der Server liest die ',
    envNach:
      ' bei jeder Anfrage neu — ein Neustart ist nicht nötig. Diese Seite zeigt den Stand nach dem nächsten Neuladen.',
    schluesselFehlt: 'Die Schlüsseldatei fehlt: Unter APPLE_PRIVATE_KEY_PATH in der .env liegt keine Datei, die der Server lesen kann. Prüf Pfad und Leserechte. Ein relativer Pfad zählt ab dem Verzeichnis der index.php. Bis dahin gibt es keine Ortsnamen und kein Wetter.',
    aus: 'Nicht eingerichtet. Vorschläge aus der Route heißen dann „Aufenthalt, 2 Std." statt nach ihrem Ort. Wie du die Dienste einrichtest, steht in der Installationsanleitung.',
    halb: 'Eines von beiden fehlt. Prüf die .env auf dem Server:',
    an: 'Beides läuft. Der Server holt die Angaben einmal je Highlight. „In der Nähe finden" nutzt denselben Zugang auf Knopfdruck.',
    umkreis: 'Umkreis der Umgebungssuche (Meter)',
    umkreisHinweis: 'So weit sucht „In der Nähe finden". 500 m sind Gehweite. Mehr bringt mehr Treffer, aber auch solche, an denen niemand war.',
    weitUmkreis: 'Umkreis für weitere Orte (Meter)',
    weitUmkreisHinweis: 'Für die Spots, die unter einem POI aufgelistet werden. Er darf größer sein als der obere.',
    weitZuKlein: 'Zurzeit ist er kleiner als der obere.',
    eingerichtet: 'eingerichtet',
    buchEmojiFehlt: 'Die Datei fonts/noto-emoji-72.zip fehlt auf dem Server oder ist nicht lesbar. Im PDF und EPUB fehlen dann alle Emojis. Sie liegt im Bündel unter fonts/.',
    nichtEingerichtet: 'nicht eingerichtet',
  },

  farben: {
    /** The animation of the day card (#90): the administration's default and the trip's own choice. */
    animation: {
      title: 'Animation der Streckenstatistik',
      on: 'Route als Video zeigen',
      length: 'Länge des Videos',
      fixed: 'Fest',
      seconds: 'Sekunden',
      secondsShort: 's',
      byDistance: 'Nach Strecke',
      /** The longest film (1.122.0): the ceiling of a slowed fixed length and of „Nach Strecke“. */
      most: 'Höchstens',
      mostLabel: 'Höchstlänge des Videos in Sekunden',
      /** The rate of „Nach Strecke“ (1.97.0), stored in tenths of a second. */
      rate: 'Sekunden je 10 km',
      rateUnit: 's je 10 km',
      rateValue: (tenths: number) => (tenths / 10).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      pins: 'Highlights erscheinen, wenn das Fahrzeug vorbeikommt',
      /** The stop at a highlight (1.100.0). */
      pause: 'Halt an jedem Highlight',
      pauseValue: (tenths: number) =>
        tenths === 0 ? 'kein Halt' : `${(tenths / 10).toLocaleString('de-DE', { maximumFractionDigits: 1 })} s`,
      /** The stop where the vehicle changes (1.150.0), with the same values as `pauseValue`. */
      change: 'Halt beim Fahrzeugwechsel',
      /**
       * Under a fixed length (1.104.0): the speed of a section changes it.
       * Since 1.149.0 a film over the length is a question, not a ceiling;
       * the sentence said "über … nur, wenn die Halte allein schon länger
       * dauern" until 1.163.0.
       */
      fixedSpeedHint: (min: number, max: number) =>
        `Die Länge gilt bei normalem Tempo. Läuft ein Abschnitt schneller oder langsamer, wird das Video kürzer oder länger, nicht kürzer als ${min} s. Wird es länger als eingestellt, fragt die App beim Erstellen, ob es länger werden darf, höchstens ${max} s.`,
    },
    listeEigeneLead: 'Gilt nur für dich, auf allen deinen Geräten. Eigene Reisen tragen zusätzlich einen Streifen am linken Rand.',
    listeVorgabeLead: 'Die Vorgabe für alle. Wer sich selbst etwas anderes einstellt, sieht seine eigene Wahl.',
    voreinstellung: 'Voreinstellung',
    allesVoreinstellung: 'Alles auf Voreinstellung',
    /**
     * Die zwei Zeilen der Vorschau.
     *
     * Sie standen als deutscher Text im JSX -- ein Rest, den der Prüflauf
     * `texte.ts` nicht gesehen hat, weil er in einem Textknoten über mehrere
     * Zeilen steht. Beim Anfassen mitgenommen.
     */
    listeBeispielEigen: 'Deine eigene Reise',
    listeBeispielFremd: 'Eine Reise, bei der du mitliest',
    /*
     * **Und die zwei Sprechblasen darunter** (1.62.0). Genau derselbe Fall
     * wie die zwei Zeilen darüber, nur eine Vorschau weiter -- sie standen
     * noch im JSX. Die zweite hätte der Lauf auch mit mehrzeiligen
     * Textknoten nicht gemeldet: in ihr steht kein Wort aus seiner Liste.
     * Sie gehören zusammen, also ziehen sie zusammen um.
     */
    blasenBeispielAndere: 'Sind die Bilder von heute?',
    blasenBeispielEigen: 'Ja, vom Pass — es hat den ganzen Tag geregnet.',
    automatisch: 'Automatisch',
    allesAutomatisch: 'Alles automatisch',
    blasen: 'Farbe der Sprechblasen',
    blasenLead: 'Die Vorgabe für neue Reisen. Bei einer bestehenden Reise steht die Farbe auf ihrer Infoseite unter „Aussehen".',
    /** Die Voreinstellung für die Karte einer neuen Reise (#50). */
    karte: {
      titel: 'Karte',
      lead: 'Die Vorgabe für neue Reisen: Kartenbild, Farbe und Stärke der Route, Farben der Nadeln, Linie der Streckenstatistik. Eine bestehende Reise stellt der Autor / die Autorin auf ihrer Karte ein, unter „Bearbeiten".',
      kartenbild: 'Kartenbild',
      fest: 'In der .env ist eine feste Quelle eingetragen (MAP_TILE_URL). Sie gilt für alle Reisen.',
      routenfarbe: 'Farbe der Route',
      strichstaerke: 'Stärke der Route',
      /** Die eigene Linie der Streckenstatistik (#80). */
      statistik: 'Streckenstatistik',
      statistikStaerke: 'Stärke der Linie auf der Streckenstatistik',
      /** Berechnete Routen (1.18.0, #84). */
      verbundenWieRoute: 'Wie die Route',
      nadeln: 'Nadeln und Kärtchen',
      vorgabe: 'Alles auf Vorgabe',
    },
    knoepfe: 'Farbe der Knöpfe',
    knoepfeLead: 'Gilt für alle, in der ganzen App: Knöpfe, Sendeknopf und alles andere in der Akzentfarbe.',
    knoepfeAutomatisch: 'Automatisch heißt: hell und dunkel bekommen je einen eigenen Ton.',
    /** The defaults of a new collage (1.168.0). */
    collage: 'Collage',
    collageLead: 'So beginnt jede neue Collage. Beim Senden lässt sich alles für die einzelne Collage ändern.',
    /*
     * **Die zwei Knöpfe der Vorschau** (1.64.0). Sie tun nichts -- sie zeigen,
     * wie ein Knopf mit der gewählten Farbe aussieht. Eigene Schlüssel und
     * nicht die der Karte (`karte.blatt.freigeben`): dort ist es eine
     * Handlung, hier ein Beispiel, und eine Karte der Einstellungen greift
     * nicht in den Zweig einer anderen Seite.
     */
    probeFreigeben: 'Freigeben',
    probeImportieren: 'GPX importieren',
    /*
     * **Nicht `voreinstellung`, das gibt es hier schon** (das Wort allein, als
     * Text für „nichts eingestellt"). Und derselbe Wortlaut wie am
     * Kartenregler (`karte.regler.voreinstellung`), aus demselben Grund
     * getrennt wie die zwei Knöpfe darüber: zwei Karten, dieselbe Handlung,
     * zwei Zweige. Der Eintrag steht deshalb in `DOPPELT_MIT_GRUND` des
     * Prüflaufs.
     */
    voreinstellungUebernehmen: 'Voreinstellung übernehmen',
    /**
     * Die Namen der einzelnen Farbfelder.
     *
     * Sie standen in `lib/blasenfarben.ts` und `lib/listenfarben.ts` neben
     * den CSS-Variablen. Die Mechanik bleibt dort, die Wörter kommen hierher.
     */
    blasenFelder: {
      chat_own_color: { titel: 'Deine Beiträge', hinweis: 'Rechts, und beim Leser seine eigenen.' },
      chat_other_color: {
        titel: 'Beiträge der anderen',
        hinweis: 'Links, also die Antworten deiner Leser.',
      },
    },
    listenFelder: {
      own: { titel: 'Eigene Reisen', hinweis: 'Berichte, die du selbst schreibst.' },
      other: { titel: 'Reisen anderer', hinweis: 'Berichte, bei denen du mitliest.' },
      bg: { titel: 'Freie Fläche', hinweis: 'Der Platz unter der Liste.' },
      unread: { titel: 'Neue Nachrichten', hinweis: 'Anzahl der ungelesenen Nachrichten einer Reise' },
    },
  },

  /** Die Karten der Diagnose. Messwerte bleiben Messwerte. */
  verbindung: {
    titel: 'Verbindung',
    online: 'Die letzte Anfrage hat den Server erreicht. Was du schreibst, geht sofort raus.',
    offline: 'Der Server ist gerade nicht erreichbar. Was du schreibst, wartet auf dem Gerät.',
    erreicht: 'Zuletzt erreicht',
    browserMeldet: 'Der Browser meldet',
    wartende: 'Wartende Nachrichten',
    ja: 'ja',
    nein: 'nein',
    taubesNetz: 'Das Gerät hängt an einem Netz ohne Verbindung nach draußen, zum Beispiel einem WLAN ohne Internet. Das passiert auch im Flugmodus mit eingeschaltetem WLAN.',
    unverschluesselt: (protokoll: string) =>
      `Diese Seite läuft unverschlüsselt (${protokoll}). Benachrichtigungen gehen so nicht, und jeder im selben Netz kann mitlesen. Ruf sie über https:// auf. Ein Symbol auf dem Home-Bildschirm legst du von der sicheren Adresse aus neu an.`,
  },

  tastatur: {
    titel: 'Tastatur',
    lead: 'Tippe ins Feld. Bleibt es über der Tastatur sichtbar, sitzt es richtig.',
    feld: 'Hier tippen',
    feldName: 'Testfeld für die Tastatur',
    hoeheApp: 'Höhe der App',
    vonFenster: (fenster: number | string) => `von ${fenster}`,
    abstand: 'Freigehaltener Abstand',
    fensterhoehe: 'Fensterhöhe',
    sichtbareHoehe: 'Sichtbare Höhe',
    versatz: 'Versatz der Fläche',
    seitenversatz: 'Seitenversatz',
    gesetzteHoehe: 'Gesetzte Höhe',
    nichtGesetzt: 'nicht gesetzt',
    greiftNicht: 'greift nicht',
    dvh: '100dvh ergibt',
    bildschirm: 'Bildschirm',
    fensterbreite: 'Fensterbreite',
    alsApp: 'Als App',
    mindesthoehe: 'Mindesthöhe vom Bildschirm',
    raender: 'Sicherheitsabstände',
    mitschrieb: 'Mitschrieb: Fenster / sichtbar / gesetzt / App / Unterkante · Abstand · Seite ↕ Fläche · Kopfzeile oben / Rand / Abstand oben · gemerkte Tastatur / angenommen, neueste zuerst:',
    vermutetOffen: 'offen',
    vermutetZu: 'zu',
    senden: 'Senden und Videos, neueste zuerst:',
    sendenLeer: 'Noch nichts. Schreib im Chat etwas und tippe auf Senden, oder häng ein Video an, dann komm zurück.',
  },

  spruenge: {
    titel: 'Sprünge aus Benachrichtigungen',
    lead: 'Was beim letzten Tipp auf eine Benachrichtigung passiert ist. Oben der Hintergrunddienst, unten die App. Diese Daten bleiben auf dem Gerät.',
    oberflaeche: 'Oberfläche',
    imHintergrund: 'Im Hintergrund',
    inDerApp: 'In der App',
    wirdGelesen: 'Wird gelesen …',
    keinWorker: 'Auf diesem Gerät läuft kein Hintergrunddienst. Ohne ihn gibt es keine Benachrichtigungen.',
    keineAntwort: 'Der Hintergrunddienst antwortet nicht. Ob er etwas aufgezeichnet hat, bleibt offen.',
    nichts: 'Der Hintergrunddienst hat nichts aufgezeichnet: Seit dem letzten Aufräumen kam keine Benachrichtigung an.',
    nichtsInDerApp: 'Seit dem Start ist hier nichts angekommen.',
    neuLesen: 'Neu lesen',
    /**
     * Die Zeile, die „nicht getippt" von „getippt, kam nicht an" trennt.
     *
     * Eine App kommt nicht von selbst nach vorn. Steht hier ein Zeitpunkt
     * wenige Sekunden nach einer Push-Nachricht und oben kein „Angetippt", dann
     * hat jemand getippt und der Service Worker hat es nicht erfahren.
     */
    zuletztVorn: (zeit: string) => `Zuletzt nach vorn gekommen: ${zeit}`,
    nieVorn: 'Die App war noch nicht im Hintergrund.',
    workerStand: (stand: string | null) =>
      stand === null ? 'Stand des Hintergrunddienstes: unbekannt' : `Stand des Hintergrunddienstes: ${stand}`,
  },

  stand: {
    /**
     * **Die Karte steht ganz oben in der Diagnose** — aus dem Betrieb: „die
     * Infos sind oft wichtiger als die von weiter unten". Sie beantwortet die
     * Frage vor allen anderen: welche Version läuft hier?
     */
    titel: 'Stand',
    lead: 'Welche Version dieses Gerät gerade fährt und welche der Server. Das Erste, was bei einer Meldung gefragt wird.',
    zeile: (version: string, oberflaeche: string, server: string) =>
      `Travel Broadcast ${version} · Oberfläche ${oberflaeche} · Server ${server}`,
    anzeige: (art: string, herkunft: string) => `Anzeige: ${art} · ${herkunft}`,
    gelesen: (ziel: string) => `beim Öffnen gelesen: ${ziel}`,
    gemerkt: (ziel: string) => `jetzt gemerkt: ${ziel}`,
    uebersicht: 'Übersicht',
    keinZiel: 'nichts',
    chat: (pfad: string) => `Chat ${pfad}`,
    app: 'App',
    browser: 'Browser',
    browserOhneLeisten: 'Browser (ohne Leisten)',
    unsicher: 'unsicher',
    /**
     * Was oben freigehalten wird (#48, seit 1.54.1 mit Zahlen).
     *
     * Beide Zahlen, weil erst der Vergleich etwas sagt: sind sie gleich,
     * zählt der Rand; stehen sie auseinander, meldet das Gerät selbst einen
     * Abstand, und der Rand tut nichts.
     */
    glasband: (rand: number, kopfzeile: number) =>
      `Rand oben für iOS 27: ${rand === 0 ? 'nein' : `${rand} px`}, Kopfzeile ${kopfzeile} px`,
    /** Der Schalter dazu, je Gerät (1.53.3). */
    glasbandSchalter: 'Rand oben für iOS 27 freihalten',
    glasbandHinweis:
      'Zum Prüfen abschalten, dann läuft die Kopfzeile wieder bis oben. Vorher die Seite in Safari neu laden, das Symbol löschen und die App neu zum Home-Bildschirm legen.',
  },

  /**
   * Der Chat — Verlauf, Eingabezeile und was daran hängt.
   *
   * **Der Wortlaut ist beim Umzug Zeichen für Zeichen derselbe geblieben.**
   * Gemeckert war über die Einstellungen (#67), und die sind erledigt; ob
   * hier etwas sperrig klingt, entscheidet sich besser an dieser Datei als
   * beim Verschieben.
   */
  chat: {
    // --- Auswählen und löschen ---------------------------------------------
    auswahlLeiste: 'Nachrichten löschen',
    nichtsAusgewaehlt: 'Nichts ausgewählt',
    nachrichten: (anzahl: number) => (anzahl === 1 ? '1 Nachricht' : `${anzahl} Nachrichten`),
    ausgewaehlt: (nachrichten: string) => `${nachrichten} ausgewählt`,
    loeschenName: (nachrichten: string) => `${nachrichten} löschen`,
    /** Was ausgewählt ist: Nachrichten und Tagestrenner getrennt gezählt (1.86.4). */
    auswahlWorte: (nachrichten: number, trenner: number) => {
      const n = nachrichten === 1 ? '1 Nachricht' : `${nachrichten} Nachrichten`
      const k = `${trenner} Tagestrenner`
      if (trenner === 0) return n
      if (nachrichten === 0) return k
      return `${n} und ${k}`
    },
    loeschenFrage: (was: string) => `${was} löschen?`,
    loeschenKnopf: 'Löschen',
    teilweiseGeloescht: (weg: number, alle: number) => `${weg} von ${alle} gelöscht. Der Rest blieb stehen.`,

    // --- Antworten der Mitlesenden -----------------------------------------
    antworten: 'Antworten',
    antwortenName: 'Antworten der Mitlesenden',
    antwortenLead:
      'Was dir die Mitlesenden geschrieben haben. Jeder Verlauf gehört nur euch beiden — die anderen sehen ihn nicht.',
    ungelesenJenseits:
      'Weiter oben liegt noch Ungelesenes — mehr, als sich auf einmal nachladen lässt.',
    antwortenUngelesen: (ungelesen: number) =>
      `Antworten der Mitlesenden, ${ungelesen} ungelesen`,

    // --- Was im Ausgangskorb wartet ----------------------------------------
    wartet: '⏳ wartet auf Netz',
    /**
     * Netz ist da, und trotzdem geht es nicht hinaus.
     *
     * Aus dem Betrieb: „es steht nach wie vor wartet auf Netz, obwohl Netz da
     * ist." Genau das darf nicht dastehen -- der Satz war dann falsch, und
     * daneben stand kein Knopf, mit dem sich etwas hätte tun lassen.
     */
    gehtNichtRaus: '⚠︎ geht gerade nicht raus',
    nichtAngenommen: '⚠︎ nicht angenommen',
    nochmal: 'Nochmal versuchen',
    wartendeVerwerfen: 'Diese wartende Nachricht verwerfen?',

    // --- Abstimmungen ------------------------------------------------------
    abstimmung: 'Abstimmung',
    /** Die Wetterkarte (0.31.0): ein Eintrag im Plus-Menü und drei Stände dahinter. */
    wetter: 'Aktuelles Wetter',
    wetterOrtung: 'Standort wird ermittelt …',
    wetterHolen: 'Das Wetter wird geholt …',
    wetterOhneNetz: 'Ohne Netz lässt sich das Wetter nicht holen.',
    /** Ohne Netz in den Ausgangskorb (#98, 1.54.0): Ort und Zeit sind gemerkt. */
    wetterWartet: 'Kein Netz: Ort und Zeit sind gemerkt. Die Wetterkarte kommt, sobald Netz da ist.',
    /** Das Wetter an einem gewählten Ort zu einer gewählten Stunde (#83, 1.13.0): der zweite Eintrag und das Blatt dahinter. */
    wetterAnderswo: 'Wetter an Ort und Zeit',
    wetterWo: 'Wo und wann?',
    wetterOrt: 'Ort',
    wetterOrtSuchen: 'Ort suchen',
    wetterOrtAendern: 'Anderen Ort suchen',
    wetterKeinOrt: 'Noch kein Ort gewählt.',
    wetterTag: 'Tag',
    wetterUhrzeit: 'Uhrzeit',
    wetterVorschlag: 'Vorbelegt mit der Zeit vor zwölf Stunden. Das Wetter gibt es für vergangene Stunden.',
    wetterZukunft: 'Der Zeitpunkt liegt in der Zukunft.',
    wetterSenden: 'Karte senden',
    /** Der zweite Weg zu Ort und Zeit (1.14.0): ein Foto der Reise mit Ortsangabe. */
    wetterAusFoto: 'Aus einem Foto',
    wetterFotoTitel: 'Foto mit Ortsangabe',
    wetterFotoKeins: 'In dieser Reise gibt es kein eigenes Foto mit Ortsangabe.',
    wetterFotoOrt: (zeit: string) => `Aus dem Foto vom ${zeit}`,
    wetterFotoHinweis: 'Ort und Zeit kommen aus dem Foto. Beides lässt sich danach ändern.',
    /** Der letzte Tag (#79): ein Eintrag im Plus-Menü und drei Stände dahinter. */
    streckenstatistik: 'Streckenstatistik',
    tagAuswerten: 'Die Route wird ausgewertet …',
    tagZeichnen: 'Die Karte wird gezeichnet …',
    tagSchicken: 'Die Karte wird gesendet …',
    tagOhneNetz: 'Ohne Netz lässt sich der Tag nicht auswerten.',
    tagWelcher: 'Welcher Tag?',
    /** What the day card shows (#132). */
    dayDisplay: 'Zeigen',
    dayDisplayChoice: { both: 'Zahlen und Karte', map: 'Nur Karte', numbers: 'Nur Zahlen' },
    /** While a chosen day is being read (1.100.0): it stands under a running bar. */
    tagLaedt: 'Der Tag wird geladen …',
    /** The running bar over the chat while a jump loads up to its post (1.102.0). */
    jumpLoading: 'Der Beitrag wird geladen …',
    chatLoading: 'Der Chat wird geladen …',
    /**
     * A change of the zone course after a landing (1.100.0): the sheet
     * proposes to move it. Both times are in the new zone.
     */
    landingZoneHint: (zone: string, from: string, landing: string) =>
      `Laut Reise gilt ${zone} erst ab ${from}. Gelandet bist du um ${landing}.`,
    /** The same, for a change before the take-off. */
    landingZoneEarlyHint: (zone: string, from: string, landing: string) =>
      `Laut Reise gilt ${zone} schon ab ${from}, vor dem Abflug. Gelandet bist du um ${landing}.`,
    landingZoneApply: 'Ab der Landung umstellen',
    tagFeld: 'Tag der Route',
    tagVon: 'Von',
    tagBis: 'Bis',
    tagTeilHinweis: 'Vorbelegt mit der ersten und letzten Bewegung des Tages. Wer nur einen Teil will, ändert die Zeiten.',
    /** Several days (1.151.0): a last day instead of the times. */
    tagBisTag: 'Bis Tag',
    tagNurDieser: 'Nur dieser Tag',
    tagMehrereHinweis: 'Von gilt für den ersten Tag, Bis für den letzten. Vorbelegt mit der ersten und letzten Bewegung.',
    tagVonErster: 'Von (erster Tag)',
    tagBisLetzter: 'Bis (letzter Tag)',
    /** Only the steps in motion (1.151.0), by the rule of the time in motion. */
    tagNurFahrt: 'Nur Fahrt',
    tagNurFahrtHinweis: 'Zählt die Strecke wie die Fahrzeit: Stehen und ein ruhendes Telefon zählen nicht mit.',
    /** The rows on the card (1.151.0), off for a new card. */
    tagZeilenAbschnitte: 'Abschnitte auf der Karte zeigen',
    tagZeilenTage: 'Eine Zeile je Tag zeigen',
    tagSenden: 'Karte senden',
    /** A card computed anew (1.86.0); "Speichern" until 1.125.0, from use: "sollte das nicht eher heißen ‚Erstellen'?" */
    tagNeuErstellen: 'Neu erstellen',
    /** The sections of the day and the film (#90). */
    daySections: 'Abschnitte',
    dayNextDay: '+1 Tag',
    /** The length the film will have when a section's speed is not normal (1.104.0). */
    dayFilmLength: (seconds: number, normal: number) =>
      seconds === normal ? `Das Video dauert ${seconds} s.` : `Mit diesem Tempo dauert das Video ${seconds} s statt ${normal} s.`,
    /**
     * The video longer than its limit (1.149.0): asked, never decided alone.
     * From use: "falls der Film zu lang wird: Abfrage: Film länger oder
     * dynamische Anpassungen".
     */
    filmFit: {
      overFixed: (natural: number, limit: number) => `Das Video würde ${natural} s dauern. Eingestellt sind ${limit} s.`,
      overMost: (natural: number, limit: number) => `Das Video würde ${natural} s dauern. Höchstens eingestellt sind ${limit} s.`,
      longer: (seconds: number) => `Länger machen: ${seconds} s`,
      fit: (seconds: number) => `Alles gleichmäßig kürzen: ${seconds} s`,
      choose: 'Wähle, wie das Video passen soll.',
    },
    dayFilmUnsupported: 'Dieses Gerät kann kein Video rechnen. Die Karte geht als Bild.',
    dayFilmNoSection: 'Für diesen Teil des Tages gibt es keine Strecke zum Abfahren. Die Karte geht als Bild.',
    /** The complex film beside the simple one (#139, 1.118.0, `TourFields`). */
    tour: {
      follow: 'Fahrzeug verfolgen',
      tilt: 'Gekippt (3D)',
      profile: 'Höhenprofil',
      map: 'Kartenbild',
      mapTrip: 'Wie die Reise',
      /** The height on the profile in the film's picture. */
      heightLabel: (metres: string) => `${metres} m`,
      /** The letter on the film's north arrow (1.140.0). */
      compassNorth: 'N',
      noHeights: 'Die Aufzeichnung hat keine Höhen. Das Video kommt ohne Höhenprofil.',
      /** The fifth switch (#140, 1.129.0). */
      terrain: 'Gelände',
      terrainHint: 'Karte von OpenFreeMap, Höhen von AWS. Beides lädt dein Gerät nur für dieses Video.',
      noTerrain: 'Das Gelände ließ sich nicht laden. Das Video kommt ohne Gelände.',
      /** The sixth switch (1.137.0). */
      vehicle3d: '3D-Fahrzeug',
      noVehicle3d: 'Das 3D-Fahrzeug ließ sich nicht zeichnen. Das Video kommt mit dem Symbol.',
      /** Beside a card's kept video (1.132.0): make it anew although nothing changed. */
      rebuild: 'Video neu rechnen',
    },
    film: {
      title: 'Video der Route',
      /** Before the tiles (1.125.0): the server reads the day, the device is asked what it can encode. */
      prepare: 'Route auswerten',
      map: 'Karte laden',
      compute: 'Video rechnen',
      upload: 'Hochladen',
      share: (percent: number) => `${percent} %`,
      hint: 'Bleib in der App, bis das Video fertig ist. Wenn du abbrichst oder die App verlässt, wird nichts gesendet.',
      cancelled: 'Abgebrochen. Es wurde nichts gesendet.',
      left: 'Die App wurde verlassen. Es wurde nichts gesendet. Starte die Streckenstatistik neu.',
      /** Nothing the video was made from changed (1.132.0): it stays. */
      kept: 'Nichts geändert, das Video bleibt.',
    },

    /** The filter of the chat (1.93.0, #114): the funnel in the header and its sheet. */
    filter: {
      knopf: 'Nachrichten filtern',
      titel: 'Was soll im Chat stehen?',
      alle: 'Alle Nachrichten',
      beitraege: 'Nur Beiträge an alle',
      privat: 'Nur private Nachrichten',
      /** The header line while filtered: what stands, and how many new ones are hidden. */
      hinweis: (art: 'beitraege' | 'privat', neu: number) =>
        (art === 'beitraege' ? 'Nur Beiträge an alle' : 'Nur private Nachrichten')
        + (neu > 0 ? ` · ${neu} neue ausgeblendet` : ''),
      leerBeitraege: 'Hier stehen noch keine Beiträge an alle.',
      leerPrivat: 'Hier stehen noch keine privaten Nachrichten.',
      alleZeigen: 'Alle zeigen',
    },
    /**
     * Der Tagestrenner (#113, 1.85.0): der Eintrag im Plus-Menü, das Blatt,
     * der Chip über der Eingabezeile, die Zeile im Chat, die drei Einträge im
     * Menü beim langen Drücken und die Liste hinter dem Kalender in der Pille.
     */
    tagestrenner: {
      eintrag: 'Tagestrenner',
      /** Das Blatt. */
      titelNeu: 'Tagestrenner',
      titelAendern: 'Tagestrenner ändern',
      tag: 'Datum',
      nummer: 'Tag',
      wort: 'Wort',
      wortTag: 'Tag',
      wortDay: 'Day',
      erlaeuterung: 'Erläuterung (optional)',
      erlaeuterungBeispiel: 'z. B. von New York nach Burlington VT',
      uebernehmen: 'Übernehmen',
      speichern: 'Speichern',
      /** Die Vorschau im Blatt und die Zeile im Chat: Datum, Zähler, Wochentag. */
      zeile: (datum: string, zaehler: string, wochentag: string) => `${datum} · ${zaehler} · ${wochentag}`,
      zaehler: (wort: 'tag' | 'day', nummer: number) => `${wort === 'day' ? 'Day' : 'Tag'} ${nummer}`,
      /** Im Menü des Trenners, nur für den Autor / die Autorin. */
      menueAendern: 'Tagestrenner ändern',
      /** Im Blatt eines neuen Trenners (#130). */
      nachnummerieren: 'Folgende Tagestrenner neu nummerieren',
      ohneNetz: 'Ohne Netz lässt sich ein Tagestrenner nicht ändern.',
      /** Die Liste hinter dem Kalender in der Pille. */
      listeTitel: 'Zu einem Tag der Reise springen',
      listeLeer: 'Noch kein Tagestrenner.',
      listeZeile: (zaehler: string, wochentag: string, datum: string) => `${zaehler} · ${wochentag}, ${datum}`,
      /** Was im Ausgangskorb wartet. */
      wartet: (zaehler: string, datum: string) => `Tagestrenner ${zaehler} · ${datum}`,
    },
    /** Einfügen an einem Beitrag (#130): das Menü und die Leiste über der Eingabezeile. */
    /** The author's private message to everyone (1.157.0). */
    /** A collage of two to four photos (1.168.0). */
    collage: {
      menue: 'Collage',
      titel: 'Collage',
      aendernTitel: 'Collage ändern',
      fotosWaehlen: 'Fotos wählen',
      fotoDazu: 'Foto hinzufügen',
      fotoEntfernen: (nummer: number) => `Foto ${nummer} entfernen`,
      tauschen: (nummer: number) => `Foto ${nummer} tauschen`,
      tauschHinweis: 'Tippe zwei Fotos an, um sie zu tauschen. Das erste ist das große.',
      aufteilung: 'Aufteilung',
      layout: {
        columns: 'Nebeneinander',
        rows: 'Übereinander',
        'big-left': 'Groß links',
        'big-top': 'Groß oben',
        grid: 'Zwei mal zwei',
      } as Record<string, string>,
      ecken: 'Ecken',
      steg: 'Steg',
      stegfarbe: 'Farbe des Stegs',
      punkte: (zahl: number) => `${zahl} pt`,
      zuWenige: 'Wähle mindestens zwei Fotos.',
      zuViele: 'Eine Collage hat höchstens vier Fotos.',
      senden: 'Collage senden',
      laedt: 'Fotos werden geladen …',
      ohneNetz: 'Für eine Collage brauchst du Netz.',
      fehler: (grund: string) => `Die Collage konnte nicht gesendet werden. ${grund}`,
      speichernFehler: (grund: string) => `Die Collage konnte nicht gespeichert werden. ${grund}`,
    },
    publishToAll: {
      menue: 'An alle senden',
      /** The question over a selection (1.167.0), with `auswahlWorte()`. */
      frage: (was: string) => `${was} an alle senden?`,
      erklaerung: (anzahl: number) =>
        anzahl === 1
          ? 'Aus der privaten Nachricht wird ein Beitrag an alle, am Ende des Reise-Chats. Alle bekommen eine Mitteilung.'
          : 'Aus jeder privaten Nachricht wird ein Beitrag an alle, am Ende des Reise-Chats und in derselben Reihenfolge. Alle bekommen eine Mitteilung.',
      knopf: 'An alle senden',
      /** The bar's button (1.167.0). */
      knopfName: (was: string) => `${was} an alle senden`,
      /** With the server's reason; a selection goes all or none (1.167.0). */
      fehler: (anzahl: number, grund: string) =>
        anzahl === 1
          ? `Die Nachricht konnte nicht an alle gesendet werden. ${grund}`
          : `Die Nachrichten konnten nicht an alle gesendet werden, keine ist gesendet. ${grund}`,
    },
    /** Posts to another place (#130, 1.117.0). */
    verschieben: {
      /** The menu entry (1.117.3); the bar's button carries the same word as its name. */
      menue: 'Verschieben',
      knopf: 'Verschieben',
      leiste: (anzahl: number) =>
        anzahl === 1 ? 'Einen Beitrag verschieben. Tippe auf das Ziel.' : `${anzahl} Beiträge verschieben. Tippe auf das Ziel.`,
      zielName: 'Als Ziel wählen',
      frage: (anzahl: number) => (anzahl === 1 ? 'Wohin kommt der Beitrag?' : `Wohin kommen die ${anzahl} Beiträge?`),
      ziel: (tag: string, worte: string) => (worte === '' ? `Ziel: Beitrag vom ${tag}` : `Ziel: „${worte}“ vom ${tag}`),
      davor: 'Davor',
      danach: 'Danach',
      fertig: (anzahl: number) => (anzahl === 1 ? 'Der Beitrag ist verschoben.' : `${anzahl} Beiträge sind verschoben.`),
    },
    einfuegen: {
      menueDavor: 'Beitrag davor einfügen',
      menueDanach: 'Beitrag danach einfügen',
      leisteDavor: (text: string) => `Wird davor eingefügt: ${text === '' ? 'Beitrag ohne Text' : text}`,
      leisteDanach: (text: string) => `Wird danach eingefügt: ${text === '' ? 'Beitrag ohne Text' : text}`,
      weg: 'Nicht einfügen',
      brauchtNetz: 'Ohne Netz geht nur ein neuer Beitrag am Ende.',
    },
    /** Ein Link in einem Text (1.113.0): das Blatt, bevor er im Browser aufgeht. */
    link: {
      frage: (host: string) => `${host} im Browser öffnen?`,
      oeffnen: 'Im Browser öffnen',
      vonMitlesend: 'Diesen Link hat eine mitlesende Person geschickt.',
      warnung: {
        unverschluesselt: 'Die Verbindung ist nicht verschlüsselt (http).',
        ip: 'Statt eines Namens steht dort nur eine Zahl (IP-Adresse).',
        fremdeSchrift: 'Im Namen stehen Zeichen aus einer anderen Schrift. So sehen gefälschte Seiten oft echt aus.',
        zugangsdaten: 'In der Adresse steht ein Benutzername. Damit wird oft ein anderes Ziel vorgetäuscht.',
        port: 'Die Adresse nutzt einen ungewöhnlichen Anschluss (Port).',
        kurzlink: 'Das ist ein Kurzlink. Wohin er führt, siehst du erst nach dem Öffnen.',
      } as Record<string, string>,
    },
    /** Link zur Karte (1.113.0): Punkte, die schon auf der Karte stehen, als Beitrag. */
    kartenlink: {
      eintrag: 'Link zur Karte',
      titel: 'Link zur Karte',
      leer: 'Es gibt noch keine freigegebenen POIs.',
      laedt: 'Wird geladen …',
      imChat: 'Steht schon im Chat',
      hoechstens: (zahl: number) => `Höchstens ${zahl} POIs in einem Beitrag.`,
      hinzufuegen: (zahl: number) => (zahl === 0 ? 'Hinzufügen' : `Hinzufügen (${zahl})`),
      sendet: 'Der Link zur Karte wird gesendet …',
      ohneNetz: 'Ohne Netz lässt sich kein Link zur Karte hinzufügen.',
    },
    /** Musik im Chat (#102, 1.57.0): der Eintrag, das Blatt und die Stände. */
    musik: 'Musik hinzufügen',
    musikTitel: 'Musik hinzufügen',
    musikFeld: 'Link von Spotify oder Apple Music',
    musikPlatzhalter: 'https://open.spotify.com/track/…',
    musikText: 'Dein Text dazu',
    musikTextPlatzhalter: 'Der Song für die Fahrt über die Lofoten',
    musikHinweis: 'Kopier den Link in Spotify oder Apple Music über „Teilen". Er wird nur geprüft, gesucht wird nichts.',
    musikExtern: 'Beim Anzeigen lädt der Player von Spotify oder Apple Music. Die beiden erfahren dabei, wer zusieht.',
    musikSenden: 'Musik senden',
    musikSchicken: 'Die Musik wird gesendet …',
    musikOhneNetz: 'Ohne Netz lässt sich keine Musik hinzufügen.',
    /** Was in der Karte steht -- die Schreibweise des Anbieters wird hier zum Wort. */
    musikAnbieter: { spotify: 'Spotify', apple_music: 'Apple Music' } as Record<string, string>,
    musikArten: {
      track: 'Titel',
      song: 'Titel',
      album: 'Album',
      playlist: 'Playlist',
      artist: 'Künstler',
      show: 'Podcast',
      episode: 'Folge',
    } as Record<string, string>,
    musikOeffnen: (anbieter: string) => `In ${anbieter} öffnen`,
    musikPlayer: (anbieter: string, art: string) => `${anbieter}: ${art}`,
    musikAbspielenBei: (anbieter: string) => `Bei ${anbieter} abspielen`,
    musikAnhalten: 'Anhalten',
    musikLaedt: 'Lädt …',
    musikAnmelden: 'Bei Apple Music anmelden',
    musikWeiterZuApple: 'Weiter zur Anmeldung bei Apple',
    musikAusschnitt: 'Ohne Apple-Music-Abo abspielen',
    /** Unter Apples Player: nach der Anmeldung einmal neu laden (1.73.0). */
    musikNeuLaden: 'Player neu laden',
    musikAbmelden: 'Bei Apple Music abmelden',
    musikAnmeldungFehlt: 'Die Anmeldung ist nicht angekommen. Den Grund findest du unter Einstellungen › Diagnose.',
    musikAbspielenFehlt: 'Apple Music spielt den Titel nicht ab. Den Grund findest du unter Einstellungen › Diagnose.',
    musikLadenFehlt: 'Apple Music ließ sich nicht laden. Den Grund findest du unter Einstellungen › Diagnose.',
    musikOhneWarteschlange: 'Einen Künstler spielt Apple Music hier nicht ab. Der Knopf darunter führt hin.',
    abstimmungAnlegen: 'Abstimmung anlegen',
    /** Eine Sprachnachricht an einer wartenden Blase (1.80.0), mit ihrer Länge als m:ss. */
    tonWartet: (dauer: string) => `Sprachnachricht · ${dauer}`,
    /** Eine Abstimmung, die ohne Netz im Ausgangskorb wartet (1.80.0). */
    abstimmungKommtMitNetz: 'Die Abstimmung geht raus, sobald Netz da ist.',
    frage: 'Frage',
    fragePlatzhalter: 'Wohin morgen?',
    antwortenFeld: 'Antworten',
    antwortPlatzhalter: (i: number) => (i === 0 ? 'Berge' : i === 1 ? 'Meer' : 'Noch etwas'),
    mehrfachErlauben: 'Mehrfachauswahl erlauben',
    abstimmungSenden: 'Abstimmung senden',
    abstimmungUnfertig: 'Es braucht eine Frage und zwei verschiedene Antworten.',
    antwortDazu: 'Antwort hinzufügen',
    stimmen: (anzahl: number) => (anzahl === 1 ? 'Stimme' : 'Stimmen'),
    ergebnis: 'Ergebnis',
    ergebnisZahl: (anzahl: number) => `Ergebnis · ${anzahl} ${anzahl === 1 ? 'Stimme' : 'Stimmen'}`,
    stimmenZeile: (anzahl: number, offen: boolean) =>
      `${anzahl} ${anzahl === 1 ? 'Stimme' : 'Stimmen'} · `
      + (offen ? 'noch offen, Stimmen können sich ändern' : 'geschlossen'),
    werWie: 'Wer wie',
    niemandAbgestimmt: 'Noch hat niemand abgestimmt.',
    geschlossen: 'Geschlossen',
    mehrfach: 'Mehrfachauswahl',
    // Closing with the right answer (#143, 1.138.0).
    pollCloseTitle: 'Abstimmung schließen',
    pollCloseHint: 'Danach kann niemand mehr abstimmen. Das Ergebnis bleibt stehen.',
    pollRightHeading: (multi: boolean) =>
      multi ? 'Richtige Antworten (freiwillig)' : 'Richtige Antwort (freiwillig)',
    pollRightTitle: 'Richtige Antwort',
    pollRightSave: 'Speichern',
    /** For the screen reader, next to the sign. */
    pollRightSr: ', richtige Antwort',
    pollWrongSr: ', deine Wahl, falsch',
    /** Für eine versehentlich geschlossene Abstimmung (1.126.0). */
    wiederOeffnen: 'Wieder öffnen',
    abstimmungOeffnen:
      'Abstimmung wieder öffnen? Danach kann wieder abgestimmt werden.',
    nochNichtAbgestimmt: (mehrfach: boolean) =>
      (mehrfach ? 'Kreuze an, was passt' : 'Tipp auf eine Antwort')
      + '. Danach siehst du, wie die anderen abgestimmt haben.',

    // --- Was sich anhängen lässt -------------------------------------------
    wasAnhaengen: 'Was anhängen?',
    fotoOderVideo: 'Foto oder Video',
    nurFoto: 'Foto',

    // --- Die Eingabezeile ------------------------------------------------------
    chatGeschlossen: 'Der Chat dieser Reise ist geschlossen.',
    bezugEntfernen: 'Bezug entfernen',
    etwasAnhaengen: 'Etwas anhängen',
    fotosHinzufuegen: 'Fotos hinzufügen',
    fotosUndVideosHinzufuegen: 'Fotos und Videos hinzufügen',
    fotoEntfernen: 'Foto wieder entfernen',
    entfernen: 'Entfernen',
    wartetAufNetz: 'Wartet auf Netz',
    nurVideosAbgewiesen: 'In einer Antwort kannst du Fotos und GIFs schicken, keine Videos.',
    videosAbgewiesen: (anzahl: number) =>
      `${anzahl === 1 ? 'Ein Video bleibt' : `${anzahl} Videos bleiben`} außen vor. `
      + 'In einer Antwort kannst du Fotos und GIFs schicken, keine Videos.',
    nurVideosOhneNetz: 'Ohne Netz geht kein Video. Ein Foto geht auch ohne.',
    videosOhneNetz: (anzahl: number) =>
      `${anzahl === 1 ? 'Ein Video bleibt' : `${anzahl} Videos bleiben`} außen vor. `
      + 'Ohne Netz geht kein Video, die Fotos gehen später von selbst raus.',
    ohneNetzKeinVideo: 'Ohne Netz geht kein Video.',
    bildNichtGeoeffnet: 'Das Bild ließ sich nicht öffnen.',
    mikrofonGesperrt:
      'Das Mikrofon ist nicht freigegeben. Erlaube es in den Einstellungen des Browsers.',
    formatStumm:
      'Dein Browser nimmt in einem Format auf, das Safari nicht abspielt, und das Umwandeln hat nicht geklappt. Auf einem iPhone bleibt diese Aufnahme stumm.',
    aufnahmeNichtDurch: 'Die Aufnahme ging nicht durch.',
    videoBrauchtNetz:
      'Ein Video braucht Netz. Nimm es heraus, dann gehen Text und Fotos später von selbst raus.',
    tonBrauchtNetz:
      'Ohne Netz wartet nur eine Sprachnachricht. Nimm die zweite heraus, dann geht der Rest später von selbst raus.',
    /** Over the line while a voice message is attached (1.116.0). */
    spracheAllein: 'Eine Sprachnachricht geht ohne Text und ohne weitere Anhänge.',
    /** The same, when text stood in the field and was discarded. */
    spracheTextVerworfen: 'Dein Text ist verworfen. Eine Sprachnachricht geht ohne Text und ohne weitere Anhänge.',
    /** Unter einem Eintrag im Plus-Menü, der ohne Netz nicht geht (1.80.0). */
    gehtNurMitNetz: 'Geht nur mit Netz.',
    ausReiseOhneNetz: 'Ohne Netz lassen sich keine Fotos aus der Reise holen.',
    gifBrauchtNetz:
      'Ein GIF braucht Netz. Nimm es heraus, dann gehen Text und Fotos später von selbst raus.',
    reisefotoBrauchtNetz:
      'Was aus dieser Reise kommt, braucht Netz. Nimm es heraus, dann gehen Text und Fotos später von selbst raus.',
    fotoKamNichtDurch: 'Ein Foto kam nicht durch. Prüf es und schick es noch einmal ab.',
    sendetSpaeter: 'Wird gesendet, sobald alles durch ist …',
    fertigVon: (fertig: number, alle: number) => `${fertig} von ${alle} fertig …`,
    nachrichtNichtRaus: 'Die Nachricht ging nicht raus.',
    videoWirdVerkleinert: 'Video wird verkleinert …',
    kompatibelHinweis: 'Wähle beim Teilen auf dem iPhone',
    kompatibelBetont: '„Am kompatibelsten"',
    kompatibelRest: ', damit sie auch am Rechner und auf Android laufen.',
    grenzeLiegtBei: (mb: number) => `Die Grenze liegt bei ${mb} MB.`,
    ueberGrenze: (mb: number) => `über ${mb} MB`,

    // --- Die Galerie und die Großansicht --------------------------------------
    fotoVergroessern: (nummer: number, alle: number) => `Foto ${nummer} von ${alle} vergrößern`,
    grossansicht: 'Foto in Großansicht',
    vorherigesFoto: 'Vorheriges Foto',
    naechstesFoto: 'Nächstes Foto',
    fotoZaehler: (nummer: number, alle: number) => `${nummer} von ${alle}`,
    fotoSichern: 'Foto sichern',
    fotoGesichert: 'Im Ordner „Downloads" gespeichert. Dort findet es auch die Galerie.',
    fotoNichtGeholt: 'Das Foto ließ sich nicht laden. Dafür brauchst du Netz.',
    // Saving a video (1.159.0): in the large view and in a bubble's menu.
    videoSichern: 'Video sichern',
    videoWirdGeladen: 'Video wird geladen …',
    videoBereit: 'Das Video ist geladen.',
    videoBereitKnopf: 'Sichern',
    // A video or several files, downloaded (not the photo's sentence: that one names the gallery).
    inDownloads: 'Im Ordner „Downloads" gespeichert.',
    videoNichtGeholt: 'Das Video ließ sich nicht laden. Dafür brauchst du Netz.',
    videoOhneNetz: 'Zum Sichern eines Videos brauchst du Netz.',
    // Several photos and videos of one post, from its menu (1.159.0).
    medienSichern: (fotos: number, videos: number) =>
      videos === 0 ? 'Fotos herunterladen' : fotos === 0 ? 'Videos herunterladen' : 'Fotos und Videos herunterladen',
    medienWirdGeladen: (anzahl: number) => `${anzahl} Dateien werden geladen …`,
    medienBereit: (anzahl: number) => `${anzahl} Dateien sind geladen.`,
    ladenStand: (geladen: string, gesamt: string) => `${geladen} von ${gesamt}`,
    ladenOhneGesamt: (geladen: string) => `${geladen} geladen`,
    medienNichtGeholt: 'Nicht alles ließ sich laden. Dafür brauchst du Netz.',
    medienOhneNetz: 'Zum Herunterladen brauchst du Netz.',
    aufFotoAntworten: 'Auf dieses Foto antworten',

    // --- GIFs ---------------------------------------------------------------
    gifWahl: 'GIF auswählen',
    gifSuchen: 'GIFs suchen',
    gifPlatzhalter: 'GIFs suchen …',
    /*
     * **Die Zeile im Plus-Menü**, und sie heißt nicht `gifSuchen`: das ist der
     * Name des Suchfeldes in der Auswahl („GIFs suchen"). Zwei Stellen, zwei
     * Wortlaute, zwei Namen.
     *
     * **„GIF suchen" und nicht „GIF"**: das Sinnbild trägt das Wort schon, und
     * zweimal dasselbe nebeneinander liest sich als Fehler. Der Zusatz sagt
     * außerdem, was passiert -- es öffnet eine Suche.
     */
    gifAnhang: 'GIF suchen',
    gifSchliessen: 'Auswahl schließen',
    gifNichts: 'Dazu gibt es kein GIF.',
    gifOhneNetz: 'Ohne Netz gibt es keine GIFs.',
    /** Um den Namen des Dienstes herum -- der ist eine Marke und bleibt. */
    gifQuelleVor: 'GIFs von ',
    gifQuelleNach: '. Der Suchbegriff geht dorthin, deine Adresse nicht.',
    gifSucheFehlt: 'Die Suche hat gerade nicht geklappt.',
    gifNichtGeholt: 'Das GIF ließ sich nicht holen.',
    gifSuchtGerade: 'Suche …',

    // --- Reaktionen ----------------------------------------------------------
    /** Ersatzzeile im Player, wenn der Browser das Format nicht kann. */
    tonNichtAbspielbar: 'Dein Browser spielt diese Aufnahme nicht ab.',
    reagieren: 'Reagieren',
    reagierenName: 'Mit einem Emoji reagieren',
    mehrEmoji: 'Mehr Emoji',
    /** Wer reagiert hat (#110): Blatt, Menüeintrag, Abschnitte. */
    reaktionenTitel: 'Reaktionen',
    reaktionenMenue: 'Wer hat reagiert?',
    reaktionenZumBeitrag: 'Zum Beitrag',
    reaktionenAnhang: (art: 'image' | 'video' | 'audio', nummer: number) =>
      `${art === 'video' ? 'Video' : art === 'audio' ? 'Aufnahme' : 'Foto'} ${nummer}`,

    // --- Sprachnachricht aufnehmen -------------------------------------------
    aufnahme: 'Sprachnachricht aufnehmen',
    aufnahmeVerwerfen: 'Aufnahme verwerfen',
    aufnahmeAnhalten: 'Aufnahme anhalten',
    aufnahmeWeiter: 'Weiter aufnehmen',
    pause: 'Pause',
    weiter: 'Weiter',
    aufnahmeSenden: 'Sprachnachricht senden',
    senden: 'Senden',

    blasenmenue: 'Was mit dieser Nachricht?',
    punktWeg: 'nicht mehr auf der Karte',
    wetterNennung: 'Wetter:',
    weiterePunkte: (anzahl: number) =>
      (anzahl === 1 ? 'und ein weiterer POI' : `und ${anzahl} weitere POIs`)
      + ' auf der Karte',

    // --- Der Verlauf ---------------------------------------------------------
    nichtZuOeffnen: 'Dieser Chat lässt sich nicht öffnen',
    serverStockt: 'Der Server antwortet gerade nicht. Neue Nachrichten kommen, sobald er wieder da ist.',
    /** The zone suggestion for the author (1.87.0). */
    zonenvorschlag: {
      frage: (stadt: string) => `Du bist jetzt in der Zeitzone ${stadt}. Ab jetzt für die Reise übernehmen?`,
      uebernehmen: 'Übernehmen',
      nein: 'Nein',
    },
    zurueckZurListe: 'Zurück zur Liste',
    zumReiseChat: 'Zurück zum Reise-Chat',
    angabenZurReise: 'Angaben zur Reise',
    wirdGeladen: 'Wird geladen …',
    aeltere: 'Ältere Nachrichten',
    neueNachrichten: 'Neue Nachrichten',
    /**
     * Wenn der Verlauf **nicht geladen werden konnte** — und das ist etwas
     * anderes als „noch nichts geschrieben".
     *
     * Aus dem Betrieb: der Chat blieb bei „Wird geladen …" stehen und sagte
     * danach, es sei nichts da. Beides war falsch: die Abfrage war
     * fehlgeschlagen. Auf der Karte stand in derselben Lage „Diese Reise gibt
     * es nicht" — daran ließ sich weiterarbeiten, hier nicht.
     *
     * **Die Nummer steht dabei.** Sie ist kein Zierrat: 403 heißt etwas
     * anderes als 500, und wer ein Bild davon schickt, hat den Unterschied
     * schon mitgeschickt.
     */
    ladefehler: 'Der Verlauf konnte nicht geladen werden',
    ladefehlerText:
      'Der Server hat die Nachrichten nicht geliefert. Versuch es noch einmal.',
    ladefehlerNummer: (nummer: number) => `Fehler ${nummer}`,
    // Der Knopf heißt `chat.nochmal` -- es gibt ihn schon, für die wartende
    // Nachricht im Ausgangskorb. Ein zweiter mit demselben Wort daneben wäre
    // genau die Doppelung, die diese Datei vermeiden soll.
    keinNetz: 'Kein Netz',
    keinNetzText:
      'Der Verlauf kommt, sobald du wieder Empfang hast. Schreiben kannst du trotzdem, es geht später von selbst raus.',
    nochNichts: 'Noch nichts geschrieben',
    diesePerson: 'Diese Person',
    dieserPerson: 'diese Person',
    nochKeineAntwort: (name: string) => `${name} hat noch nicht geantwortet.`,
    schreibDieErste: 'Schreib die erste Nachricht. Alle Empfänger bekommen sie zu lesen.',
    wartetAufAutor: 'Sobald der Autor / die Autorin etwas schreibt, erscheint es hier.',
    nurAn: (name: string) => `Nur an ${name}`,
    aufDerReiseseite: 'Auf der Reiseseite ändern',
    platzhalterAnEinen: (name: string) => [
      `Nachricht nur an ${name}`,
      `Nur an ${name}`,
      'Nur an eine Person',
    ],
    platzhalterUnbekannt: ['Nachricht schreiben', 'Nachricht'],
    platzhalterAnAlle: ['Nachricht an alle Empfänger', 'An alle Empfänger', 'An alle'],
    platzhalterAntwort: (name: string) => [
      `Antwort an ${name}`,
      'Antwort schreiben',
      'Antwort',
    ],
    nichtMehrDabei:
      'Diese Person ist nicht mehr in dieser Reise. Der Verlauf bleibt lesbar, schreiben geht nicht mehr.',
    ausgestiegen:
      '{name} ist aus dieser Reise ausgestiegen. Der Verlauf bleibt lesbar, schreiben geht nicht mehr.',
    stillgelegt: 'Das Konto von {name} ist stillgelegt und kommt nirgends mehr herein.',
    ausDemVerteiler: '{name} ist aus dem Verteiler genommen und bekommt nichts Neues.',

    // --- Die Sprechblase -----------------------------------------------------
    blaseName: 'Nachricht, lang drücken für das Menü',
    fotoGross: 'Dieses Foto groß ansehen',
    fotoGrossKurz: 'Foto groß ansehen',
    zumBezug: 'Zur bezogenen Nachricht springen',
    geloescht: 'Diese Nachricht wurde gelöscht.',
    nurAnDich: 'Nur an dich',
    anAlleEmpfaenger: 'An alle Empfänger',
    anAlle: 'An alle',
    bearbeitet: ' · bearbeitet',
    nachtraeglichGeaendert: 'Nachträglich geändert',
    auswahlAufheben: 'Auswahl aufheben',
    // For deleting, moving and sending to everyone alike (1.167.0).
    auswaehlen: 'Auswählen',
    gelesen: 'Gelesen',
    liegtBereit: 'Liegt bereit, noch nicht gelesen',
    vonGelesen: (von: number, alle: number) => `Von ${von} von ${alle} gelesen`,
    menueAntworten: 'Antworten',
    menueKopieren: 'Kopieren',
    menueAufDieKarte: 'Auf die Karte',
    menueAendern: 'Ändern',
    /** Die Wetterkarte an Ort und Zeit (1.86.0): ihre Daten neu, wie beim Anlegen. */
    menueWetterAendern: 'Wetterkarte ändern',
    /** Abstimmung, Streckenstatistik und GIF (1.86.0): jede Art über ihr eigenes Blatt. */
    menueAbstimmungAendern: 'Abstimmung ändern',
    menueTagAendern: 'Streckenstatistik ändern',
    menueGifAendern: 'GIF ändern',
    menueLoeschen: 'Löschen',

    /** Die Überschrift des Blattes, das „Ändern" aufmacht (1.68.0). */
    beitragAendern: 'Beitrag ändern',
    /**
     * Bilder aus einem abgeschickten Beitrag nehmen (1.88.0). Weg ist ein
     * Bild erst mit „Speichern“; davor fragt das Blatt einmal nach.
     */
    aenderungBilder: 'Bilder und Videos',
    aenderungBildEntfernen: 'Aus dem Beitrag nehmen',
    aenderungBilderFrage: (anzahl: number) =>
      anzahl === 1 ? 'Das Bild aus dem Beitrag entfernen?' : `${anzahl} Bilder aus dem Beitrag entfernen?`,
    aenderungBilderKnopf: 'Entfernen',
    aenderungLeer: 'Ohne Text und Bild bleibt nichts übrig. Lösch den Beitrag über das Menü.',

    /**
     * Die vier Auszeichnungen (1.69.0).
     *
     * **Sie standen bis hierher in `lib/auszeichnung.ts`**, und der Textwächter
     * hat sie nicht gesehen: `name: 'Fett'` ist eine kurze Zeichenkette ohne
     * Leerzeichen, und genau die lässt die Heuristik seit 1.64.0 mit Ansage
     * durch. Vier echte Fundstellen für die dort gemessene Lücke.
     *
     * `kurz` ist die Beschriftung auf dem Knopf, `name` der ganze Name für
     * Vorlesehilfe und Kurzhinweis. Die Anfangsbuchstaben sind deutsch und
     * gehören deshalb hierher wie der Rest.
     */
    auszeichnung: {
      fett: { kurz: 'F', name: 'Fett' },
      kursiv: { kurz: 'K', name: 'Kursiv' },
      durch: { kurz: 'D', name: 'Durchgestrichen' },
      kapitael: { kurz: 'Kap', name: 'Kapitälchen' },
    },

    /** „Zu einem Tag springen" (1.19.0): das Blatt hinter dem Tagestrenner. */
    tage: {
      titel: 'Zu einem Tag springen',
      trenner: 'Tag wählen',
      keine: 'Noch keine Nachrichten.',
      anzahl: (n: number) => (n === 1 ? '1 Nachricht' : `${n} Nachrichten`),
    },

    /** Der Chatentwurf (#85, 1.21.0): die Seite, das Menü, das Blatt, der Trenner. */
    entwurf: {
      titel: 'Reisebericht',
      zweck: (reise: string) => (reise === '' ? 'Entwurf, nur du siehst ihn' : `Entwurf zu „${reise}“, nur du siehst ihn`),
      leer: 'Noch nichts im Entwurf. Was du hier schreibst, sieht niemand, bis du es veröffentlichst.',
      platzhalter: ['Beitrag für den Reisebericht', 'Beitrag im Entwurf', 'Entwurf'],
      ausgeschaltet: 'Der Chatentwurf ist für diese Reise ausgeschaltet. Einschalten kannst du ihn',
      menueVeroeffentlichen: 'Bis hierher veröffentlichen',
      blattTitel: 'Veröffentlichen',
      beitraege: (n: number) => (n === 1 ? '1 Beitrag' : `${n} Beiträge`),
      zeitraum: (tag: string, von: string, bis: string) => `${tag}, ${von} bis ${bis}`,
      punkteMit: (n: number) =>
        n === 1 ? '1 POI aus diesem Zeitraum mit freigeben' : `${n} POIs aus diesem Zeitraum mit freigeben`,
      keinePunkte: 'Kein POI wartet in diesem Zeitraum.',
      hinweis: 'Die Beiträge stehen danach im Chat. Alle Mitlesenden bekommen eine Benachrichtigung, nur eine für den ganzen Bericht.',
      knopf: (n: number) => `Veröffentlichen (${n})`,
      laeuft: 'Wird veröffentlicht …',
    },

    // --- Emojis ------------------------------------------------------------
    emojiWahl: 'Emoji auswählen',
    emojiEigenes: 'Anderes Zeichen eingeben',
    emojiPlatzhalter: 'Anderes Zeichen …',
    emojiNurEines: 'Hier passt nur ein Emoji.',
  },

  /**
   * Karte und Highlights.
   *
   * Der größte Zweig, und er hat den größten Bereich hinter sich: siebzehn
   * Dateien, von der Kopfzeile mit den drei Reitern bis zum Formular am
   * Punkt. Gegliedert wird nach dem, was man vor sich sieht, nicht nach
   * Dateinamen — `formular` ist das Blatt am Punkt, `seite` die Kartenseite
   * darunter.
   */
  karte: {
    /** Die drei Reiter unter der Kopfzeile. */
    umschalter: {
      ansicht: 'Ansicht',
      chat: 'Chat',
      karte: 'Karte',
      bearbeiten: 'Bearbeiten',
      entwurf: 'Reisebericht',
      playlist: 'Playlist',
      /** Der Kalender (#113): die Tagestrenner der Reise, nur wenn es einen gibt. */
      tage: 'Tage der Reise',
      /** Der Punkt am Feld: dort wartet etwas Ungesendetes (1.22.0). */
      wartend: (feld: string) => `${feld}, ungesendete Eingabe`,
    },

    /** Die Regler auf der Karte — nur für die Verwaltung. */
    /** Die zwei Zeilen unter der Karte (#88): Aktionen und Reiter. */
    werkbank: {
      aktionen: 'POIs hinzufügen',
      werkzeuge: 'Werkzeuge',
      /** Die Reiter (1.49.1, aus dem Betrieb: „statt Kartenbild → Farben; statt GPX → Routen"; 1.97.0: „Darstellung", seit dort auch das Video der Streckenstatistik darin steht). */
      darstellung: 'Darstellung',
      routenReiter: (anzahl: number) => (anzahl === 0 ? 'Routen' : `Routen · ${anzahl}`),
    },

    regler: {
      kartenbild: 'Kartenbild',
      festHinweis: 'In der .env ist eine feste Quelle eingetragen (MAP_TILE_URL). Entferne die Zeile, dann kannst du hier wieder wählen.',
      fest: 'Fest aus der .env',
      routenfarbe: 'Farbe der Route',
      strichstaerke: 'Strichstärke der Route',
      mehr: 'Mehr …',
      mehrTitel: 'Karte und Streckenstatistik',
      /** Die eigene Linie der Streckenstatistik (#80), im aufgeklappten Fach. */
      statistik: 'Streckenstatistik',
      statistikStaerke: 'Strichstärke der Linie auf der Streckenstatistik',
      /** Berechnete Routen (1.18.0, #84). */
      verbundenWieRoute: 'Wie die Route',
      voreinstellung: 'Voreinstellung übernehmen',
    },

    /**
     * Die Namen der Farbfelder auf der Karte.
     *
     * Sie standen in `features/map/nadelfarben.ts` neben den CSS-Variablen —
     * dieselbe Aufteilung wie bei den Sprechblasen: die Mechanik bleibt dort,
     * die Wörter kommen hierher.
     */
    nadelFelder: {
      pin_color: 'Freigegeben',
      pin_draft_color: 'Entwurf',
      pin_suggestion_color: 'Vorschlag',
      pin_selected_color: 'Ausgewählt',
      pin_editing_color: 'In Arbeit',
      card_color: 'Kärtchen ohne Foto',
    },

    /** Einen Ort nach seinem Namen finden. */
    ortssuche: {
      titel: 'Ort suchen',
      schliessen: 'Suche schließen',
      feld: 'Ortsname',
      suchen: 'Suchen',
      laeuft: '…',
      nichts: 'Dazu findet Apple Maps nichts. Prüf die Schreibweise oder tippe den Ort auf der Karte an.',
      keineAntwort: (status: number) => `Die Suche hat nicht geantwortet (${status}).`,
      offline: 'Ohne Netz gibt es keine Ortssuche. Auf der Karte antippen geht trotzdem.',
      quelleVor: 'Gesucht wird über den Server bei ',
      quelleName: 'Apple Maps',
      quelleNach: '. Dorthin geht dein Suchbegriff, nicht deine Adresse.',
    },

    /**
     * Apples Wetterschlüssel auf Deutsch.
     *
     * Die Zuordnung „welcher Schlüssel bekommt welches Zeichen" bleibt in
     * `lib/wetterlage.ts`; hier stehen nur die Wörter. **Was hier fehlt, wird
     * durchgereicht** — Apple ergänzt die Liste, ohne zu fragen, und ein
     * unbekannter Schlüssel ist wenigstens als solcher zu erkennen.
     */
    wetterlagen: {
      Clear: 'klar',
      MostlyClear: 'überwiegend klar',
      PartlyCloudy: 'teils bewölkt',
      MostlyCloudy: 'überwiegend bewölkt',
      Cloudy: 'bewölkt',
      Drizzle: 'Nieselregen',
      Rain: 'Regen',
      HeavyRain: 'starker Regen',
      Snow: 'Schnee',
      Sleet: 'Schneeregen',
      Hail: 'Hagel',
      Thunderstorms: 'Gewitter',
      Foggy: 'Nebel',
      Haze: 'Dunst',
      Windy: 'windig',
      Breezy: 'frischer Wind',
    },
    /** Die Windrose zu acht Strichen, für „Wind aus SW". */
    himmelsrichtungen: ['N', 'NNO', 'NO', 'ONO', 'O', 'OSO', 'SO', 'SSO', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'],

    /** Fotos und Videos aus der Reise an einen Punkt hängen. */
    fotowahl: {
      titel: 'Aus dieser Reise',
      laedt: 'Wird geladen …',
      allesVergeben: 'Alles aus dieser Reise hängt schon an einem POI.',
      nichtsDa: 'In dieser Reise gibt es keine weiteren eigenen Fotos oder Videos. Hier stehen nur deine eigenen aus dem Chat, nicht die der Mitlesenden.',
      passt: 'Passt zu diesem POI',
      weitere: 'Weitere aus dieser Reise',
      /** Die Fotos des POI, die in den Spot umziehen können (1.52.0). */
      ausDiesemPoi: 'Aus diesem POI',
      imChat: 'im Chat',
      amPoi: 'am POI',
      amSpot: 'am Spot',
      nirgends: 'noch nirgends',
      ohneDatum: 'ohne Datum',
      video: 'Video',
      tonaufnahme: 'Sprachnachricht',
      /** Was der Chat anbietet: nur, was an einem POI oder Spot hängt und noch nicht im Chat steht. */
      nichtsFuerChat: 'Alles, was an einem POI oder Spot hängt, steht schon im Chat.',
      keineToene: 'Sprachnachrichten bleiben am POI oder im Chat.',
      vergeben: (anzahl: number) =>
        anzahl === 1
          ? 'Eines hängt schon an einem anderen POI oder Spot.'
          : `${anzahl} weitere hängen schon an anderen POIs oder Spots.`,
      vergebenZusatz: 'Nimm sie dort weg, wenn sie hierher sollen.',
      auswaehlen: 'Auswählen',
      fotosAnhaengen: (anzahl: number) =>
        anzahl === 1 ? '1 Foto anhängen' : `${anzahl} Fotos anhängen`,
      videosAnhaengen: (anzahl: number) =>
        anzahl === 1 ? '1 Video anhängen' : `${anzahl} Videos anhängen`,
      toeneAnhaengen: (anzahl: number) =>
        anzahl === 1 ? '1 Sprachnachricht anhängen' : `${anzahl} Sprachnachrichten anhängen`,
      gemischtAnhaengen: (anzahl: number) => `${anzahl} anhängen`,
      aeltereFehlen: (anzahl: number) =>
        anzahl === 1
          ? '1 älteres Bild wird nicht angezeigt.'
          : `${anzahl} ältere Bilder werden nicht angezeigt.`,
      umziehen: (anzahl: number) =>
        anzahl === 1 ? '1 Foto in den Spot verschieben' : `${anzahl} in den Spot verschieben`,
    },

    /** Die Sammelfreigabe unter der Karte. */
    freigabe: {
      falte: (offene: string) => `Freigeben · ${offene}`,
      entwuerfe: (anzahl: number) => (anzahl === 1 ? '1 Entwurf' : `${anzahl} Entwürfe`),
      vorschlaege: (anzahl: number) =>
        anzahl === 1 ? '1 Vorschlag' : `${anzahl} Vorschläge`,
      einerVonMehreren: (betroffen: number, alle: number) =>
        alle === 1 ? '1 Entwurf' : `${betroffen} von ${alle} Entwürfen`,
      bis: ' bis',
      bisLabel: 'Freigeben bis',
      laeuft: 'Läuft …',
      freigebenMit: (anzahl: number) => `Freigeben (${anzahl})`,
      ergebnis: (freigegeben: number, ohneZeit: number) =>
        `${freigegeben} freigegeben` +
        (ohneZeit > 0 ? `, ${ohneZeit} ohne Zeitangabe liegen geblieben` : ''),
      nichtDabei: (anzahl: number) =>
        anzahl === 1
          ? 'Ein POI ist noch im Modus Vorschlag und wird nicht automatisch freigegeben.'
          : `${anzahl} POIs sind noch im Modus Vorschlag und werden nicht automatisch freigegeben.`,
      sammelbeitrag: 'Ab sechs Highlights steht im Chat eine Zusammenfassung statt einzelner Beiträge.',
      ohneZeitKopf: 'Ohne Zeitangabe, deshalb nicht von „Freigeben bis" erfasst:',
      zeitNachtragen: 'Zeit nachtragen',
      trotzdemFreigeben: 'Trotzdem freigeben',
    },

    /** Die Detailkarte zu einem Punkt. */
    blatt: {
      neuerer: 'Neuerer POI',
      aelterer: 'Älterer POI',
      ohneZeit: 'ohne Zeitangabe',
      /** Warum an diesem Punkt kein Wetter steht (#95). Nur, wenn der Dienst eingerichtet ist. */
      wetterKommt: 'Ort und Wetter werden noch geholt.',
      wetterOhneZeit: 'Ohne Zeitangabe gibt es kein Wetter.',
      wetterFehlt: 'Keine Wetterinfo verfügbar.',
      wetterErstAb: 'Wetter gibt es erst ab August 2021.',
      entwurf: 'Entwurf',
      vorschlag: 'Vorschlag',
      entwurfTitel: 'Nur für dich sichtbar',
      vorschlagTitel: 'Aus der Aufzeichnung, nur für dich sichtbar',
      freigeben: 'Freigeben',
      verbergen: 'Verbergen',
      verbergenFrage: (titel: string) =>
        `„${titel}" wieder verbergen? Der Beitrag dazu im Chat bleibt stehen.`,
      aendern: 'Ändern',
      aendernLabel: (titel: string) => `${titel} ändern`,
      wegnehmen: 'Wegnehmen',
      wegnehmenLabel: (titel: string) => `${titel} wegnehmen`,
      wegnehmenFrage: (titel: string) => `„${titel}" wegnehmen?`,
    },

    /** Der Stapel der Kärtchen unter der Karte. */
    stapel: {
      leer: 'Noch keine Highlights. Nimm einen der Knöpfe auf der Karte: „Highlight", „Hier" oder „Aus Foto/Video".',
      anzahl: (anzahl: number) => (anzahl === 1 ? '1 Highlight' : `${anzahl} Highlights`),
      offeneAnhang: (worte: string) => ` · ${worte}`,
      ohneZeit: 'ohne Zeitangabe',
      entwurf: 'Entwurf',
      vorschlag: 'Vorschlag',
      /**
       * Steht am blassen Kärtchen eines Punktes, der noch nicht draußen ist.
       *
       * **Wortgleich mit dem Satz an der wartenden Sprechblase im Chat**, und
       * das ist Absicht: es ist dieselbe Lage, und zwei Formulierungen dafür
       * wären zwei Auskünfte über eine Sache. Ein eigener Schlüssel ist es
       * trotzdem -- eine andere Sprache stellt einen Satz an einem Kärtchen
       * womöglich anders als in einer Sprechblase.
       */
      wartetAufNetz: 'Wartet auf Netz',
    },

    /** Die Landkarte selbst — Knöpfe darauf und ihre Meldungen. */
    flaeche: {
      /**
       * Die Kacheln kommen nicht (Codereview E8). Punkte und Route liegen
       * trotzdem da -- die kommen aus dem Vorrat. Ohne diesen Satz hält man
       * die graue Fläche für einen Fehler der Anwendung.
       */
      kachelnFehlen: 'Das Kartenbild fehlt gerade. Deine POIs und die Route stehen trotzdem.',
      ganzeReise: 'Ganze Reise zeigen',
      /** Der vierte Knopf (#112): zurück in die Gegend, in der die Reise gerade ist. */
      letzteRegion: 'Region des letzten Highlights zeigen',
      punktSetzen: 'Highlight',
      tippen: 'Tippe auf die Karte',
      hier: 'Hier',
      ortet: 'Wird geortet …',
      keinePosition: 'Die Position kam nicht.',
      ausFoto: 'Aus Foto/Video',
      ortSuchen: 'Ort suchen',
    },

    /** Die Kartenseite: Kopfzeile, Leerzustände, GPX-Leiste. */
    seite: {
      zurReiseseite: 'Zur Reiseseite',
      route: 'Route',
      routeMitDateien: (anzahl: number) =>
        `Route · ${anzahl} ${anzahl === 1 ? 'GPX-Datei' : 'GPX-Dateien'}`,
      punkte: (anzahl: number) => `${anzahl} ${anzahl === 1 ? 'Highlight' : 'Highlights'}`,
      abgeschlossen:
        'Der Chat dieser Reise ist geschlossen. Die Karte kannst du weiter bearbeiten. Freigaben erscheinen dann nicht im Chat.',
      laedt: 'Wird geladen …',
      routeFehlt: 'Die Route lässt sich nicht laden',
      leerTitel: 'Noch kein POI vorhanden',
      bildUnlesbar: 'Ein Bild ließ sich nicht lesen.',
      keinePosition: 'Die Position kam nicht.',
      importLaeuft: 'Wird gelesen …',
      importieren: 'GPX-Route importieren',
      /** „Punkte verbinden" (1.18.0, #84). */
      verbinden: 'POIs verbinden',
      /** The titles of the groups in the routes panel (1.156.1). */
      groupImport: 'GPX importieren',
      groupTools: 'Werkzeuge',
      berechnet: 'berechnet',
      leereWerkbank:
        'Noch kein POI vorhanden. „Hier" nimmt deinen Standort, „Aus Foto/Video" den Ort der Aufnahme. Oder importiere eine GPX-Datei aus myTracks, Komoot, OsmAnd oder Garmin.',
      nachleseLaeuft: (fertig: number, gesamt: number) =>
        `Wird gesucht … ${fertig} von ${gesamt}`,
      nachlese: 'Aufenthalte in der Route suchen',
      offeneAngaben: (anzahl: number) => `Ort und Wetter werden geholt, noch ${anzahl}.`,
      dateien: (anzahl: number) => (anzahl === 1 ? '1 GPX-Datei' : `${anzahl} GPX-Dateien`),
      dateiZahlen: (behalten: number, roh: number, groesse: string) =>
        `${behalten} von ${roh} Punkten · ${groesse}`,
      dateiEntfernen: 'GPX-Datei entfernen',
      dateiEntfernenFrage: (name: string) => `„${name}" aus der Route entfernen?`,
      entfernen: 'Entfernen',
      ohneZeitangabe: 'ohne Zeitangabe',
      zeitraum: (von: string, bis: string) => `${von} – ${bis}`,
    },

    /**
     * Was die Aufenthaltserkennung gesehen hat — fünf Lagen, nicht eine.
     *
     * „Keine längeren Aufenthalte" stand einmal für alles: nichts gefunden,
     * knapp verfehlt, schon vorhanden, Datei ohne Zeitstempel. Die Trennung
     * ist der Sinn dieser Sätze, und sie überlebt den Umzug.
     */
    befund: {
      ausDateien: (anzahl: number) => `aus ${anzahl} Dateien`,
      ausRoute: 'aus der Route',
      vorschlaege: (anzahl: number, wo: string) =>
        `${anzahl} ${anzahl === 1 ? 'Vorschlag' : 'Vorschläge'} ${wo}, als ${anzahl === 1 ? 'Entwurf' : 'Entwürfe'} unter der Karte.`,
      schonDa: (anzahl: number) =>
        anzahl === 1
          ? '1 Aufenthalt erkannt, dort steht schon ein Highlight.'
          : `${anzahl} Aufenthalte erkannt, dort stehen schon Highlights.`,
      ohneZeitstempelMehrere:
        'Keine dieser Dateien enthält Zeitstempel. Ohne sie lassen sich keine Aufenthalte erkennen.',
      ohneZeitstempelEine:
        'Diese Datei enthält keine Zeitstempel. Ohne sie lassen sich keine Aufenthalte erkennen.',
      knappVerfehlt: (minuten: number, schwelle: number, meter: number) =>
        `Nichts gefunden. Der längste Halt dauerte ${minuten} Min., gesucht wird ab ${schwelle} Min. im Umkreis von ${meter} m.`,
      abgeschaltet: 'Die Aufenthaltserkennung ist abgeschaltet (0 Minuten in den Einstellungen).',
      nichtsMehrere: 'Keine weiteren Aufenthalte in diesen Aufzeichnungen.',
      nichtsEine: 'Keine längeren Aufenthalte in dieser Aufzeichnung.',
      rueckwaerts: (anzahl: number) =>
        `${anzahl} Punkte mit rückwärts laufender Zeit übergangen.`,
      /** Removed from the file on upload (1.117.0; since 1.117.2 out of the longest ordered run). */
      entfernt: (anzahl: number) =>
        anzahl === 1
          ? '1 Punkt passte zeitlich nicht in die Reihenfolge und wurde entfernt.'
          : `${anzahl} Punkte passten zeitlich nicht in die Reihenfolge und wurden entfernt.`,
      /** End points with only the time of the export (1.117.2). */
      randpunkte: (anzahl: number) =>
        anzahl === 1
          ? '1 Endpunkt trug nur die Zeit des Exports und wurde entfernt.'
          : `${anzahl} Endpunkte trugen nur die Zeit des Exports und wurden entfernt.`,
      /** Erkannt, aber in einer geschützten Region (#99). */
      geschuetzt: (anzahl: number) =>
        anzahl === 1
          ? '1 Aufenthalt liegt in einer geschützten Region und bleibt ohne POI.'
          : `${anzahl} Aufenthalte liegen in einer geschützten Region und bleiben ohne POI.`,
    },

    /**
     * Warum aus einer Aufnahme kein Ort kam — **und woran es lag**.
     *
     * Ein Satz für alle Fälle hat hier einen Fehler zwei Runden lang
     * versteckt: eine Datei, die der Leser gar nicht öffnen konnte, sah aus
     * wie ein Bild ohne Angaben.
     */
    ohneOrt: {
      nachsatz: ' Zieh den POI auf die Karte oder nimm „Hier".',
      diesesVideo: 'Dieses Video',
      diesesFoto: 'Dieses Foto',
      dieseAufnahme: 'Diese Aufnahme',
      keineOrtsangabe: (das: string) => `${das} trägt keine Ortsangabe.`,
      videoOhneDaten: (das: string) =>
        `${das} enthält keine Aufnahmedaten mehr, beim Exportieren gehen sie oft verloren. Über die Dateien-App statt über Fotos bekommst du das Original.`,
      fotoOhneDaten: (das: string) =>
        `${das} enthält keine Aufnahmedaten mehr, beim Weiterleiten oder Speichern gehen sie oft verloren.`,
      unlesbar: (wovon: string) => `Die Aufnahmedaten ${wovon} ließen sich nicht lesen.`,
      diesesVideosGenitiv: 'dieses Videos',
      diesesFotosGenitiv: 'dieses Fotos',
      garNichts: 'Aus dieser Datei lassen sich keine Aufnahmedaten lesen.',
    },

    /** Das Formular am Punkt — anlegen und ändern. */
    formular: {
      /** The title of a place found nearby, with its town (1.156.1): "Café Breakfast, York, ME". */
      titleWithTown: (name: string, town: string) => `${name}, ${town}`,
      anlegenLabel: 'Highlight anlegen',
      aendernLabel: 'Highlight ändern',
      neu: 'Neues Highlight',
      aendern: 'Highlight ändern',
      karteGross: 'Karte groß',
      formularZurueck: 'Formular zurück',
      titel: 'Titel',
      text: 'Text',
      wann: 'Wann war das?',
      zone: 'Zeitzone dieses POIs',
      wo: 'Wo war das?',
      koordinaten: (lat: string, lon: string, herkunft: string) => `${lat}, ${lon} · ${herkunft}`,
      ziehbar: 'Auf der Karte lässt sich der POI ziehen.',
      hier: 'Hier',
      ortet: 'Wird geortet …',
      keinePosition: 'Die Position kam nicht.',
      angabenHolen: 'Ort und Wetter holen',
      angabenNeuHolen: 'Ort und Wetter neu holen',
      angabenLaufen: 'Wird geholt …',
      inDerNaeheFinden: 'In der Nähe finden',
      nichtsZurueck: 'Zu diesem POI gibt es keine Angaben.',
      routeSuchen: 'Auf die Route legen',
      routeLaeuft: 'Wird gesucht …',
      keineRoute: 'Hier ist keine Route in der Nähe.',
      abstand: (meter: number) => `${meter} m entfernt`,
      uebernehmen: 'Übernehmen',
      lieberNicht: 'Lieber nicht',
      medien: 'Fotos und Videos',
      video: 'Video',
      /** Die Kachel einer Tonaufnahme im Formular, wenn keine Dauer bekannt ist. */
      tonaufnahme: 'Aufnahme',
      wiederEntfernen: 'Wieder entfernen',
      loeschenFrage: 'Wirklich löschen? Das lässt sich nicht rückgängig machen.',
      loeschen: 'Löschen',
      rechnet: 'rechnet',
      laedt: 'lädt',
      anteil: (prozent: number) => `${prozent} %`,
      ausbeute: (was: string) => `Video: ${was}`,
      ausFotoApp: 'Aus der Foto-App',
      ausDieserReise: 'Aus dieser Reise',
      imChatErzaehlen: 'Bei der Freigabe im Chat erzählen',
      chatZu: 'Der Chat dieser Reise ist geschlossen. Gibst du jetzt frei, steht der POI auf der Karte, aber im Chat wird er nicht erzählt, auch später nicht. Soll er erzählt werden, sichere ihn und gib ihn frei, wenn der Chat offen ist.',
      entwurfWort: 'Entwurf',
      fussnoteOnlineVor: 'Ein neues Highlight ist ein ',
      fussnoteOnlineNach: ' und nur für dich sichtbar, bis du es freigibst.',
      fussnoteOfflineVor: 'Ohne Netz. Das Highlight wird gesichert und geht raus, sobald wieder Netz da ist, als ',
      fussnoteOfflineNach: ', wie jedes neue Highlight.',
      speichertLaeuft: 'Wird gespeichert …',
      /**
       * Ein wartendes Foto an einem bestehenden Punkt (Codereview E4). Es geht
       * nicht verloren -- es wartet auf Netz und geht dann von selbst hinauf;
       * nur mitgesichert werden kann es nicht.
       */
      wartendeFotos: (anzahl: number) =>
        anzahl === 1
          ? 'Ein Foto wartet noch auf Netz. Sobald es da ist, geht es von selbst hinauf — dann kannst du sichern.'
          : `${anzahl} Fotos warten noch auf Netz. Sobald es da ist, gehen sie von selbst hinauf — dann kannst du sichern.`,
      inDenAusgangskorb: 'In den Ausgangskorb',
      alsEntwurf: 'Als Entwurf',
      sichern: 'Sichern',
      freigebenTitel: 'Sichern und für alle freigeben',
      freigebenLaeuft: 'Wird freigegeben …',
      freigeben: 'Freigeben',
      bildNichtVorbereitet: 'Das Bild ließ sich nicht vorbereiten.',
      titelFehlt: 'Ein Highlight braucht einen Titel.',
      /** Ein POI je Ort (#96, 1.51.0): die Frage vor dem Sichern eines neuen POI im Umkreis eines vorhandenen. */
      spotfrage: {
        satz: (name: string, weit: string) => `Im Umkreis liegt „${name}", ${weit} entfernt. Soll das ein Spot an diesem POI werden statt ein eigener POI? Dann wählst du aus der Suche, was es dort war: ein Lokal, eine Sehenswürdigkeit.`,
        ohneSuche: 'Ohne Ortssuche wird der Entwurf selbst der Spot.',
        alsSpot: (name: string) => `Spot an „${name}" anlegen`,
        eigener: 'Eigener POI',
        ohneNetz: (name: string) => `Im Umkreis liegt „${name}". Ohne Netz wird ein eigener POI angelegt.`,
        misslungen: (grund: string) => `Der Spot ließ sich nicht anlegen: ${grund}`,
      },
      freigabeMisslungen: (grund: string) =>
        `Gesichert, aber die Freigabe hat nicht geklappt: ${grund}`,
      freigabeMisslungenOhneGrund: 'Gesichert, aber die Freigabe hat nicht geklappt.',
    },

    /** Woher Ort und Zeit eines Punktes stammen — „aus dem Foto", „von Hand". */
    herkunft: {
      ortFoto: 'aus dem Foto',
      ortVideo: 'aus dem Video',
      ortPoi: 'aus der Karte gewählt',
      ortTrack: 'von der Route',
      ortGeraet: 'vom Gerät',
      ortKarte: 'auf der Karte gesetzt',
      zeitOhne: 'Ohne Zeitangabe. Nachtragen geht später.',
      zeitFoto: 'Aus den Aufnahmedaten des Fotos.',
      zeitVideo: 'Aus den Aufnahmedaten des Videos.',
      zeitTrack: 'Aus der Aufzeichnung.',
      zeitGeraet: 'Von diesem Gerät.',
      zeitManuell: 'Von Hand gesetzt.',

      /**
       * **Woher die UHR kommt — getrennt von der Zeit** (#74).
       *
       * In der Oberfläche heißt sie nicht so. Aus dem Betrieb (0.26.2):
       * „statt ‚Uhr aus der Aufnahme' → ‚Zeit aus der Aufnahme'." Die
       * Unterscheidung Uhr/Uhrzeit trägt der Kommentar, nicht der Satz —
       * ein Mensch liest „Zeit" und versteht Stunde samt Zone.
       *
       * Aus dem Betrieb, mit drei Fotos belegt: unter allen dreien stand
       * „Aus den Aufnahmedaten des Fotos". Für die *Uhrzeit* stimmte das
       * jedes Mal; für die *Uhr* nur bei zweien. Beim dritten (Kamera, 2021)
       * stand keine Zone in der Datei, und eingesprungen ist das Gerät —
       * angesagt hat es niemand.
       *
       * **Eine Vermutung, die aussieht wie eine Auskunft, ist schlechter als
       * eine, die sich zu erkennen gibt.**
       */
      uhrFoto: 'Zeitzone aus der Aufnahme.',
      uhrVerlauf: 'Zeitzone aus dem Zonenverlauf der Reise, die Aufnahme sagt nichts dazu.',
      uhrGeraet: 'Zeitzone dieses Geräts.',
      uhrHand: 'Zeitzone von Hand gewählt.',

      /**
       * **Wenn Kamera und Reise auseinandergehen** (#74).
       *
       * Aus dem Betrieb: ein Foto aus New York trug `UTC+2`, weil die Kamera
       * auf deutscher Zeit stand und nie umgestellt wurde. Die App hat richtig
       * gelesen — die Kamera hatte unrecht. Auffallen konnte es niemandem,
       * weil nichts die beiden Angaben nebeneinanderstellte.
       *
       * **Das Foto gewinnt trotzdem** (so entschieden): die Kamera war dort,
       * die Reiseeinstellung ist eine Angabe von früher. Aber es steht jetzt
       * da, und ein Griff genügt zum Ändern.
       */
      uhrWeicht: (kamera: string, reise: string) =>
        `Die Aufnahme sagt ${kamera}, die Reise ${reise}. Übernommen ist die der Aufnahme.`,
    },

    /** Ein Spot für sich — und die Liste, die zu ihm führt. */
    spots: {
      titel: 'Spots & Aktivitäten',
      lead: 'Restaurants, Stellplätze, Museen: was sonst noch da war.',
      ansehen: 'Spot ansehen',
      mitText: 'mit Text',
      mitFotos: 'mit Fotos oder Videos',
      wegnehmen: 'Wegnehmen',
      wegnehmenLabel: (name: string) => `${name} wieder wegnehmen`,
      hinzufuegen: 'Spot hinzufügen',
      wasWarNochDa: 'Was war noch da?',
      /** Ein Spot ohne eine der acht Marken — Apple kennt mehr, als hier steht. */
      ohneMarke: 'Spot',
      entfernung: (weit: string) => `${weit} vom POI`,
      wasWarHier: 'Was war hier?',
      notizPlatzhalter: 'Bestes Steak der Reise, Tisch draußen mit Blick auf den Fjord …',
      notizZusatz: 'gesichert wird, wenn du das Feld verlässt',
      keinText: 'Zu diesem Spot steht noch kein Text.',
      aufDerKarte: 'Auf der Karte ansehen',
      /** Eigene Fotos, Videos und Reaktionen am Spot (#96, Videos seit 1.52.0). */
      fotoDazu: 'Foto oder Video hinzufügen',
      fotoLaeuft: 'Wird hochgeladen …',
      fotoOhneNetz: 'Ohne Netz gehen Fotos und Videos am Spot nicht. Versuch es, sobald du wieder Netz hast.',
      nurFotosUndVideos: 'An einen Spot gehen Fotos und Videos, keine Sprachnachrichten.',
      fotoLoeschenFrage: 'Dieses Foto löschen? Das lässt sich nicht rückgängig machen.',
      fotoLoeschen: 'Löschen',
      fotoWegnehmen: 'Foto löschen',
      fotoErstSichern: 'Fotos und Videos kannst du anhängen, sobald der POI gesichert ist.',
    },

    /** „In der Nähe finden" — die Auswahl und ihre Treffer. */
    umgebung: {
      titel: 'In der Nähe finden',
      hinweis: 'Gesucht wird rund um den POI im Formular. Wählst du etwas aus, wandern Titel und Nadel dorthin.',
      nameSuchen: 'Oder nach dem Namen suchen',
      suchen: 'Suchen',
      quelle: 'Gesucht wird über den Server bei Apple Maps, im selben Umkreis wie die Kacheln.',
      andereSuche: 'Andere Suche',
      begriff: (wort: string) => `„${wort}"`,
      laeuft: 'Wird gesucht …',
      keineAntwort: 'Die Suche hat nicht geantwortet. Versuch es später noch einmal.',
      nichts: 'Hier wurde nichts gefunden. Probier eine andere Kategorie oder einen größeren Umkreis in den Einstellungen.',
      weiter: (meter: number) => `Im Umkreis von ${meter} m war nichts. Das hier liegt weiter weg.`,
    },

    /** „Punkte verbinden" (1.18.0, #84): das Blatt und seine Sätze. */
    /** The sections of a recording and their vehicles (#90, 1.96.0). */
    sections: {
      title: (name: string) => `Abschnitte: ${name}`,
      hint:
        'Teile die Aufzeichnung dort, wo du das Fahrzeug gewechselt hast. Die Streckenstatistik und ihre Animation nehmen diese Fahrzeuge. Mit dem Tempo läuft ein Abschnitt im Video schneller ab, etwa ein langer Flug.',
      fromTo: (from: string, to: string) => `${from} – ${to}`,
      noTime: 'ohne Zeitangabe',
      vehicleOf: (number: number) => `Fahrzeug für Abschnitt ${number}`,
      vehicle: {
        car: 'Auto',
        camper: 'Wohnmobil',
        bus: 'Bus',
        bike: 'Fahrrad',
        walk: 'Zu Fuß',
        train: 'Zug',
        express: 'Schnellzug',
        ship: 'Schiff oder Fähre',
        plane: 'Flugzeug',
      },
      merge: 'Mit dem vorigen zusammenlegen',
      /** The speed of a section in the film (1.98.0). */
      speed: 'Tempo im Video',
      /**
       * Said as a pace, not as "× slower" (1.104.0): from use, "der Begriff
       * 2x (4x) langsamer missverständlich" -- and "× schneller" is just as
       * ambiguous in German, so the fast side says "so schnell".
       */
      speedValue: (speed: number) =>
        SPEED_WORDS[speed] ?? `${speed.toLocaleString('de-DE')}× so schnell`,
      flightSuggestion: (km: number, from: string, to: string) =>
        `Zwischen ${from} und ${to} liegt ein Sprung von ${km.toLocaleString('de-DE')} km. War das ein Flug?`,
      asFlight: 'Als Flug teilen',
      splitAt: 'Teilen um',
      /** „Route“ since 1.163.0, from use: "es sollte besser heißen: auf der Route trennen". */
      onMap: 'Auf der Route trennen',
      split: 'Hier teilen',
      noTimes: 'Diese Aufzeichnung hat keine Zeiten und lässt sich nicht teilen.',
      clearAll: 'Alle Trennstellen entfernen',
      clearAllQuestion: 'Alle Trennstellen dieser Aufzeichnung entfernen? Sie ist dann wieder ein Abschnitt mit einem Fahrzeug.',
      done: 'Fertig',
      /** In the list of recordings. */
      open: 'Abschnitte und Fahrzeuge',
      /** More sections than the row shows (1.156.1): the count after the first symbol. */
      more: (n: number) => `+${n}`,
      /** The button with all its sections, for a screen reader. */
      openWith: (n: number) => (n === 1 ? 'Abschnitte und Fahrzeuge, 1 Abschnitt' : `Abschnitte und Fahrzeuge, ${n} Abschnitte`),
      tapLine: 'Tippe auf die Route, wo das Fahrzeug wechselt.',
      /** The recording passes the tapped place more than once (1.149.0). */
      whichPass: 'Hier kommst du mehrmals vorbei. Welche Durchfahrt?',
      /** A time mark on the line (1.149.0) where the recording reaches a new day. */
      markWithDay: (tag: string, uhr: string) => `${tag}, ${uhr}`,
      /** On import. */
      importVehicle: 'Fahrzeug',
      /** On import (1.117.0): the file holds the wall clock instead of UTC. */
      importLocal: 'Uhrzeiten in der Datei sind Ortszeit',
      importLocalNoZone: 'Dafür braucht die Reise eine Zeitzone.',
    },

    routen: {
      titel: 'POIs verbinden',
      hinweis: 'Wähle die POIs, die eine Route verbinden soll. Die Reihenfolge ist die der Zeit.',
      zuWenig: 'Dafür braucht es mindestens zwei POIs.',
      ohneNamen: 'POI',
      ohneZeit: 'ohne Zeit',
      verbunden: 'verbunden',
      aufRoute: 'auf der Route',
      hinweisGrau: 'Grau steht schon auf einer Route. Wo eine Route endet und keine weitergeht, bleibt der POI wählbar.',
      verkehrsmittel: 'Verkehrsmittel',
      auto: 'Auto',
      rad: 'Fahrrad',
      zuFuss: 'Zu Fuß',
      mautVermeiden: 'Mautstraßen vermeiden',
      nichtAbwaehlbar: 'Autobahnen und Fähren lassen sich bei Apple nicht abwählen.',
      laeuft: 'Wird berechnet …',
      verbindenKnopf: (n: number) => (n < 2 ? 'Verbinden' : `${n} POIs verbinden`),
      ueberApple: 'Die Route berechnet der Server bei Apple Maps, ein Aufruf je Abschnitt.',
      fertig: (ok: number, gesamt: number) =>
        ok === gesamt
          ? gesamt === 1 ? 'Ein Abschnitt berechnet und als Route gespeichert.' : `${gesamt} Abschnitte berechnet und als Route gespeichert.`
          : `${ok} von ${gesamt} Abschnitten berechnet. Der Rest fehlt und wurde nicht gezeichnet.`,
      abschnitt: (von: string, nach: string, meter: number, sekunden: number, maut: boolean, mitZeit: boolean) =>
        `${von} – ${nach}: ${(meter / 1000).toFixed(meter < 10000 ? 1 : 0)} km, ${Math.round(sekunden / 60)} min`
        + (maut ? ', mit Maut' : '')
        + (mitZeit ? '' : ', ohne Zeiten'),
      abschnittFehlt: (von: string, nach: string) => `${von} – ${nach}: Apple hat keine Route geliefert.`,
    },

    /**
     * Die acht Marken der Umgebungssuche.
     *
     * Der deutsche Name steht hier, die Apple-Kategorie und das Zeichen
     * bleiben in `Umgebungswahl.tsx`. Wer eine Kachel ergänzt, ergänzt beides
     * — aber das Wort nur einmal.
     */
    marken: {
      essen: 'Essen & Trinken',
      abend: 'Bar & Abend',
      kultur: 'Kultur',
      sehenswert: 'Sehenswürdigkeit',
      natur: 'Natur & Aussicht',
      uebernachten: 'Übernachtung',
      camping: 'Camping & Stellplatz',
      laden: 'Markt & Laden',
    },
  },

  /** Was überall gleich heißt. */
  allgemein: {
    /** The link at the top of a page that leads to the list of trips (1.53.2). */
    zuDenReisen: 'Zu den Reisen',
    speichern: 'Speichern',
    abbrechen: 'Abbrechen',
    verwerfen: 'Verwerfen',
    schliessen: 'Schließen',
    laeuft: 'Einen Moment …',
    gespeichert: 'Gespeichert.',
    schiefgelaufen: 'Das hat nicht geklappt.',
    /**
     * **Der eine Satz für „das geht gerade nicht"** (Codereview E7).
     *
     * Er stand viermal, mit zwei Wortlauten. Vier Schlüssel für eine Sache
     * heißt: beim nächsten Umformulieren stimmen drei nicht mehr.
     * `tools/pruefstand/texte.ts` hält seitdem fest, dass kein Satz zweimal
     * im Wortlaut vorkommt.
     */
    ohneNetz: 'Ohne Netz geht das gerade nicht. Versuch es später noch einmal.',
    ja: 'Ja',
    name: 'Travel Broadcast',
    /**
     * Das Sprachgebiet für `Intl` und `toLocaleDateString`.
     *
     * **Es gehört neben die Wörter und nicht in den Code**, denn mit der
     * Sprache wechselt auch das Format: „5. Okt. 2026" wird „Oct 5, 2026",
     * und die Reihenfolge von Tag und Monat ist keine Kleinigkeit. Es stand
     * bis `0.16.0` neunmal in `lib/` und einmal in der Karte; jetzt einmal
     * hier.
     */
    gebiet: 'de-DE',
  },

  /**
   * Wörter aus `lib/` und `api/` — Stellen ohne eine einzige Komponente.
   *
   * Sie holen ihren Wortlaut über `WORTLAUT` statt über `useTexte()`; das ist
   * die eine erlaubte Ausnahme, und `tools/pruefstand/texte.ts` führt Buch
   * darüber, wer sie nimmt und warum.
   *
   * **Nicht hier steht die Diagnosespur.** `notiere('das Element meldet keine
   * Masse')` schreibt in den Mitschrieb unter *Einstellungen › Diagnose* — ein
   * Werkzeug für die Fehlersuche, das mit derselben Sorgfalt zu übersetzen
   * wäre wie ein Stacktrace. Die Trennung ist im Bestand schon sichtbar:
   * **Oberflächentext fängt groß an und hört mit einem Punkt auf**, ein
   * Diagnosestück nicht.
   */
  /**
   * Was dasteht, wenn beim Zeichnen etwas wirft (`components/Absturz.tsx`).
   *
   * **Der Anlass war ein weißer Bildschirm** — die schlimmste Form der stillen
   * Absage: aus ihm lässt sich nicht ablesen, was schiefging, in welchem Stand,
   * und ob es am Gerät liegt oder am Bau.
   */
  absturz: {
    titel: 'Da ist etwas schiefgegangen',
    lead: 'Die App konnte diese Seite nicht anzeigen. Oft liegt es an einem veralteten Zwischenspeicher, besonders in der App auf dem Home-Bildschirm.',
    stand: (bau: string) => `Oberfläche ${bau}`,
    neuLaden: 'Zwischenspeicher leeren und neu laden',
    hinweis: 'Ungesendete Nachrichten bleiben erhalten. Hilft es nicht, schick ein Bildschirmfoto dieser Seite.',
  },

  technik: {
    /** Zeitangaben, wie ein Messenger sie zeigt (`lib/format.ts`). */
    zeit: {
      heute: 'Heute',
      gestern: 'Gestern',
      /**
       * Die zweite Uhrzeit an einer Sprechblase — **die des Absenders** (#71).
       *
       * Steht nur da, wenn sie von der eigenen abweicht. Zweimal dieselbe Zahl
       * nebeneinander wäre kein Hinweis, sondern Lärm.
       *
       * **„vor Ort" und nicht „dort":** die Blase steht mal links, mal rechts,
       * und „dort" müsste je nach Seite etwas anderes heißen. „Vor Ort" meint
       * immer denselben Ort — den, an dem geschrieben wurde.
       */
      vorOrt: (uhrzeit: string) => ` · ${uhrzeit} vor Ort`,
      /** Ein eingefügter Beitrag von einem anderen Tag (#130): das Datum vor der Uhrzeit. */
      mitDatum: (datum: string, uhrzeit: string) => `${datum}, ${uhrzeit}`,
      /**
       * Die Monatsnamen — ausgeschrieben, weil hier von Hand zerlegt wird.
       *
       * `new Date('2026-06-12')` liest der Browser als Mitternacht UTC und
       * zeigt westlich davon den Vortag; deshalb kommt `Intl` für den
       * Reisezeitraum nicht in Frage. Das Zerlegen bleibt in `format.ts`, die
       * zwölf Wörter stehen hier.
       */
      monate: [
        'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
      ],
      ab: (datum: string) => `ab ${datum}`,
      bis: (datum: string) => `bis ${datum}`,
      spanne: (von: string, bis: string) => `${von} – ${bis}`,
      spanneImMonat: (tag: string, bis: string) => `${tag}.–${bis}`,
      /** Wie ein Medium heißt, wenn kein Text danebensteht. */
      foto: 'Foto',
      video: 'Video',
      sprachnachricht: 'Sprachnachricht',
      /** Ohne Namen wären die Initialen leer — ein Fragezeichen sagt mehr. */
      ohneNamen: '?',
      /** Welche Uhr gilt (`lib/ortszeit.ts`). */
      geraetezeit: 'Gerätezeit',
      ortszeit: (versatz: string) => `Ortszeit ${versatz}`,
    },

    /** Was schiefgehen kann, und was dann dasteht. */
    fehler: {
      serverfehler: 'Der Server hat einen Fehler gemeldet.',
      unlesbar: 'Die Antwort des Servers war unlesbar.',
      /**
       * Es kam gar nichts zurück -- **an zwei Stellen dasselbe** (1.64.0).
       *
       * `api/client.ts` wirft das beim Holen und beim Hochladen. Der Satz
       * stand dort zweimal fest im Code, und zwar in denselben Zeilen, in
       * denen daneben schon `WORTLAUT.technik.fehler.keineAntwort` steht: der
       * richtige Weg und die Ausfransung Zeile für Zeile nebeneinander.
       */
      keineVerbindung: 'Keine Verbindung zum Server.',
      /** Ohne Service Worker gibt es keine Hülle und keine Push (`lib/huelle.ts`). */
      keinServiceWorker: 'Dieser Browser kennt keine Service Worker.',
      hochladenAbgebrochen: 'Das Hochladen wurde abgebrochen.',
      /**
       * Ein wartendes Foto lässt sich auf dem Gerät nicht mehr lesen.
       *
       * Getrennt von „keine Verbindung", weil es das Gegenteil ist: hier
       * hilft kein Warten. Ohne diesen Satz stünde an der Blase weiter
       * „wartet auf Netz", und zwar für immer.
       */
      bildNichtLesbar: 'Ein wartendes Foto lässt sich auf diesem Gerät nicht mehr lesen.',
      keineAntwort: 'Der Server hat nicht rechtzeitig geantwortet.',
      indexeddbBelegt: 'IndexedDB ist von einem anderen Fenster belegt.',
      bildNichtVerkleinern: 'Dieser Browser kann das Bild nicht verkleinern.',
      bildNichtUmwandeln: 'Das Bild ließ sich nicht umwandeln.',
      bildNichtOeffnen: 'Dieses Bild lässt sich nicht öffnen.',
    },

    /** Warum keine Position kam (`lib/ortung.ts`). */
    ortung: {
      kannNicht: 'Dieses Gerät kann seine Position nicht bestimmen.',
      nichtErlaubt:
        'Die Ortung ist für diese Seite nicht erlaubt. Erlaube sie in den Einstellungen des Browsers.',
      zuSpaet: 'Die Position kam nicht rechtzeitig. Unter freiem Himmel klappt es meist.',
      misslungen: 'Die Position ließ sich nicht bestimmen.',
    },

    /** Benachrichtigungen einrichten (`lib/push.ts`). */
    push: {
      nichtEingerichtet: 'Auf diesem Server sind Benachrichtigungen nicht eingerichtet.',
      abgelehnt:
        'Die Erlaubnis wurde abgelehnt. Sie lässt sich in den Einstellungen des Browsers wieder erteilen.',
      unbeantwortet:
        'Die Frage nach der Erlaubnis blieb unbeantwortet. Am Rechner zeigt der Browser sie oft als kleines Symbol links in der Adresszeile. Dort kannst du sie nachholen.',
      keinAbonnement: (grund: string) =>
        'Der Browser konnte sich nicht anmelden. Auf dem iPhone geht das nur in der '
        + 'installierten App (Teilen → Zum Home-Bildschirm). '
        + `Sonst hilft ein Blick in die Browser-Einstellungen. (${grund})`,
      unbekannterFehler: 'unbekannter Fehler',
    },

    /** Video: umrechnen, hochladen, ein Standbild finden. */
    /**
     * Apple Music im eigenen Browser (#106).
     *
     * Deutsch und hier, obwohl es um ein fremdes Skript geht: was ein Mensch
     * liest, steht in dieser Datei. `lib/musickit.ts` hat keine Komponente
     * und holt es über `WORTLAUT` -- eingetragen im Register des Prüfstands.
     */
    musik: {
      skriptFehlt:
        'Apple Music ließ sich nicht laden. Über „In Apple Music öffnen" geht es weiterhin.',
    },
    /** The film of the day card (#90, `features/thread/routeFilm.ts`). */
    routeFilm: {
      noEncoder: 'Dieses Gerät kann kein Video rechnen.',
      noMap: 'Die Karte ließ sich nicht laden. Versuch es später noch einmal.',
      cancelled: 'Abgebrochen.',
    },
    video: {
      browserKannNicht: 'Dieser Browser kann Videos nicht umrechnen.',
      nichtUmgerechnet: 'Das Video ließ sich nicht umrechnen.',
      keinH264: 'Dieses Gerät kann kein H.264 schreiben.',
      keineLeinwand: 'Das Bild ließ sich nicht zeichnen.',
      umrechnenAbgebrochen: 'Das Umrechnen wurde abgebrochen.',
      keineVerwaltungsdaten: 'Der Aufbau dieser Videodatei ließ sich nicht lesen.',
      keineBildspur: 'In dieser Datei ist keine Bildspur.',
      /**
       * Der Nachschlag kam nicht -- **und das ist kein Urteil über das Gerät.**
       *
       * Aus dem Betrieb (1.45.0), am Bildschirmfoto: „Dieses Gerät konnte das
       * Video nicht umrechnen (Importing a module script failed)." Der Satz
       * stimmte nicht: das Gerät kann es, geholt wurde der Umrechner nur nie.
       * Ein Download, der nicht ankam, und eine Eigenschaft des Geräts sind
       * zwei Dinge, und nur das zweite bleibt beim nächsten Versuch dasselbe.
       */
      nachschlagFehlt:
        'Der Umrechner für Videos ließ sich nicht laden. Versuch es noch einmal.',
      /**
       * Der Grund vom Browser steht **in Klammern hinter** dem deutschen Satz.
       *
       * Aus dem Betrieb, am Bildschirmfoto: „Decoder failure Ohne Umrechnung
       * wuerde es auf manchen Geraeten nicht abspielen." Angeklebt ergab das
       * einen Satz, der wie ein Fehler im Text aussieht.
       */
      keinFormat:
        'Dieses Video lässt sich auf diesem Gerät nicht in ein Format umrechnen, das alle abspielen.',
      nichtUmgerechnetMitGrund: (grund: string) =>
        `Dieses Gerät konnte das Video nicht umrechnen (${grund}).`
        + ' Ohne Umrechnung würde es auf manchen Geräten nicht abspielen.',
      keineMasse: 'Dieses Video meldet seine Maße nicht.',
      nichtOeffnen: 'Dieses Video lässt sich nicht öffnen.',
      nichtSpulbar: 'Diese Aufnahme lässt sich nicht spulen.',
      nichtGespult: 'Diese Aufnahme ließ sich nicht spulen.',
      spulenFehlgeschlagen: 'Das Video ließ sich nicht spulen.',
      /** Der Player im Verlauf (`VideoPlayer.tsx`). */
      fremdesFormat:
        'Dieses Video ist in einem Format, das dieser Browser nicht abspielt (meist HEVC vom iPhone). '
        + 'Auf einem iPhone, iPad oder Mac läuft es.',
      oeffnen: (dauer: string) => (dauer === '' ? 'Video öffnen' : `Video öffnen (${dauer})`),
      /**
       * Der Player hat einen Fehler gemeldet (1.5.0). Der Grund steht in der
       * Diagnose, hier steht nur, was jetzt zu tun ist.
       */
      abspielfehler: 'Das Video ließ sich gerade nicht abspielen.',
      mitOrt: 'Mit Aufnahmeort',
      nochEinmal: 'Noch einmal laden',
    },

    /**
     * Die Reiter der Emoji-Auswahl (`lib/emojiliste.ts`).
     *
     * Der Reiter trägt ein Zeichen und kein Wort — acht Wörter wären breiter
     * als ein Telefon. Das Wort steht daneben, für Vorlesehilfen und den
     * Tooltip, und der Schlüssel holt es hier ab: dieselbe Aufteilung wie bei
     * den Nadelfarben und den Marken der Umgebungssuche.
     */
    emojigruppen: {
      gesichter: 'Gesichter',
      gesten: 'Gesten',
      herzen: 'Herzen und Zeichen',
      unterwegs: 'Unterwegs',
      natur: 'Natur und Wetter',
      essen: 'Essen und Trinken',
      anlaesse: 'Anlässe und Dinge',
      fahnen: 'Fahnen',
    },
  },

  /** Die Reiseübersicht — die Liste, mit der die Anwendung aufgeht. */
  liste: {
    titel: 'Reisen',
    neueReise: 'Neue Reise',
    einstellungen: 'Einstellungen',
    laedt: 'Wird geladen …',
    /** Kein Netz ist kein Ausfall — deshalb ein ruhiger Satz statt einer Meldung. */
    keinNetz: 'Kein Netz',
    keinNetzLead:
      'Die Liste kommt, sobald du wieder Empfang hast. Was du bis dahin schreibst, geht später von selbst raus.',
    fehlt: 'Die Liste ließ sich nicht laden',
    schemaVor: 'Der Datenbank fehlt, was diese Version erwartet: ',
    schemaNach: '. Die Anweisung dazu steht in ',
    schemaDatei: 'docs/migrationen.md',
    schemaPunkt: '.',
    keineReise: 'Noch keine Reise',
    keineReiseLead:
      'Lege eine Reise an und teile den Einladungslink mit denen, die mitlesen sollen.',
    reiseAnlegen: 'Reise anlegen',
    nichtsZuLesen: 'Noch nichts zu lesen',
    nichtsZuLesenLead: 'Sobald dich jemand zu einer Reise einlädt, erscheint sie hier.',

    /**
     * Die drei Abschnitte am Ende der Liste.
     *
     * *Chat geschlossen* heißt: im Verlauf kommt nichts mehr dazu, gelesen
     * wird weiter. *Nur für dich* heißt: die Reise ist aus den Listen aller
     * anderen verschwunden. Zwei Stufen, zwei Namen.
     */
    aktiv: 'Aktiv',
    weitereReisen: 'Weitere Reisen',
    chatGeschlossen: 'Chat geschlossen',
    nurFuerDich: 'Nur für dich',

    verlaeufeZeigen: 'Antwortverläufe zeigen',
    verlaeufeEinklappen: 'Antwortverläufe einklappen',
    verlaeufeZeigenVon: (reise: string) => `Antwortverläufe von ${reise} zeigen`,
    verlaeufeEinklappenVon: (reise: string) => `Antwortverläufe von ${reise} einklappen`,

    /** Eine Zeile je Mitlesendem unter seiner Reise. */
    person: {
      stillgelegt: 'stillgelegt',
      nichtMehrDabei: 'nicht mehr dabei',
      pausiert: 'pausiert',
      pausiertTitel: 'Bekommt nichts Neues',
      beitraegeVon: (anzahl: number) => `${anzahl} Beiträge von dieser Person`,
      beitraegeGelesen: (anzahl: number) =>
        anzahl === 1 ? ' Beitrag geschrieben' : ' Beiträge geschrieben',
      reaktionenTitel: (anzahl: number) => `${anzahl} Reaktionen`,
      reaktionen: ' Reaktionen',
      allesGelesen: 'Hat alles gelesen',
      bereitgelegt: 'Bereitgelegt, noch nicht gelesen',
      nochNichtGelesen: 'Noch nicht gelesen',
    },

    ungelesen: ' ungelesene Nachrichten',
    ungelesenStumm: ' ungelesene Nachrichten, stumm',
    stumm: 'Stummgeschaltet',
    duDoppelpunkt: 'Du: ',
    keineNachricht: 'Noch keine Nachricht',
  },

  /** Eine Reise anlegen, einrichten und verwalten — die Reiseseite. */
  reise: {
    /** Eine neue Reise. */
    neu: {
      titel: 'Neue Reise',
      lead: 'Du bist der Absender. Was du schreibst, lesen alle Empfänger. Ihre Antworten liest nur du.',
      feldTitel: 'Titel',
      platzhalter: 'z. B. Norwegen 2026',
      beschreibung: 'Beschreibung (kann leer bleiben)',
      von: 'Von (kann leer bleiben)',
      bis: 'Bis',
      wirdAngelegt: 'Wird angelegt …',
      anlegen: 'Reise anlegen',
      /** Settings from another trip of the same author (#150). */
      copy: {
        label: 'Einstellungen übernehmen von',
        none: 'Keiner Reise, Voreinstellungen',
        lead: 'Was du anhakst, kommt aus dieser Reise. Alles andere kommt aus den Voreinstellungen.',
        groups: {
          cover: 'Symbolbild',
          background: 'Chat-Hintergrund',
          bubbles: 'Farben der Sprechblasen',
          map: 'Karte: Farben, Linien und Kartenbild',
          film: 'Film der Streckenstatistik',
          routes: 'Punkte verbinden: Verkehrsmittel und Maut',
          chat: 'Chat: Kennzeichnung, Entwurf, Playlist und Tageswort',
        },
        skipped: (namen: string[]) =>
          `Die Reise ist angelegt. Nicht übernommen, weil das Bild fehlt oder kein Platz mehr ist: ${namen.join(', ')}.`,
        toTrip: 'Zur Reise',
      },
    },

    /** Das Symbolbild in der Übersicht. */
    symbolbild: {
      titel: 'Symbolbild',
      lead: 'Was in der Übersicht vor dem Titel steht. Ohne Bild stehen dort die Anfangsbuchstaben.',
      anderesBild: 'Anderes Bild',
      bildWaehlen: 'Bild wählen',
      ausschnitt: 'Ausschnitt wählen',
      ausschnittTitel: 'Ausschnitt des Symbolbildes',
      wegnehmen: 'Wegnehmen',
      wegFrage: 'Das Symbolbild dieser Reise wegnehmen?',
    },

    /** Der Hintergrund des Chats — eine Farbe, ein Bild, oder beides. */
    grund: {
      titel: 'Hintergrund im Chat',
      lead: 'Gilt für alle, die diese Reise mitlesen. Eine Farbe, ein Bild oder beides.',
      deckgradHinweis: 'Je kräftiger das Bild, desto schwerer ist die Schrift zu lesen.',
      /** Die sechs Vorschläge — auf Lesbarkeit hin gewählt. */
      farben: {
        ohne: 'Ohne',
        sand: 'Sand',
        salbei: 'Salbei',
        nebel: 'Nebel',
        rose: 'Rosé',
        nacht: 'Nacht',
      },
      eigeneFarbe: 'Eigene Farbe',
      farbHinweis: 'Sechs Vorschläge, oder rechts eine eigene Farbe.',
      zurzeit: (farbe: string) => ` Zurzeit ${farbe}.`,
      deckgrad: (prozent: number) => `Deckgrad des Bildes: ${prozent} %`,
      ausschnittTitel: 'Ausschnitt des Hintergrunds',
    },

    /**
     * Die Reiseseite — Angaben, Buch, Einladungen, Empfänger, Zustand.
     *
     * Die größte Datei der Anwendung und der größte Zweig hier. Gegliedert
     * nach den Kategorien der Seite, so wie sie einer vor sich sieht.
     */
    info: {
      /** Die Kategorien in der Leiste. Der Schlüssel steht in der Adresse. */
      kategorien: {
        suche: 'Suchen',
        fotos: 'Fotos',
        abstimmungen: 'Abstimmungen',
        reise: 'Reise',
        aussehen: 'Aussehen',
        benachrichtigung: 'Benachrichtigung',
        einladen: 'Einladen',
        anfragen: 'Anfragen',
        empfaenger: 'Empfänger',
        willkommen: 'Willkommensnachricht',
        statistik: 'Statistik',
        zuruecksetzen: 'Zurücksetzen',
        verlassen: 'Diese Reise verlassen',
      },
      zurueck: 'Zurück',

      /** Angaben zur Reise und das Formular dahinter. */
      angabenAendern: 'Angaben ändern',
      duBistAbsender: 'Du bist der Absender dieser Reise.',
      berichtVon: (wer: string) => `Reisebericht von ${wer}.`,
      feldTitel: 'Titel',
      beschreibung: 'Beschreibung',
      von: 'Von',
      bis: 'Bis',
      /**
       * The trip's zone course (1.87.0) -- a list instead of one number,
       * because a trip crosses zones and daylight saving.
       */
      zonen: {
        titel: 'Zeitzonen der Reise',
        hinweis: 'Jede Zeitzone gilt ab ihrem Zeitpunkt bis zur nächsten. Fotos ohne eigene Zeitzone stehen in der, die damals galt.',
        /**
         * The time of a change is the local time of the new zone, not UTC
         * (1.100.0, from use: "die Zeitzoneneinstellungen in der Reise
         * (Zeitzone ab) sind doch in UTC?" -- they are not, and nothing
         * said so but the field's label while editing).
         */
        localTimeHint: 'Die Uhrzeit bei „Ab“ ist die Ortszeit der neuen Zeitzone, nicht UTC.',
        vonAnfang: 'Von Anfang an',
        ab: (wann: string) => `Ab ${wann} Ortszeit`,
        hinzufuegen: 'Zeitzone hinzufügen',
        aendern: 'Ändern',
        entfernen: 'Entfernen',
        entfernenFrage: (zone: string) => `${zone} entfernen? Zeiten danach stehen dann in der Zeitzone davor.`,
        feldZone: 'Zeitzone',
        feldAb: (zone: string) => `Gilt ab (Uhrzeit in ${zone})`,
        geraet: (zone: string) => `Zeitzone dieses Geräts (${zone})`,
        ersterHinweis: 'Die erste Zeitzone gilt für alles davor.',
        neuGerechnet: (anzahl: number) => (anzahl === 1 ? 'Eine Zeit wurde angepasst.' : `${anzahl} Zeiten wurden angepasst.`),
        /**
         * Sommer- und Winterzeit (1.89.0). Am Eintrag steht der Versatz zu
         * seinem Beginn samt Art; die Umstellungen darunter rechnet die
         * Liste selbst aus und speichert sie nicht.
         */
        sommerzeit: 'Sommerzeit',
        winterzeit: 'Winterzeit',
        versatzMitArt: (versatz: string, art: string | null) => (art === null ? versatz : `${art} · ${versatz}`),
        umstellung: (wann: string, versatz: string, art: string | null) =>
          art === null ? `Ab ${wann}: ${versatz}` : `Ab ${wann}: ${art} · ${versatz}`,
      },
      wirdGespeichert: 'Wird gespeichert …',

      /**
       * Die drei Zustände einer Reise, als Leiter.
       *
       * Jeder nimmt etwas mehr weg, und keiner löscht etwas. Die Rückfrage
       * steht dort, wo etwas verschwindet — beim Öffnen und beim Schließen
       * braucht es keine, beim Zurückziehen schon.
       */
      zustandTitel: 'Zustand der Reise',
      zustand: {
        active: {
          titel: 'Läuft',
          text: 'Du schreibst Beiträge, die Empfänger antworten.',
        },
        closed: {
          titel: 'Chat geschlossen',
          text: 'Niemand schreibt mehr, auch du nicht. Lesen können den Bericht weiter alle, und die Karte kannst du weiter bearbeiten.',
          frage:
            'Den Chat dieser Reise schließen?\n\nDanach kann niemand mehr schreiben, auch du nicht. Lesen können den Bericht weiter alle.\n\nDie Karte bleibt offen. Freigaben erscheinen dann aber nicht mehr im Chat.',
        },
        private: {
          titel: 'Nur noch für mich',
          text: 'Die Reise verschwindet aus den Listen aller Mitlesenden, mit Chat und Karte. Sie steht nur noch bei dir, und du kannst sie weiter bearbeiten.',
          frage:
            'Diese Reise zurückziehen?\n\nSie verschwindet aus den Listen aller Mitlesenden und steht nur noch bei dir. Gelöscht wird nichts: Nachrichten und Empfänger bleiben, und du kannst sie jederzeit wieder freigeben.',
        },
      },

      /** Das Buch und die privaten Dokumente. */
      buch: {
        titel: 'Als Buch',
        leadVor: 'Beiträge, Fotos, Karte und POIs der Reihe nach, zum Drucken oder als PDF. ',
        leadFett: 'Antworten stehen nicht darin;',
        leadNach: ' die privaten Gespräche gibt es je Person einzeln.',
        zeitraumHinweis:
          'Beide Felder können leer bleiben, dann kommt der ganze Bericht. Der Zeitraum gilt auch für Karte, POIs und die Dokumente darunter.',
        oeffnen: 'Buch öffnen',
        jePerson: 'Euer Briefwechsel, je Person einzeln:',
      },

      /** Die Marke „aktiv" — genau eine, und nur an einer laufenden Reise. */
      aktiv: {
        titel: 'Aktive Reise',
        gesetzt: 'Diese Reise steht bei allen Mitlesenden oben in der Liste. Die übrigen Reisen sind darunter zusammengeklappt.',
        offen: 'Markiere die Reise, auf der du gerade unterwegs bist. Sie steht dann bei allen Mitlesenden oben in der Liste, die übrigen Reisen darunter zusammengeklappt. Je Autorin und Autor kann nur eine Reise aktiv sein. Beim Abschließen oder Zurückziehen fällt die Marke weg.',
        loesen: 'Marke wieder lösen',
        setzen: 'Als aktive Reise markieren',
      },

      /** Trägt auch ein Beitrag an alle seine Marke? */
      kennzeichnung: {
        titel: 'Kennzeichnung',
        knopf: 'Beiträge an alle kennzeichnen',
        an: 'Jede Nachricht zeigt, ob sie an alle oder an einen Einzelnen geht.',
        aus: 'Nur Nachrichten an einen Einzelnen sind gekennzeichnet.',
      },

      /** Der Chatentwurf (#85): je Reise, Vorgabe aus. */
      entwurf: {
        titel: 'Reisebericht als Entwurf',
        knopf: 'Beiträge erst als Entwurf sammeln',
        an: 'Du schreibst im Reisebericht und veröffentlichst, wenn du so weit bist. Bis dahin sieht niemand etwas.',
        aus: 'Jeder Beitrag geht sofort an alle.',
      },

      /** Die Playlist der Reise (#102, 1.58.0) -- je Reise, standardmäßig aus. */
      playlist: {
        titel: 'Playlist',
        knopf: 'Playlist der Reise zeigen',
        an: 'Neben Chat und Karte steht eine Seite mit der Musik der Reise. Alle lesen sie, Musik hinzufügen darfst nur du.',
        aus: 'Die Reise hat keine Playlist. Musik im Chat geht weiterhin.',
      },

      /** Das Wort vor der Tagesnummer der Tagestrenner (#113) -- je Reise. */
      tageswort: {
        titel: 'Tagestrenner',
        hinweis: 'So heißt der Zähler über einem Tagestrenner. Die Vorgabe für neue Trenner, jeder lässt sich einzeln ändern.',
        tag: 'Tag 1, Tag 2 …',
        day: 'Day 1, Day 2 …',
      },

      /** Push und Hervorhebung — je Reise. */
      stumm: {
        titel: 'Benachrichtigung',
        istStumm: 'Diese Reise ist stummgeschaltet: keine Push-Nachrichten und keine Hervorhebung in der Liste. Neue Nachrichten kommen trotzdem an.',
        laut: 'Neue Nachrichten werden in der Liste hervorgehoben, und du bekommst eine Push-Nachricht. Stummschalten hält beides an, nur für diese Reise und jederzeit umkehrbar.',
        aufheben: 'Stummschaltung aufheben',
        setzen: 'Stummschalten',
      },

      /** Einladungslinks. */
      einladen: {
        titel: 'Einladen',
        mitBestaetigung:
          'Teile den Link, wie du möchtest. Wer ihn öffnet, fragt bei dir an und liest mit, sobald du zustimmst.',
        ohneBestaetigung:
          'Teile den Link, wie du möchtest. Wer ihn öffnet, legt ein Konto an und liest sofort mit.',
        einerWartet: 'einer wartet auf dich',
        wartenAufDich: (anzahl: number) => `${anzahl} warten auf dich`,
        nochNichtBenutzt: 'noch nicht benutzt',
        einmalBenutzt: 'einmal benutzt',
        malBenutzt: (anzahl: number) => `${anzahl}-mal benutzt`,
        fuerEine: 'Gilt für eine Person',
        fuerMehrere: (anzahl: number) => `Gilt für bis zu ${anzahl} Beitritte`,
        laeuftAm: ' · läuft am ',
        ab: ' ab',
        kopiert: 'Kopiert',
        kopieren: 'Link kopieren',
        deaktivieren: 'Link deaktivieren',
        deaktivierenKurz: 'Deaktivieren',
        deaktivierenFrage:
          'Diesen Link deaktivieren? Wer ihn noch nicht eingelöst hat, kommt damit nicht mehr herein. Wer schon dabei ist, bleibt dabei. Das lässt sich nicht rückgängig machen, für neue Einladungen brauchst du einen neuen Link.',
        keinLinkHinein: 'Solange die Reise nur für dich steht, führt kein Link hinein.',
        neuerLink: 'Neuen Link erzeugen',
        linkFuerEine: 'Link für eine Person',
        bestaetigenKnopf: 'Beitritt bestätigen',
        bestaetigenAn: 'Wer den Link öffnet, kommt erst herein, wenn du zustimmst. Auch mit neuem Konto.',
        bestaetigenAus: 'Wer den Link hat, liest sofort mit, auch wenn ihn jemand weitergegeben hat. Wer schon einmal dabei war, braucht deine Zustimmung.',
      },

      /** Wer mitlesen möchte — und was aus einer Ablehnung wird. */
      anfragen: {
        titel: 'Möchte mitlesen',
        niemand: 'Zurzeit wartet niemand auf deine Bestätigung.',
        mitZahl: (anzahl: number) => `Möchte mitlesen (${anzahl})`,
        abgelehnteTitel: 'Abgelehnte Anfragen',
        lead: 'Diese Personen haben deinen Einladungslink geöffnet. Erst mit deiner Bestätigung lesen sie den Bericht.',
        aufnehmen: 'Aufnehmen',
        trotzdemFrage: (grund: string, name: string) => `${grund} ${name} trotzdem aufnehmen?`,
        trotzdem: 'Trotzdem aufnehmen',
        ablehnenKnopf: 'Nur für diese Reise ablehnen',
        ablehnen: 'Ablehnen',
        ablehnenFrage: (name: string) =>
          `${name} für diese Reise ablehnen? Die Person erfährt nichts davon und wartet weiter auf eine Bestätigung. Das Konto bleibt, und du kannst sie später doch aufnehmen.`,
        sperrenKnopf: 'Ablehnen und Konto sperren',
        sperren: 'Ablehnen und sperren',
        sperrenFrage: (name: string) =>
          `${name} ablehnen und das Konto sperren? Die Person wird sofort abgemeldet und kommt nirgends mehr herein, auch nicht in andere Reisen. Unter Einstellungen → Personen kannst du das zurücknehmen.`,
        abgelehntMitZahl: (anzahl: number) => `Abgelehnt (${anzahl})`,
        abgelehntLead:
          'Diese Personen warten weiter auf eine Bestätigung und erfahren nichts von der Ablehnung. Du kannst sie jederzeit doch aufnehmen.',
        dochAufnehmen: 'Doch aufnehmen',
      },

      /** Die Empfängerliste. */
      empfaenger: {
        titelMitZahl: (anzahl: number) => `Empfänger (${anzahl})`,
        lead: 'Die Empfänger sehen weder diese Liste noch die Antworten der anderen.',
        zuordnen: 'Personen zuordnen',
        legendeErreichbar: ' bekommt Benachrichtigungen',
        legendeStumm: ' stummgeschaltet',
        legendeStummOhne: ' stumm, ohne Benachrichtigungen',
        ausgestiegen: ' · ausgestiegen',
        stillgelegt: ' · stillgelegt',
        nichtsNeues: ' · bekommt nichts Neues',
        entfernen: (name: string) => `${name} entfernen`,
        entfernenFrage: (name: string) =>
          `${name} aus dieser Reise entfernen? Die Person kann den Bericht dann nicht mehr lesen. Ihre bisherigen Antworten bleiben für dich sichtbar.`,
        entfernenKnopf: 'Entfernen',
        willkommenSenden: 'Willkommen senden',
        abschneidenFrage: (name: string) =>
          `${name} nichts Neues mehr schicken? Der Bericht bleibt für die Person stehen, wie er jetzt ist. Sie merkt nichts davon. Das kannst du jederzeit rückgängig machen.`,
        abschneiden: 'Nichts Neues mehr',
        wiederBeliefern: 'Wieder beliefern',
        abschneidenKnopf: 'Nichts Neues mehr schicken',
        niemand: 'Noch niemand beigetreten.',
      },

      /** Selbst aussteigen — nur für Mitlesende. */
      verlassen: {
        titel: 'Diese Reise verlassen',
        leadVor: 'Du liest dann nicht mehr mit. Was du geschrieben hast, bleibt beim Autor / bei der Autorin. ',
        leadFett: 'Zurück geht es nur über eine neue Einladung',
        leadNach: ', und auch dann muss der Autor / die Autorin zustimmen.',
        dieseReise: 'dieser Reise',
        frage: (titel: string) =>
          `Aus „${titel}" aussteigen? Du liest dann nicht mehr mit. Zurück kommst du nur über eine neue Einladung des Autors / der Autorin.`,
        aussteigen: 'Aussteigen',
      },
    },

    /** Der Gruß an alle, die neu zugeordnet werden. */
    willkommen: {
      titel: 'Willkommensnachricht',
      lead: 'Der Gruß an alle, die du dieser Reise neu zuordnest. Er geht als private Nachricht nur an die einzelne Person.',
      text: 'Text',
      platzhalter: 'Hallo %N, schön, dass du mitliest!',
      erstSpeichern: 'Erst speichern – verschickt wird der hinterlegte Text.',
      /** Steht noch niemand in der Runde, tut es ein Beispielname. */
      beispielname: 'Anna',
      marke: '%N',
      markeHinweis:
        ' wird durch den Namen der Person ersetzt. Fotos gehen nicht mit.',
      vorschauVor: 'So sieht sie bei ',
      vorschauNach: ' aus:',
      wirdGespeichert: 'Wird gespeichert …',
      speichern: 'Text speichern',
      alleVersendet: 'Alle versendet',
      anAlleSenden: (offene: number) => `An alle senden, die sie noch nicht haben (${offene})`,
      niemandOffen: 'Es gab niemanden, der sie noch nicht hatte.',
      verschickt: (anzahl: number) => `Verschickt: ${anzahl}.`,
    },

    /** Personen ohne Einladungslink zuordnen. */
    zuordnen: {
      titel: 'Personen zuordnen',
      schliessen: 'Zuordnung schließen',
      lead: 'Wer hier ein Häkchen bekommt, liest den Bericht sofort mit, ohne Einladungslink und ohne Bestätigung.',
      filtern: 'Namen filtern',
      laedt: 'Wird geladen …',
      niemand: 'Es gibt sonst niemanden mit einem Konto.',
      keinTreffer: 'Kein Name passt dazu.',
      alleDabei: 'Alle lesen schon mit.',
      offene: (anzahl: number) =>
        `${anzahl} ${anzahl === 1 ? 'Person kann' : 'Personen können'} noch dazukommen.`,
      zuordnenLabel: (name: string) => `${name} zuordnen`,
      /** Was neben dem Namen steht — vom Server entschieden, hier nur benannt. */
      stand: {
        dabei: 'liest mit',
        wartet: 'wartet auf Bestätigung',
        ausgestiegen: 'ausgestiegen',
        offen: '',
      },
    },

    /**
     * Den Inhalt einer Reise wegwerfen und die Reise behalten.
     *
     * Zwei Schritte, und der erste ist keine Zierde: erst steht da, was genau
     * verschwindet, dann muss ein Code abgetippt werden.
     */
    zuruecksetzen: {
      titel: 'Chat zurücksetzen',
      was: 'Löscht den ganzen Inhalt dieser Reise: Nachrichten mit Fotos, Videos, Abstimmungen und Musik, Reaktionen, Lesestände, die Playlist, die Route und alle POIs auf der Karte. Die Reise selbst bleibt, mit Empfängern, Einladungen, Einstellungen, Symbolbild und Chat-Hintergrund.',
      wofuerVor: 'Gedacht für die Probe zu zweit, bevor die eigentlichen Empfänger dazukommen. ',
      wofuerFett: 'Das lässt sich nicht rückgängig machen.',
      knopf: 'Zurücksetzen …',
      wirdGezaehlt: 'Wird gezählt …',
      verschwindet: 'Das verschwindet:',
      nachricht: 'Nachricht',
      nachrichten: 'Nachrichten',
      medium: 'Foto oder Video',
      medien: 'Fotos und Videos',
      datei: 'Datei',
      dateien: 'Dateien',
      gpx: 'GPX-Datei',
      gpxMehrere: 'GPX-Dateien',
      punkt: 'POI auf der Karte',
      punkte: 'POIs auf der Karte',
      punktKurz: 'POI',
      punkteKurz: 'POIs',
      mitGroesse: (was: string) => ` · ${was}`,
      leserEins: 'Person liest',
      leserViele: 'Personen lesen',
      leserWarnung: ' diese Reise mit. Auch ihre Antworten verschwinden.',
      abtippenLabel: (code: string) => `Code ${code} abtippen`,
      laeuft: 'Wird zurückgesetzt …',
      endgueltig: 'Endgültig zurücksetzen',
      fertig: (titel: string, nachrichten: string, dateien: string, gpx: string, punkte: string) =>
        `„${titel}" ist zurückgesetzt: ${nachrichten}, ${dateien}, ${gpx} und ${punkte} sind weg.`,
      anzahl: (anzahl: number, eins: string, viele: string) =>
        `${anzahl} ${anzahl === 1 ? eins : viele}`,
    },

    /** Die Reise für immer löschen (#15) -- der Schritt nach dem Zurücksetzen. */
    /** Die Vorschläge aus der Route auf einmal löschen (#100). */
    vorschlaege: {
      titel: 'Vorschläge löschen',
      was: 'Löscht alle POIs, die noch im Modus Vorschlag sind: was der Import aus der Route angelegt hat und niemand geöffnet und gesichert hat. Entwürfe und freigegebene POIs bleiben. Der nächste Import legt Vorschläge wieder an.',
      wirdGezaehlt: 'Wird gezählt …',
      zahl: (anzahl: number) =>
        anzahl === 0
          ? 'Kein POI ist im Modus Vorschlag.'
          : anzahl === 1
            ? '1 POI ist im Modus Vorschlag.'
            : `${anzahl} POIs sind im Modus Vorschlag.`,
      knopf: 'Vorschläge löschen',
      frage: (anzahl: number) =>
        anzahl === 1 ? 'Diesen einen Vorschlag löschen?' : `Alle ${anzahl} Vorschläge löschen?`,
      laeuft: 'Wird gelöscht …',
      fertig: (anzahl: number) =>
        anzahl === 1 ? '1 Vorschlag ist gelöscht.' : `${anzahl} Vorschläge sind gelöscht.`,
    },
    loeschen: {
      titel: 'Reise löschen',
      was: 'Löscht die ganze Reise: alle Nachrichten mit Fotos und Videos, die Route, die POIs auf der Karte, Symbolbild und Chat-Hintergrund, alle Empfänger und Einladungen. Auch die Mitlesenden sehen die Reise danach nicht mehr.',
      sichernVor: 'Wenn du den Bericht behalten willst, erzeuge vorher das Buch oder lade die Medien als ZIP herunter. ',
      sichernFett: 'Das Löschen lässt sich nicht rückgängig machen.',
      knopf: 'Reise löschen …',
      dieReise: (titel: string) => `Die Reise „${titel}" mit allen Empfängern und Einladungen`,
      mitBildern: ', Symbolbild und Chat-Hintergrund eingeschlossen',
      buch: 'gebautes Buch oder ZIP auf dem Server',
      buecher: 'gebaute Bücher oder ZIPs auf dem Server',
      leserWarnung: ' diese Reise mit. Sie verlieren den ganzen Bericht.',
      laeuft: 'Wird gelöscht …',
      endgueltig: (titel: string) => `„${titel}" endgültig löschen`,
      nurPrivat: 'Löschen geht nur, wenn die Reise nur für dich steht. Stell sie zuerst um:',
      zumZustand: 'Zum Zustand der Reise',
    },

    /** Die Farbe der Sprechblasen — einmal für alle, einmal nur für dich. */
    blasen: {
      titel: 'Farbe der Sprechblasen',
      leadReise: 'Gilt für alle, die diese Reise mitlesen. Die Vorgabe kam beim Anlegen aus den Einstellungen.',
      leadEigen: 'Nur für dich. Die anderen sehen weiter die Farbe des Autors / der Autorin.',
      wieDerAutor: 'Wie der Autor / die Autorin',
    },
  },

  /** Die Reise als Buch — und das private Dokument je Person. */
  /** Die Wetterkarte im Chat (0.31.0, seit 0.32.0 mit Symbolen statt Wörtern). */
  /** Die Suche im Verlauf einer Reise (1.17.0). */
  suche: {
    titel: 'Suchen',
    zurueck: 'Zurück zur Reise',
    feld: 'Wort oder Satzteil',
    hinweis: 'Gesucht wird im Text der Beiträge und deiner Antworten. Mindestens zwei Zeichen.',
    sucht: 'Sucht …',
    keineTreffer: 'Nichts gefunden.',
    mehr: 'Es gibt mehr Treffer, als hier stehen. Ein längeres Wort grenzt sie ein.',
    treffer: (n: number) => (n === 1 ? 'Ein Treffer' : `${n} Treffer`),
    medien: (n: number) => (n === 1 ? '1 Anhang' : `${n} Anhänge`),
    oeffnen: 'Im Chat zeigen',
  },

  /**
   * Die Playlist der Reise (#102, 1.58.0): die Musik neben Chat und Karte.
   *
   * Der Ton ist der der Oberfläche und nicht der der Kommentare: du, kurze
   * Sätze, kein Gedankenstrich.
   */
  playlist: {
    titel: 'Playlist',
    zurueck: 'Zurück zur Reise',
    leerAutor: 'Noch keine Musik. Füg den ersten Titel oder deine Playlist hinzu.',
    leerLeser: 'Noch keine Musik in dieser Reise.',
    hinzufuegen: 'Musik hinzufügen',
    nehmen: 'Aus der Playlist nehmen',
    nehmenFrage: 'Diesen Eintrag aus der Playlist nehmen?',
    nehmenKnopf: 'Herausnehmen',
    ohneNetz: 'Die Playlist gibt es nur online. Sie holt die Player von Spotify und Apple Music, und dafür braucht sie Netz.',
    laedt: 'Wird geladen …',
    anzahl: (n: number) => (n === 1 ? '1 Eintrag' : `${n} Einträge`),
  },

  /** Alle Abstimmungen der Reise (1.92.0, #116). */
  abstimmungen: {
    titel: 'Abstimmungen',
    zurueck: 'Zurück zur Reise',
    keine: 'Noch keine Abstimmungen.',
    springeZu: 'Springe zu',
    listeTitel: 'Abstimmungen',
    imChat: 'Im Chat zeigen',
  },

  /** Alle Fotos und Videos der Reise (1.20.0). */
  fotos: {
    titel: 'Fotos und Videos',
    zurueck: 'Zurück zur Reise',
    keine: 'Noch keine Fotos.',
    ohneTag: 'Ohne Datum',
    mehr: 'Mehr laden',
    bildOeffnen: 'Bild groß zeigen',
    videoOeffnen: 'Video abspielen',
    zumBeitrag: 'Zum Beitrag im Chat',
    zumPunkt: 'Zum POI auf der Karte',
  },

  /** Die Vorgabe für berechnete Routen (1.18.0, #84), in der Verwaltung. */
  routen: {
    titel: 'Berechnete Routen',
    lead: 'Die Vorgabe für „POIs verbinden": so fragt der Server bei Apple nach dem Weg zwischen zwei POIs. Eine Reise stellt es beim Verbinden selbst ein.',
    verkehrsmittel: 'Verkehrsmittel',
    toleranz: 'Abstand zur Route (Meter)',
    toleranzHinweis: 'Ein POI, der näher an einer Route liegt, gilt beim Verbinden als schon verbunden. 300 m sind ein guter Wert: ein Stellplatz liegt so weit von der Straße, an der die Aufzeichnung endet.',
  },

  tageskarte: {
    titel: (tag: string) => `Am ${tag} gefahren`,
    /** Over several days (1.151.0): „12.–14. Okt. 2026 gefahren“. */
    titelSpanne: (spanne: string) => `${spanne} gefahren`,
    /** One row per day (1.151.0). */
    tage: 'Tage',
    tagZeile: (tag: string, km: string, dauer: string) => `${tag} · ${km} · ${dauer}`,
    strecke: 'Strecke',
    fahrzeit: 'Fahrzeit',
    hoehenmeter: 'Höhenmeter',
    hoechsterPunkt: 'Höchster Punkt',
    start: 'Start',
    ziel: 'Ziel',
    highlights: 'Highlights:',
    trenner: ' · ',
    km: (km: number) => `${km.toLocaleString('de-DE', { maximumFractionDigits: km < 100 ? 1 : 0, minimumFractionDigits: km < 100 ? 1 : 0 })} km`,
    dauer: (sekunden: number) => {
      const minuten = Math.round(sekunden / 60)
      return minuten < 60 ? `${minuten} Min.` : `${Math.floor(minuten / 60)}:${String(minuten % 60).padStart(2, '0')} Std.`
    },
    bergauf: (m: number) => `↑ ${m.toLocaleString('de-DE')} m`,
    bergab: (m: number) => `↓ ${m.toLocaleString('de-DE')} m`,
    meter: (m: number) => `${m.toLocaleString('de-DE')} m`,
    ortUmZeit: (zeit: string, ort: string) => `${zeit} · ${ort}`,
    /** The sections of the day (1.98.0), one line each. */
    sections: 'Abschnitte',
    sectionLine: (from: string, to: string, km: string, duration: string) => `${from}–${to} · ${km} · ${duration}`,
    /** A time in another zone than the card's start (1.100.0): „11:12 (New York)“. */
    timeInZone: (time: string, city: string) => `${time} (${city})`,
    sectionPlaces: (start: string, end: string) => (start === '' || end === '' || start === end ? start || end : `${start} → ${end}`),
  },

  /** Die Statistik zur Reise (#78, 1.11.0): auf der Reiseseite und als Seite im Buch. */
  statistik: {
    titel: 'Statistik',
    lead: 'Was die Reise bis jetzt ausmacht: die Route, der Bericht, das Wetter. Die Zahlen kommen aus der Aufzeichnung, den Beiträgen und den Wetterkarten.',
    leadMitlesend: 'Die Zahlen gelten für das, was du von dieser Reise siehst.',
    laedt: 'Zahlen werden geholt …',
    route: 'Die Route',
    strecke: 'Strecke',
    fahrzeit: 'Fahrzeit',
    tage: 'Tage mit Aufzeichnung',
    laengsterTag: 'Längster Tag',
    tagMitStrecke: (tag: string, strecke: string) => `${tag}, ${strecke}`,
    hoehenmeter: 'Höhenmeter',
    hoechsterPunkt: 'Höchster Punkt',
    niedrigsterPunkt: 'Niedrigster Punkt',
    keineRoute: 'Noch keine Aufzeichnung.',
    bericht: 'Der Bericht',
    beitraege: 'Beiträge',
    antworten: 'Antworten',
    fotos: 'Fotos',
    videos: 'Videos',
    sprachnachrichten: 'Sprachnachrichten',
    highlights: 'Highlights',
    wetter: 'Das Wetter',
    waermster: 'Wärmster Tag',
    kaeltester: 'Kältester Tag',
    keinWetter: 'Noch keine Wetterkarte.',
    gradAm: (grad: number, tag: string, ort: string) => `${grad} °C, ${tag}${ort === '' ? '' : `, ${ort}`}`,
    imBuch: 'Im Buch steht diese Seite hinter dem Deckblatt.',
  },

  wetterkarte: {
    /** Die wartende Karte aus dem Ausgangskorb (#98): was sie schon weiß. */
    koordinaten: (lat: number, lon: number) => `${lat.toFixed(4)}, ${lon.toFixed(4)}`,
    gemerkt: (wann: string) => `Gemerkt am ${wann}`,
    kommtMitNetz: 'Das Wetter für diesen Moment kommt, sobald Netz da ist.',
    grad: (grad: number) => `${grad}°`,
    trenner: ' · ',
    prozent: (prozent: number) => `${prozent} %`,
    /** Die Wörter zu den drei Kacheln (seit 0.37.0: Wind, Luftfeuchte mit Gefühlt, Mond). */
    gefuehlt: 'Gefühlt',
    hoechst: 'Höchstwert',
    tiefst: 'Tiefstwert',
    wind: 'Wind',
    luftfeuchte: 'Luftfeuchte',
    richtungWert: (grad: number, strich: string) => `${grad}° ${strich}`,
    kmh: (kmh: number) => `${kmh} km/h`,
    boeenWert: (kmh: string) => `Böen ${kmh}`,
    /** Auf- und Untergang des Mondes in einer Zeile: „↑ 13:05 ↓ 22:40". */
    mondZeiten: (auf: string | null, unter: string | null) => [auf === null ? null : `↑ ${auf}`, unter === null ? null : `↓ ${unter}`].filter((s) => s !== null).join(' '),
    himmel: { n: 'N', o: 'O', s: 'S', w: 'W' },
    sonnenaufgang: 'Sonnenaufgang',
    sonnenuntergang: 'Sonnenuntergang',
    heute: 'Heute',
    stunden: 'Die nächsten Stunden',
    jetzt: 'Jetzt',
    stunde: (stunde: number) => `${String(stunde).padStart(2, '0')} Uhr`,
    standort: 'Am Standort des Geräts beim Antippen',
    /** Eine Karte zu gewähltem Ort und Stunde (#83): kein Pfeil, dafür der Zeitpunkt. */
    gewaehlt: (wann: string) => `Gewählter Ort, ${wann}`,
    stundeZeile: (wann: string, lage: string | null, grad: number | null) =>
      `${wann}${lage === null ? '' : `, ${lage}`}${grad === null ? '' : `, ${grad}°`}`,
    regenchance: (prozent: number) => `Regenwahrscheinlichkeit ${prozent} %`,
    tagZeile: (tag: string, lage: string | null, tief: number | null, hoch: number | null) =>
      `${tag}${lage === null ? '' : `, ${lage}`}${tief === null ? '' : `, tiefstens ${tief}°`}${hoch === null ? '' : `, höchstens ${hoch}°`}`,
    mondphasen: {
      new: 'Neumond',
      waxingCrescent: 'zunehmende Sichel',
      firstQuarter: 'zunehmender Halbmond',
      waxingGibbous: 'zunehmender Mond',
      full: 'Vollmond',
      waningGibbous: 'abnehmender Mond',
      thirdQuarter: 'abnehmender Halbmond',
      waningCrescent: 'abnehmende Sichel',
    } as Record<string, string>,
  },

  buch: {
    zurueck: 'Zurück zur Reise',
    /** Die Kapitelzeile eines Tagestrenners (#113): „Tag 3 · Freitag, 2. Oktober 2026". */
    kapitel: (zaehler: string, wochentag: string, datum: string) => `${zaehler} · ${wochentag}, ${datum}`,
    zusammenstellen: (seiten: number) => `Wird zusammengestellt … (${seiten} Seiten)`,
    umfang: (beitraege: number, punkte: number) => `${beitraege} Beiträge, ${punkte} POIs`,
    nachrichten: (anzahl: number) => `${anzahl} Nachrichten`,
    nochNichtFertig: 'Noch nicht fertig …',
    nichtBereit: 'Nicht bereit',
    /** Das Buch als Datei vom Server (#14): zwei Knöpfe statt des Druckdialogs. */
    alsPdf: 'Als PDF',
    alsEpub: 'Als EPUB',
    /** Die drei Schritte hinter dem Knopf, damit man sieht, wo es hängt (0.30.9). */
    exportKarten: 'Karten werden abgemalt …',
    exportServer: 'Der Server baut …',
    exportHolen: 'Datei wird geholt …',
    /** Der zweite Knopf nach dem Bau (1.53.0): sein Tipp ist die Geste fürs Teilen-Blatt. */
    dateiSichern: (format: 'pdf' | 'epub') => (format === 'pdf' ? 'PDF sichern' : 'EPUB sichern'),
    dateiGeladen: 'Die Datei liegt im Ordner „Downloads“.',
    dateiNichtGeholt: 'Die Datei ließ sich nicht holen. Dafür brauchst du Netz; der Link darunter bleibt.',
    exportKeineAntwort: 'Der Server hat nach sechs Minuten nicht geantwortet. Versuch es mit einem kürzeren Zeitraum. Unter Einstellungen › Diagnose steht, wie weit es kam.',
    /** Der Link unter den Knöpfen, wenn die Datei gebaut ist. */
    dateiOeffnen: (name: string) => `${name} öffnen`,
    /** Die Medien als ZIP (#75): ein Knopf, ein Link, ein Satz zu den Ortsnamen. */
    medienZip: 'Medien als ZIP',
    medienPackt: 'Der Server packt …',
    medienSichern: 'ZIP sichern',
    medienHolen: (groesse: string) => `ZIP wird geholt … (${groesse})`,
    medienOeffnen: (name: string, dateien: number, groesse: string) => `${name} öffnen (${dateien === 1 ? '1 Datei' : `${dateien} Dateien`}, ${groesse})`,
    medienOrteApple: 'Fotos, Videos und Sprachnachrichten in drei Ordnern, benannt nach Datum, Ort und Nummer. Den Ort holt der Server einmal je Foto mit Ortsangabe bei Apple Maps.',
    medienOrteOhne: 'Fotos, Videos und Sprachnachrichten in drei Ordnern, benannt nach Datum, Ort und Nummer. Ohne Apple Maps heißt der Ort „unbekannt".',
    appHinweis: 'In der App öffnet sich dafür Safari. Dort lässt sich die Datei sichern.',
    /** Steht in der Fußzeile und auf dem Deckel, wenn der Titel fehlt. */
    reise: 'Reise',
    ausschnitt: (zeitraum: string) => `Ausschnitt: ${zeitraum}`,
    route: 'Die Route',
    bericht: 'Der Bericht',
    keinBeitragImZeitraum: 'In diesem Zeitraum steht kein Beitrag.',
    keinBeitrag: 'In diesem Bericht steht noch kein Beitrag.',
    punkte: 'Die POIs auf der Karte',
    kartenausschnitte: (vermerk: string) => `Kartenausschnitte: ${vermerk}`,
    empfaenger: 'Empfänger',
    stand: (datum: string) => `Stand: ${datum}`,

    /** Ein Medium auf Papier — es steht als Angabe da, nicht als Abspieler. */
    sprachnachricht: (sekunden: number, groesse: string) =>
      `Sprachnachricht · ${sekunden} s · ${groesse}`,
    ohneStandbild: 'Ohne Standbild',
    video: (minuten: number, sekunden: string) => `Video · ${minuten}:${sekunden}`,
    videoHoehe: (hoehe: number) => ` · ${hoehe}p`,
    videoCodec: (codec: string) => ` · ${codec}`,
    grad: (grad: number) => `${grad} °C`,
    wetterWort: (wort: string) => `, ${wort}`,
    /** Die Nennung ist Pflicht, wo Wetter steht. */
    wetterquelle: ' (Apple Weather)',

    /**
     * Eine Abstimmung auf Papier.
     *
     * **Gedruckt wird die Sicht eines Lesers, obwohl der Autor druckt.**
     * Der Server schickt ihm Namen und absolute Zahlen mit — im Buch stehen
     * sie trotzdem nicht: das Buch wird weitergereicht, und „Anna: Ja" verriete
     * jedem, der es in die Hand bekommt, wie eine Einzelne gestimmt hat. Das
     * ist dieselbe Grenze, an der auch die Antworten draußen bleiben, und
     * dieselbe, die schon bei den Reaktionen gilt: dort stehen die Zeichen und
     * nicht, wer sie gesetzt hat.
     *
     * **Auch keine Stimmenzahl.** Aus „sieben Stimmen" ließe sich die Größe
     * des Kreises zurückrechnen; aus „43 Prozent" nicht. Der Server begründet
     * das an derselben Stelle (`Abstimmung::ergebnis()`).
     *
     * **Die Frage steht hier nicht**, und das ist kein Versehen: der Server
     * schreibt sie als Text des Beitrags („Abstimmung: Wo fahren wir hin?"),
     * damit sie in der Chatliste und in der Benachrichtigung steht. Im Buch
     * ist sie damit schon gedruckt — sie ein zweites Mal zu setzen hieße, sie
     * doppelt zu lesen.
     */
    abstimmung: {
      anteil: (prozent: number) => `${prozent} %`,
      geschlossen: 'geschlossen',
      mehrfach: 'Mehrfachauswahl',
      /** Steht da, wo es (noch) kein Ergebnis gibt — statt einer leeren Stelle. */
      ohneErgebnis: 'Noch kein Ergebnis.',
      trenner: ' · ',
      /** The right answer on paper (#143): the sign, and words for a reader. */
      richtig: 'richtige Antwort',
    },

    /** Das private Dokument — je Person eines, und es sagt das auf Seite eins. */
    privat: {
      diesePerson: 'dieser Person',
      dateiname: (reise: string, wer: string) => `${reise}, privat mit ${wer}`,
      fuss: (reise: string, wer: string) => `${reise}, privat mit ${wer}`,
      deckel: (wer: string) => `Privates Gespräch mit ${wer}`,
      warnungVor: 'Dieses Dokument ist ',
      warnungNicht: 'nicht',
      warnungNach: (wer: string) =>
        ` der Reisebericht. Es enthält den Briefwechsel zwischen dir und ${wer}: die Antworten und deine privaten Nachrichten. Niemand sonst hat sie je gesehen. Zum Weitergeben nimm das Buch, nicht dieses Dokument.`,
      briefwechsel: 'Der Briefwechsel',
      leerImZeitraum: 'In diesem Zeitraum steht zwischen euch nichts.',
      leer: 'Zwischen euch steht noch nichts.',
      du: 'Du',
      // Around the quoted words, which carry their marks as elements
      // (1.87.2) -- a function cannot return them, hence …Vor/…Nach.
      zitatVor: '„',
      zitatNach: '"',
    },
  },

  /** Anmelden, Konto anlegen, Passwort vergessen — und der Einladungslink. */
  anmeldung: {
    anmelden: 'Anmelden',
    email: 'E-Mail-Adresse',
    passwort: 'Passwort',
    passwortVergessen: 'Passwort vergessen?',
    /** Anmelden mit Passkey (#21): der Knopf unter dem Formular. */
    oder: 'oder',
    mitPasskey: 'Mit Passkey anmelden',
    passkeyAbgebrochen: 'Abgebrochen. Du kannst dich mit E-Mail-Adresse und Passwort anmelden.',
    keinKontoVor: 'Du hast einen Einladungslink? Öffne ihn, darüber legst du dein Konto an. ',
    registrierung: 'Registrierung',

    kontoAnlegen: 'Konto anlegen',
    kontoLead: 'Der Name steht später über deinen Nachrichten.',
    name: 'Name',
    einladungscode: 'Einladungscode',
    codeBeispiel: 'z. B. K7M2QRXD',
    schonKontoVor: 'Schon ein Konto? ',

    vergessenTitel: 'Passwort vergessen',
    vergessenLead: 'Trag deine E-Mail-Adresse ein. Wir schicken dir einen Link, über den du ein neues Passwort vergibst.',
    linkAnfordern: 'Link anfordern',
    postfachTitel: 'Sieh in dein Postfach',
    postfachVor: 'Wenn es zu ',
    postfachNach: ' ein Konto gibt, ist eine Nachricht mit einem Link unterwegs. Er gilt eine Stunde und lässt sich einmal verwenden.',
    postfachSpam: 'Nichts angekommen? Sieh im Spam-Ordner nach, und prüfe die Schreibweise der Adresse.',
    zurueckZurAnmeldung: 'Zurück zur Anmeldung',

    neuesPasswort: 'Neues Passwort',
    neuesPasswortLead: 'Vergib ein neues Passwort. Danach bist du gleich angemeldet. Alle anderen Geräte werden abgemeldet, und deine Passkeys werden gelöscht.',
    nochEinmal: 'Noch einmal',
    stimmtNichtUeberein: 'Die beiden Eingaben stimmen nicht überein.',
    passwortSpeichern: 'Passwort speichern',
    abgelaufenVor: 'Link abgelaufen? ',
    neuenAnfordern: 'Neuen anfordern',

    /** Der Einladungslink — vier Zustände, und sie sind auseinanderzuhalten. */
    einladung: {
      dieseReise: 'Diese Reise',
      schonDabei: 'Du liest hier schon mit. Es gibt nichts mehr zu bestätigen.',
      zurReise: 'Zur Reise',
      wirdGeprueft: 'Einladung wird geprüft …',
      ungueltig: 'Einladung ungültig',
      weiterZurAnwendung: 'Weiter zur Anwendung',
      angefragt: (wer: string) =>
        `Deine Anfrage ist bei ${wer}. Sobald sie bestätigt ist, steht der Reisebericht in deiner Liste.`,
      laedtEin: (wer: string) => `${wer} lädt dich ein, den Reisebericht mitzulesen.`,
      nurDerAbsender: (wer: string) =>
        `Du liest alle Beiträge. Deine Antworten sieht nur ${wer}, die anderen Empfänger nicht.`,
      bestaetigtJeden: (wer: string) =>
        `${wer} bestätigt jeden Beitritt. Sobald das passiert ist, liest du mit.`,
      beitrittAnfragen: 'Beitritt anfragen',
      beitreten: 'Beitreten',
      angemeldetAls: (name: string) => `Angemeldet als ${name}`,
      kontoUndBeitreten: 'Konto anlegen und beitreten',
      habeKonto: 'Ich habe schon ein Konto',
    },
  },

  /** Der Rahmen: Startseite, Wartetafel, was zwischen den Seiten steht. */
  rahmen: {
    karteLaedt: 'Karte wird geladen …',
    seiteLaedt: 'Wird geladen …',
    /*
     * **Die drei Sätze von `RequireAuth`** (1.62.0). Sie standen als Text im
     * JSX, und zwei davon hätte der Prüflauf auch mit mehrzeiligen
     * Textknoten nicht gemeldet: „Kein Netz" liegt unter seiner
     * Zeichengrenze, „Einen Moment …" trägt kein Wort aus seiner Liste. Beim
     * Anfassen mitgenommen -- sonst bleiben sie stehen, bis jemand die
     * Wortliste anfasst. „Einen Moment …" ist seit 1.79.0 die Startanzeige
     * mit dem laufenden Balken (`startLaedt`).
     */
    /**
     * Die Startanzeige, solange die App aufgebaut wird (1.79.0). Der Satz ist
     * für Vorleser; zu sehen ist das Symbol und ein laufender Balken. Eine
     * Abschrift steht in `index.html`, weil die Anzeige dort vor React steht.
     */
    startLaedt: 'Travel Broadcast wird geladen',
    /**
     * Die `StandWache`, wenn auch das Neuladen den neuen Stand nicht brachte.
     * Die beiden Kennungen sind Commits und bleiben, wie sie sind.
     */
    veraltet: (hier: string, server: string) =>
      `Diese Seite ist älter als der Server (${hier} statt ${server}). Lade die Seite neu. Am iPhone: App schließen und neu öffnen.`,
    keinNetz: 'Kein Netz',
    ohneVorrat:
      'Auf diesem Gerät ist von zuletzt nichts gemerkt. Sobald du wieder Empfang hast, geht es weiter.',
    serverStockt: 'Der Server antwortet gerade nicht',
    serverStocktText: 'Du bist weiter angemeldet. Versuch es gleich noch einmal.',
    nochmal: 'Nochmal',
    /**
     * The maintenance page (#131): what everyone sees who does not pass.
     * The supervisor's own sentence stands above the fixed ones.
     */
    maintenance: {
      title: 'Wartung',
      fallback: 'Travel Broadcast wird gerade gewartet.',
      backAt: (when: string) => `Voraussichtlich wieder da: ${when}`,
      longer: 'Es dauert länger als geplant.',
      kept: 'Was du ohne Verbindung geschrieben hast, bleibt auf diesem Gerät und geht danach hinaus.',
      check: 'Erneut prüfen',
      signIn: 'Anmelden',
    },
    /** Die Startseite auf breiten Fenstern — links die Liste, hier nichts. */
    start: {
      waehleMitAnlegen: 'Wähle links eine Reise aus oder lege eine neue an.',
      waehle: 'Wähle links eine Reise aus.',
      alsAutor: 'Was du schreibst, lesen alle Empfänger. Was sie antworten, liest nur du.',
      alsLeser: 'Deine Antworten liest nur der Autor / die Autorin, die anderen Empfänger nicht.',
    },
    /**
     * Was jemand sieht, der noch nirgends dabei ist.
     *
     * **Wer abgelehnt wurde, sieht dieselbe Tafel.** Das ist keine
     * Nachlässigkeit, sondern dieselbe Entscheidung wie überall: eine
     * mitgeteilte Ablehnung ist eine Aufforderung nachzufragen.
     */
    warten: {
      nichts: 'Noch nichts freigegeben',
      angefragt: (wer: string) => `Deine Anfrage ist bei ${wer}.`,
      mailKommt: (adresse: string) =>
        `Sobald sie bestätigt ist, bekommst du eine E-Mail an ${adresse}. Du kannst die App jetzt schließen.`,
      ohneMail:
        'Sobald sie bestätigt ist, steht der Reisebericht hier. Du kannst die App jetzt schließen und später wieder öffnen.',
      eingeladenWerden: 'Sobald dich jemand zu einem Reisebericht einlädt, geht es hier weiter.',
      angemeldetAls: (name: string, email: string) => `Angemeldet als ${name} (${email})`,
      anderesKonto: 'Nicht du? Mit anderem Konto anmelden',
    },
  },

  /** Die kleinen Bauteile, die überall mitlaufen. */
  bausteine: {
    /** The retype field of a confirmation (`Abtippfeld`); the code follows the sentence. */
    abtippenVor: 'Zum Bestätigen abtippen: ',
    /** Das eigene Banner, wenn bei offener App eine Mitteilung kommt (1.82.0). */
    pushbanner: {
      oeffnen: (titel: string) => (titel === '' ? 'Mitteilung öffnen' : `Mitteilung öffnen: ${titel}`),
      schliessen: 'Banner schließen',
    },
    /** Der Deckel über einem langen Text. */
    langtext: {
      mehr: 'Mehr anzeigen',
      weniger: 'Weniger',
    },
    /** Der Streifen, wenn nichts durchgeht. */
    /**
     * **Was Chat, Kartenformular und Symbolbild gleich sagen** (Codereview
     * E7).
     *
     * Die Sätze standen zwei- bis dreimal -- einmal im Chat, einmal am Punkt,
     * einmal am Symbolbild. Genau die Doppelung, die `CLAUDE.md` mit
     * „dieselbe Sache wird an allen Stellen bedient" meint: eine
     * Umformulierung hätte die anderen stehen lassen, und im selben Gerät
     * stünden zwei Antworten auf dieselbe Frage.
     */
    medien: {
      videoNichtDurch: 'Das Video ging nicht durch.',
      videoZuGross: (mb: number) => `Dieses Video ist größer als ${mb} MB.`,
      bildNichtDurch: 'Das Bild ging nicht durch.',
      wederBildNochVideo: 'Das ist weder ein Bild noch ein Video.',
      videoBrauchtNetz: 'Ein Video braucht Netz, ein Foto geht auch ohne.',
    },
    /** Ortssuche und Umgebungssuche fragen dasselbe und sagen dasselbe. */
    suche: {
      platzhalter: 'Graz, Wuppertal, Pizzeria …',
      fehlgeschlagen: 'Die Suche hat nicht geklappt.',
    },
    /** Dieselben Regler in den Einstellungen und auf der Karte. */
    farbnamen: {
      statistikFarbe: 'Farbe der Linie auf der Streckenstatistik',
      verbunden: 'Farbe der berechneten Routen',
      liste: 'Farben der Reiseübersicht',
    },
    /** Zwei Übersichten, dieselbe Absage: Konto löschen und Reise zurücksetzen. */
    uebersichtFehlt: 'Die Übersicht lässt sich gerade nicht laden.',
    /** Dieselbe Angabe in Diensten und Selbstprüfung. */
    buchEmoji: 'Emoji-Bilder für das Buch',
    netz: {
      kein: 'Kein Netz',
      wartend: (anzahl: number) =>
        `: ${anzahl} ${anzahl === 1 ? 'Eintrag wartet' : 'Einträge warten'} und ${anzahl === 1 ? 'geht' : 'gehen'} später von selbst raus.`,
      leer: ': was du schreibst, geht später von selbst raus.',
      /**
       * Wo nichts nachkommt, wird auch nichts versprochen (Codereview E6).
       * Auf der Info-Seite, in den Einstellungen und bei einer neuen Reise
       * gibt es keinen Ausgangskorb: was dort nicht durchgeht, ist nicht
       * geschrieben.
       */
      nichtsWartet: ': Änderungen gehen gerade nicht durch.',
      absageWeg: 'Meldung schließen',
    },
    /** Wie viel noch geht — erst, wenn es knapp wird. */
    restzeichen: {
      zuViel: (zuViel: number) => `${zuViel} Zeichen zu viel.`,
      rest: (rest: number, grenze: number) => `Noch ${rest} von ${grenze} Zeichen.`,
      zusatz: (satz: string) => ` · ${satz}`,
    },
    /**
     * Erreichbarkeit und Ruhe eines Mitlesenden — als ein Zeichen.
     *
     * Vier Zustände, drei Sätze: „kein Gerät angemeldet" bekommt bewusst
     * keinen, weil es kein Zustand ist, sondern der Normalfall.
     */
    erreichbarkeit: {
      stummMitPush: 'Stummgeschaltet. Benachrichtigungen sind eingerichtet, kommen aber nicht an.',
      stummOhnePush: 'Stummgeschaltet, und keine Benachrichtigungen eingerichtet.',
      erreichbar: 'Bekommt Benachrichtigungen.',
    },
    /** Den Ausschnitt eines Symbolbildes wählen. */
    ausschnitt: {
      ziehen: 'Zieh das Bild an die Stelle, die zu sehen sein soll.',
      ganzesBild: ' Ganz nach links steht das ganze Bild auf der Hintergrundfarbe.',
      groesse: (zoom: number) => `Größe: ${zoom} %`,
      uebernehmen: 'Übernehmen',
      zuruecksetzen: 'Zurücksetzen',
    },
  },
} as const
